import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell,
  PieChart, Pie, AreaChart, Area, LineChart, Line
} from 'recharts';
import {
  Sparkles, Mail, Globe, Shield, RefreshCw, Key, CheckCircle, AlertTriangle, Play, Check, Copy, HelpCircle, ArrowRight,
  Database, Eye, Send, FileText, ChevronRight, Server, Plus, Trash2, Sliders, Inbox, Archive, UserCheck,
  ChevronDown, MessageSquare, Briefcase, Zap, Flame, Clock, AlertCircle, Cpu, TrendingUp, Users,
  BarChart3, Settings, PlaySquare, Film, Share2, Compass, Link2, ExternalLink, ThumbsUp, Search, EyeOff, LayoutTemplate,
  QrCode, Download, Edit2, PlayCircle, Heart, Calendar, HelpCircle as HelpIcon
} from 'lucide-react';
import { NexusUser } from '../types/nexus';

// Custom Type for Simulated Lead
interface GrowthLead {
  id: string;
  name: string;
  email: string;
  company: string;
  toolUsed: string;
  consent: boolean;
  status: 'New' | 'Free Tool Used' | 'Setup Requested' | 'Quote Requested' | 'Contacted' | 'Consultation Booked' | 'Invoice Sent' | 'Won' | 'Lost' | 'Follow Up Later';
  timestamp: string;
}

// Custom Type for QR Campaign
interface QRCampaign {
  id: string;
  name: string;
  type: string;
  url: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  caption: string;
  socialCaption: string;
  flyerText: string;
  status: 'Active' | 'Draft' | 'Paused';
  leadSourceTag: string;
}

// Custom Type for SEO Draft
interface SEODraft {
  id: string;
  keyword: string;
  title: string;
  metaDesc: string;
  slug: string;
  heroHeadline: string;
  problem: string;
  solution: string;
  features: string[];
  faq: { q: string; a: string }[];
  cta: string;
  internalLinks: string[];
  localKeywords: string[];
  approved: boolean;
}

// Custom Type for Social Post
interface SocialPost {
  id: string;
  platform: 'Facebook' | 'Instagram' | 'TikTok' | 'LinkedIn' | 'YouTube Shorts' | 'X/Twitter' | 'Pinterest';
  contentType: string;
  caption: string;
  hashtags: string[];
  imageIdea: string;
  videoScript: string;
  cta: string;
  destinationLink: string;
  qrCampaignId: string;
  status: 'Draft' | 'Ready' | 'Posted' | 'Scheduled manually' | 'Needs Edit';
}

// Custom Type for Short Video script
interface ShortVideoScript {
  id: string;
  type: string;
  hook: string;
  scene1: string;
  scene2: string;
  scene3: string;
  screenText: string;
  voiceover: string;
  cta: string;
  caption: string;
  hashtags: string[];
}

interface CommunityGoal {
  id: string;
  goalName: string;
  causeName: string;
  status: 'Planned' | 'Active' | 'Recorded' | 'Completed';
  notes: string;
  allocationBasis: string;
  recordedAllocation: number;
  publicDisclosure: boolean;
  reviewStatus: 'Pending Audit' | 'Approved' | 'Requires Verification';
}

interface NexusOrganicGrowthEngineProps {
  user: NexusUser;
  isAdminMode: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
  searchCredits: number;
  onAddSearchCredits: (amount: number, cost: number) => void;
  currentRole: 'visitor' | 'client' | 'admin' | 'superadmin';
}

export const NexusOrganicGrowthEngine: React.FC<NexusOrganicGrowthEngineProps> = ({
  user,
  isAdminMode,
  onAddLog,
  onAddTaskToWorkSuite,
  searchCredits,
  onAddSearchCredits,
  currentRole = 'admin'
}) => {
  const [activeTab, setActiveTab] = useState<'Zero-Budget HUD' | 'Trend Scout & Queue' | 'Free Lead Tools' | 'QR Campaigns' | 'Social Content' | 'SEO Pages' | 'Visibility Checklist' | 'Offer Ladder' | 'Community Goals' | 'Monday Plan' | 'Developer Console'>('Zero-Budget HUD');

  // --- TREND SCOUT & ORGANIC LAUNCH QUEUE STATES (SECTION 12) ---
  const [trendSources, setTrendSources] = useState<any[]>([]);
  const [trendCards, setTrendCards] = useState<any[]>([]);
  const [launchLeads, setLaunchLeads] = useState<any[]>([]);
  const [scoutStats, setScoutStats] = useState<any>({ totalSources: 0, totalTrends: 0, newTrends: 0, reviewedTrends: 0, approvedTrends: 0 });
  const [loadingScout, setLoadingScout] = useState(false);
  const [selectedScoutSource, setSelectedScoutSource] = useState<string>("src-futurelens");
  const [scoutKeywordTerm, setScoutKeywordTerm] = useState("");
  const [scoutingLog, setScoutingLog] = useState<string[]>([]);
  const [approvedDrafts, setApprovedDrafts] = useState<Record<string, boolean>>({});
  const [draftPipelineTab, setDraftPipelineTab] = useState<'trends' | 'socials' | 'videos' | 'seo' | 'qrs' | 'special'>('trends');

  // Modals / active drawers
  const [scoutSocialDraft, setScoutSocialDraft] = useState<any | null>(null);
  const [scoutVideoScript, setScoutVideoScript] = useState<any | null>(null);
  const [scoutSeoDraft, setScoutSeoDraft] = useState<any | null>(null);
  const [activeSocialPlatform, setActiveSocialPlatform] = useState<string>("instagram");

  // New Source form
  const [newSourceName, setNewSourceName] = useState("");
  const [newSourceType, setNewSourceType] = useState("YouTube channel");
  const [newSourceUrl, setNewSourceUrl] = useState("");
  const [newSourceTags, setNewSourceTags] = useState("");
  const [showAddSourceForm, setShowAddSourceForm] = useState(false);

  // Template Market Scan Sim
  const [competitorTemplateName, setCompetitorTemplateName] = useState("Instagram Minimalist Fashion Bundles");
  const [competitorTemplateUrl, setCompetitorTemplateUrl] = useState("");
  const [competitorTemplatePrice, setCompetitorTemplatePrice] = useState("£29");
  const [marketScanLog, setMarketScanLog] = useState<string[]>([]);
  const [suggestedProductsList, setSuggestedProductsList] = useState<any[]>([]);
  const [loadingMarketScan, setLoadingMarketScan] = useState(false);

  // Monday Funnel Public Client Form Inputs
  const [publicLeadName, setPublicLeadName] = useState("");
  const [publicLeadBusinessName, setPublicLeadBusinessName] = useState("");
  const [publicLeadEmail, setPublicLeadEmail] = useState("");
  const [publicLeadPhone, setPublicLeadPhone] = useState("");
  const [publicLeadWebsite, setPublicLeadWebsite] = useState("");
  const [publicLeadService, setPublicLeadService] = useState("Ready-made website");
  const [publicLeadBudget, setPublicLeadBudget] = useState("£5,000 - £10,000");
  const [publicLeadMessage, setPublicLeadMessage] = useState("");
  const [publicConsentResponse, setPublicConsentResponse] = useState(true);
  const [publicConsentMarketing, setPublicConsentMarketing] = useState(false); // marketing consent optional, not pre-ticked
  const [submittingPublicLead, setSubmittingPublicLead] = useState(false);
  const [publicLeadSuccessMsg, setPublicLeadSuccessMsg] = useState("");

  const [qrUtmSource, setQrUtmSource] = useState("print-flyer");
  const [qrUtmMedium, setQrUtmMedium] = useState("qr-sticker");
  const [qrUtmCampaign, setQrUtmCampaign] = useState("dunfermline-shop-walk");
  const [qrCampaignsLocal, setQrCampaignsLocal] = useState<any[]>([
    { id: "qr-001", name: "Dunfermline High Street Sticker", url: "https://nexorax.co.uk?utm_source=print-flyer&utm_medium=qr-sticker&utm_campaign=dunfermline-shop-walk", status: "Active", leadCount: 15 },
    { id: "qr-002", name: "Fife Business Cards QR", url: "https://nexorax.co.uk?utm_source=print-card&utm_medium=qr-card&utm_campaign=fife-networking", status: "Active", leadCount: 28 },
    { id: "qr-003", name: "Glasgow Tech Hub Poster", url: "https://nexorax.co.uk?utm_source=poster&utm_medium=qr-poster&utm_campaign=glasgow-launch", status: "Active", leadCount: 7 }
  ]);

  const [searchVisibilityChecklist, setSearchVisibilityChecklist] = useState<any[]>([
    { id: "g-gbp", name: "Create Google Business Profile (Dunfermline / Fife localization)", portal: "Google GBP", status: "In Progress" },
    { id: "g-gmc", name: "Verify domain ownership in Google Search Console", portal: "Google Search Console", status: "Not Started" },
    { id: "g-xml", name: "Dispatched XML Sitemap file to Bing Webmaster Portal", portal: "Bing Webmaster Search", status: "Needs Review" },
    { id: "g-sch", name: "Implement structured Schema.org LocalBusiness JSON-LD tags", portal: "Web Core Site", status: "Submitted" },
    { id: "g-hmrc", name: "Validate SC887095 registered office index tags with UK HMRC logs", portal: "HMRC Registry Profile", status: "Verified" }
  ]);

  const fetchTrendScoutData = async () => {
    try {
      const res = await fetch("/api/admin/trend-scout");
      const data = await res.json();
      if (data.success) {
        setTrendSources(data.sources);
        setTrendCards(data.trends);
        setScoutStats(data.stats);
      }
    } catch (e) {
      console.error("Error fetching Trend Scout data:", e);
    }
  };

  const fetchLaunchLeadsData = async () => {
    try {
      const res = await fetch("/api/admin/launch-list");
      const data = await res.json();
      if (data.success) {
        setLaunchLeads(data.leads);
      }
    } catch (e) {
      console.error("Error fetching launch leads:", e);
    }
  };

  useEffect(() => {
    fetchTrendScoutData();
    fetchLaunchLeadsData();
  }, []);

  const handleAddSource = async () => {
    if (!newSourceName) return;
    try {
      const res = await fetch("/api/admin/trend-scout/source", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newSourceName,
          type: newSourceType,
          url: newSourceUrl,
          tags: newSourceTags.split(",").map(t => t.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (data.success) {
        onAddLog('Trend Scout', `Added source: ${newSourceName}`, 'SUCCESS', 'Parsed source watchlist register');
        setNewSourceName("");
        setNewSourceUrl("");
        setNewSourceTags("");
        setShowAddSourceForm(false);
        fetchTrendScoutData();
      }
    } catch (err) {
      console.error("Failed to add source:", err);
    }
  };

  const handleScoutTrends = async () => {
    setLoadingScout(true);
    setScoutingLog(prev => ["Connecting to local scraping buffers...", ...prev]);
    try {
      const res = await fetch("/api/admin/trend-scout/generate-trends", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceId: selectedScoutSource, term: scoutKeywordTerm })
      });
      const data = await res.json();
      if (data.success) {
        setScoutingLog(prev => [`Scouted successfully: Detected "${data.trend.title}"`, ...prev]);
        onAddLog('Trend Scout', `New trend card created: ${data.trend.title}`, 'SUCCESS', 'Trend Watch');
        fetchTrendScoutData();
      }
    } catch (err) {
      setScoutingLog(prev => ["Trend Watch link completed. Fetching backup caches...", ...prev]);
    } finally {
      setLoadingScout(false);
    }
  };

  const handleApproveTrend = async (trendId: string, approvalStatus: string) => {
    try {
      const res = await fetch("/api/admin/trend-scout/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trendId, status: approvalStatus })
      });
      const data = await res.json();
      if (data.success) {
        onAddLog('Trend Scout', `Updated status of ${trendId} to ${approvalStatus}`, 'SUCCESS', 'Ledger flag');
        fetchTrendScoutData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleConvertToNews = async (trendId: string) => {
    try {
      const res = await fetch("/api/admin/trend-scout/generate-news-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trendId })
      });
      const data = await res.json();
      if (data.success) {
        onAddLog('Trend Scout', `Instantiated news draft for ${trendId}`, 'SUCCESS', 'Tech Radar integration');
        fetchTrendScoutData();
        alert("Tech Update Radar draft compiled successfully! You can approve it from the Tech Updates panel.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateSocial = async (trendId: string) => {
    try {
      const res = await fetch("/api/admin/trend-scout/generate-social", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trendId })
      });
      const data = await res.json();
      if (data.success) {
        setScoutSocialDraft(data.social);
        onAddLog('Trend Scout', `Compiled organic marketing posts array`, 'SUCCESS', 'Social draft storage');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateVideoScript = async (trendId: string) => {
    try {
      const res = await fetch("/api/admin/trend-scout/generate-video-script", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trendId })
      });
      const data = await res.json();
      if (data.success) {
        setScoutVideoScript(data.script);
        onAddLog('Trend Scout', `Calculated video script overlays`, 'SUCCESS', 'Video script register');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateSeoDraft = async (trendId: string) => {
    try {
      const res = await fetch("/api/admin/trend-scout/generate-seo-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trendId })
      });
      const data = await res.json();
      if (data.success) {
        setScoutSeoDraft(data.seo);
        onAddLog('Trend Scout', `Generated un-queued SEO draft: ${data.seo.keyword}`, 'SUCCESS', 'Sitemap staging');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLaunchListSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicLeadName || !publicLeadEmail) return;
    setSubmittingPublicLead(true);
    setPublicLeadSuccessMsg("");
    try {
      const res = await fetch("/api/launch-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: publicLeadName,
          businessName: publicLeadBusinessName,
          email: publicLeadEmail,
          phone: publicLeadPhone,
          website: publicLeadWebsite,
          interestedService: publicLeadService,
          budgetRange: publicLeadBudget,
          message: publicLeadMessage,
          consentToEnquiry: publicConsentResponse,
          consentMarketing: publicConsentMarketing
        })
      });
      const data = await res.json();
      if (data.success) {
        setPublicLeadSuccessMsg(data.message);
        setPublicLeadName("");
        setPublicLeadBusinessName("");
        setPublicLeadEmail("");
        setPublicLeadPhone("");
        setPublicLeadWebsite("");
        setPublicLeadMessage("");
        setPublicConsentMarketing(false);
        fetchLaunchLeadsData();
        onAddLog('Public Funnel', `New Monday Waiting List Lead: ${publicLeadEmail}`, 'SUCCESS', 'Synced WorkSuite Task');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingPublicLead(false);
    }
  };

  const handleAddLocalQrCampaign = () => {
    const freshQr = {
      id: "qr-" + Date.now(),
      name: `Local Campaign - ${qrUtmCampaign}`,
      url: `https://nexorax.co.uk?utm_source=${qrUtmSource}&utm_medium=${qrUtmMedium}&utm_campaign=${qrUtmCampaign}`,
      status: "Active",
      leadCount: 0
    };
    setQrCampaignsLocal([freshQr, ...qrCampaignsLocal]);
    onAddLog('QR Campaigner', `Initiated QR tracker path: ${qrUtmCampaign}`, 'SUCCESS', 'UTM mapping completed');
  };

  const handleSimulateMarketScan = () => {
    setLoadingMarketScan(true);
    setMarketScanLog([`Inspecting competitor URL: ${competitorTemplateUrl || "http://mock-market-place/fife-reels-packs"}`, `Parsing design parameters for structure: ${competitorTemplateName}`]);
    setTimeout(() => {
      setMarketScanLog(prev => [...prev, "Scan complete. Resolving un-duplicated design elements...", "Compiling zero-budget product ideas..."]);
      const pricingVal = competitorTemplatePrice ? competitorTemplatePrice.replace(/[^0-9]/g, "") : "30";
      const optimizedPricing = Math.floor(Number(pricingVal) * 1.5);

      setSuggestedProductsList([
        {
          name: "NexoraX Social Launch Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£49",
          description: "10 original high-fidelity organic social posts designed to power-boost local brand trust without paid traffic schedules.",
          features: ["Organic posting guidelines", "Never clone template creators", "High-contrast luxury layout ideas"]
        },
        {
          name: "NexoraX AI Agency Content Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£69",
          description: "Visual asset prompts and content ideas specifically tailored for UK-based digital services and AI integration agencies.",
          features: ["Original scripts", "Attribution-first sources", "No-Win-No-Fee terms models"]
        },
        {
          name: "NexoraX Dark Luxury Instagram Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£59",
          description: "Aesthetic, minimalist premium presentation card posts set styled for modern Inter and Outfit typography schemas.",
          features: ["Clean dark slate colors", "Zero copyright overlaps", "Strict visual alignment layouts"]
        },
        {
          name: "NexoraX Website Launch Carousel Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£39",
          description: "A high-conversion sequence of swipe-through posts announcing newly launched client portals with clear local focus.",
          features: ["Dynamic SEO snippets", "Wider CTA buttons", "Interactive visual flow checks"]
        },
        {
          name: "NexoraX Digital Product Promo Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£45",
          description: "Promo copy and story layout blueprints highlighting the benefits of downloadable tools and templates.",
          features: ["Original copy briefs", "Local UK search terms", "Clean design hierarchy"]
        },
        {
          name: "NexoraX Local Business Social Pack",
          tag: "MARKET INSPIRATION ONLY",
          price: "£55",
          description: "Optimized social templates targeted specifically at local hospitality, medical, or regional retail merchants in Fife.",
          features: ["Fife & Dunfermline focus", "Free local Google Business visibility", "Physical QR scan mappings"]
        }
      ]);
      setLoadingMarketScan(false);
      onAddLog('Market Scanner', `Parsed template market metadata: ${competitorTemplateName}`, 'SUCCESS', 'Inspiration analysis');
    }, 1500);
  };

  // --- COPIED NOTIFICATION TOOL ---
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleCopyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    onAddLog('Zero-Budget Growth', `Copy text snippet: ${id}`, 'SUCCESS', 'Copied clipboard allocation');
    setTimeout(() => setCopiedId(null), 1500);
  };

  // State management to prevent heavy mock arrays that exceed limits
  // --- SECTION 1: PUBLIC FREE LEAD MAGNET TOOLS STATE ---
  const [selectedToolId, setSelectedToolId] = useState<number>(1);
  const [toolInputs, setToolInputs] = useState<Record<string, string>>({
    businessName: 'Fife Retailers Ltd',
    websiteUrl: 'https://fiferetailers.co.uk',
    industry: 'Retail Fashion',
    presentationTopic: 'Scaling B2B E-commerce Automation',
    skillNiche: 'Automated Real Estate CRM Integration',
    targetAudience: 'Property Managers UK',
    featuresCount: '5 Pages + Blog',
    painPoint: 'Tired of manually sending booking reminders',
    contactName: 'Siddique Mahmood',
    contactEmail: 'siddique@fiferetailers.co.uk'
  });
  const [toolGeneratedResult, setToolGeneratedResult] = useState<any>(null);
  const [isToolGenerating, setIsToolGenerating] = useState<boolean>(false);
  const [toolMarketingConsent, setToolMarketingConsent] = useState<boolean>(false);

  // --- SECTION 2: QR CAMPAIGNS STATE ---
  const [qrCampaigns, setQrCampaigns] = useState<QRCampaign[]>([
    {
      id: 'qr-camp-1',
      name: 'Launch Faster Website Pack',
      type: 'Digital Products QR',
      url: 'https://preview.nexoraxdigital.co.uk/digital-products?tab=WebsiteTemplates',
      utmSource: 'qr_sticker',
      utmMedium: 'gpb_window',
      utmCampaign: 'launch_fast_web_pack',
      caption: 'Scan to launch your business website faster with NexoraX.',
      socialCaption: 'Launch modular React websites instantly. No setup limits. Zero-cost billing records ready #SaaS #React',
      flyerText: 'Scan this code to load ready-made B2B designs built with Inter displays and JetBrains Mono code snippets.',
      status: 'Active',
      leadSourceTag: 'Organic QR'
    }
  ]);
  const [newCampaignName, setNewCampaignName] = useState('My New Campaign');
  const [newCampaignType, setNewCampaignType] = useState('Business card QR');
  const [newCampaignUrl, setNewCampaignUrl] = useState('https://fiferetailers.co.uk');
  const [actionFeedback, setActionFeedback] = useState<{message: string, type: 'success' | 'info'} | null>(null);
  const [newCampaignUtmSource, setNewCampaignUtmSource] = useState('organic_qr');
  const [newCampaignUtmMedium, setNewCampaignUtmMedium] = useState('sticker');
  const [newCampaignUtmCampaign, setNewCampaignUtmCampaign] = useState('launch_fast_web');
  const [newCampaignLeadSourceTag, setNewCampaignLeadSourceTag] = useState('Organic QR');
  const [newCampaignCaption, setNewCampaignCaption] = useState('Scan to launch your business website faster with NexoraX.');
  const [newCampaignSocialCaption, setNewCampaignSocialCaption] = useState('Launch modular React websites instantly. No setup limits. Zero-budget SEO pages ready #SaaS #React');
  const [newCampaignFlyerText, setNewCampaignFlyerText] = useState('Scan this code to explore our professional setups instantly.');

  // --- NEW SEO CREATION FORM STATE ---
  const [newSeoKeyword, setNewSeoKeyword] = useState('Website design Dunfermline');
  const [newSeoTitle, setNewSeoTitle] = useState('Affordable Website Design Dunfermline | NexoraX Professional');
  const [newSeoMetaDesc, setNewSeoMetaDesc] = useState('Looking for clean web design in Dunfermline? NexoraX engineers high-speed static sitemaps optimized for Google search organic traffic.');
  const [newSeoSlug, setNewSeoSlug] = useState('website-design-dunfermline');
  const [newSeoHeroHeadline, setNewSeoHeroHeadline] = useState('Get Online In Dunfermline Natively');
  const [newSeoProblem, setNewSeoProblem] = useState('Expensive, slow local web developers who hold your domain hostage.');
  const [newSeoSolution, setNewSeoSolution] = useState('Instant B2B responsive directories with modular elements built for Scottish trades.');
  const [newSeoFeatures, setNewSeoFeatures] = useState('Touch targets over 44px, Zero manual configuration, sitemap submission logs');
  const [newSeoLocalKeywords, setNewSeoLocalKeywords] = useState('fife web design, trades marketing dunfermline, local SEO kirkcaldy');
  const [newSeoFaqQ, setNewSeoFaqQ] = useState('How long does typical setup take?');
  const [newSeoFaqA, setNewSeoFaqA] = useState('Under two business days for pre-approved corporate slots.');

  // --- NEW SOCIAL CONTENT MACHINE STATE ---
  const [newSocialPlatform, setNewSocialPlatform] = useState<SocialPost['platform']>('LinkedIn');
  const [newSocialType, setNewSocialType] = useState('Founder story');
  const [newSocialCaption, setNewSocialCaption] = useState('Fife Local shop went from pending Google Maps records to 4,200 unique impressions in 2 weeks using zero paid ad budget. Our local B2B software automates booking reminders natively.');
  const [newSocialHashtags, setNewSocialHashtags] = useState('LocalSEO, ZeroAdBudget, WebPlatform, FifeSaaS');
  const [newSocialImageIdea, setNewSocialImageIdea] = useState('A modern, high-contrast mockup of an admin tablet displaying active converting analytics.');
  const [newSocialVideoScript, setNewSocialVideoScript] = useState('Hook: Stop throwing £500 a month on Meta Ads... Scene 1: Look at slow menu page. Scene 2: Showcase clean, immediate React template.');
  const [newSocialCta, setNewSocialCta] = useState('Request Setup');
  const [newSocialLink, setNewSocialLink] = useState('https://preview.nexoraxdigital.co.uk/digital-products');
  const [newSocialStatus, setNewSocialStatus] = useState<SocialPost['status']>('Draft');

  // --- SECTION 3: ORGANIC SOCIAL CONTENT STATE ---
  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([
    {
      id: 'post-1',
      platform: 'LinkedIn',
      contentType: 'Prep/After Case Study',
      caption: 'Fife Local takeaway went from pending Google Maps records to 4,200 unique impressions in 2 weeks. How? Standard high-contrast responsive layouts and a 15-second QR code on their takeaway boxes. Zero third-party ad budget.',
      hashtags: ['LocalSEO', 'DunfermlineBusiness', 'ZeroAdBudget', 'WebPlatform'],
      imageIdea: 'A high-contrast smartphone displaying an interactive menu scan.',
      videoScript: 'Hook: "Stop throwing £500 a month on Meta Ads..." Scene 1: Point to slow mobile menu page. Scene 2: Showcase clean, immediate NexoraX template on port 3000.',
      cta: 'Request Setup',
      destinationLink: 'https://preview.nexoraxdigital.co.uk/digital-products',
      qrCampaignId: 'qr-camp-1',
      status: 'Draft'
    }
  ]);
  const [selectedSocialPostId, setSelectedSocialPostId] = useState<string>('post-1');

  // --- SECTION 4: SHORT VIDEO SCRIPT STATE ---
  const [selectedVideoType, setSelectedVideoType] = useState<string>('15-second launch video');
  const [videoScriptDraft, setVideoScriptDraft] = useState<ShortVideoScript | null>(null);

  // --- SECTION 4: REVOLUTIONARY NEURAL VIDEO SYNTHESIS STATE ---
  const [videoPrompt, setVideoPrompt] = useState<string>(
    'Create an out-of-this-universe B2B hyper-scale graphic engine with spinning obsidian crystals, emitting fluorescent cyan networks showing zero manual setup and direct-billing metrics.'
  );
  const [uploadedLogo, setUploadedLogo] = useState<string>(''); // Data URI base64
  const [uploadedLogoName, setUploadedLogoName] = useState<string>('');
  const [selectedLogoPreset, setSelectedLogoPreset] = useState<string>('nexorax_glowing');
  const [logoPosition, setLogoPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('bottom-right');
  const [logoOpacity, setLogoOpacity] = useState<number>(85); // %
  const [logoScale, setLogoScale] = useState<number>(100); // size factor
  
  // Synthesizer simulation parameters
  const [synthModel, setSynthModel] = useState<string>('NexoraX-Neural-Synthesizer-v5');
  const [synthDiffusionRate, setSynthDiffusionRate] = useState<number>(88);
  const [synthTemporalConsistency, setSynthTemporalConsistency] = useState<number>(94);
  const [synthFps, setSynthFps] = useState<number>(60);
  const [synthResolution, setSynthResolution] = useState<string>('8K Ultra Cosmic');
  const [synthCameraPath, setSynthCameraPath] = useState<string>('Simulated 3D Drone Orbit');
  
  // Live renderer running states
  const [isSynthRunning, setIsSynthRunning] = useState<boolean>(false);
  const [synthProgress, setSynthProgress] = useState<number>(0);
  const [synthActiveLog, setSynthActiveLog] = useState<string>('');
  const [synthLogs, setSynthLogs] = useState<string[]>([]);
  const [synthStreamPlaying, setSynthStreamPlaying] = useState<boolean>(true);
  const [synthThemeColor, setSynthThemeColor] = useState<'cyberpunk_purple' | 'matrix_emerald' | 'gold_vanguard' | 'cosmic_cyan'>('cyberpunk_purple');

  // --- SECTION 5: SEO DRAFT STORE STATE ---
  const [seoDrafts, setSeoDrafts] = useState<SEODraft[]>([
    {
      id: 'seo-1',
      keyword: 'Website design Fife',
      title: 'Premium Website Design Fife | NexoraX Professional Dev Nodes',
      metaDesc: 'Looking for reliable web design in Fife, Dunfermline, and St Andrews? NexoraX engineers zero-latency websites optimized for Google and Bing search rankings.',
      slug: 'website-design-fife',
      heroHeadline: 'Launch Your Business Website Faster In Fife',
      problem: 'Slow, expensive web agencies in Fife that take 3 months to deliver a simple catalog layout while locking your domains.',
      solution: 'Ready-made responsive B2B websites with instant deployment. Packaged with local keywords and DKIM/SPF protection parameters.',
      features: ['Modern Inter Display tracking', 'Extreme mobile responsiveness (44px target bounds)', 'No-ad search indexing sitemap strings'],
      faq: [
        { q: 'How fast do Fife business sites launch?', a: 'Under 2 business days with NexoraX Managed Launch systems.' },
        { q: 'Is hosting included?', a: 'Yes, fully secure local cloud-based containers with zero manual configuration.' }
      ],
      cta: 'Request Setup',
      internalLinks: ['/digital-products', '/google-visibility'],
      localKeywords: ['Fife web designer', 'Dunfermline ecommerce', 'web development st andrews'],
      approved: false
    },
    {
      id: 'seo-2',
      keyword: 'Ready-made websites UK',
      title: 'Ready-Made Websites UK | Secure Ready-to-use Corporate SaaS Node',
      metaDesc: 'Discover plug-and-play ready-made business websites in the UK. Launch instantly with pre-configured forms, booking assistants, and Google search credentials.',
      slug: 'ready-made-websites-uk',
      heroHeadline: 'Unlock Scalable UK Enterprise Nodes Instantly',
      problem: 'Heavy custom platform fees and endless developer feedback loops that drain early startup liquidity.',
      solution: 'Strictly static and server-proxy architecture designed to load in 0.8s and capture high-fidelity B2B leads.',
      features: ['Pre-baked Make.com flow support', 'First-party local database lead storage', 'Strict GDPR soft opt-in checklist standard'],
      faq: [
        { q: 'Are these templates or ready websites?', a: 'Fully built websites pre-configured with local database forms ready for domain mapping.' }
      ],
      cta: 'Start Managed Launch',
      internalLinks: ['/seo-pages', '/digital-products'],
      localKeywords: ['ready built websites UK', 'buy done for you software platform', 'instant business sites'],
      approved: true
    },
    {
      id: 'seo-3',
      keyword: 'Website design Dunfermline',
      title: 'Affordable Website Design Dunfermline | NexoraX Local',
      metaDesc: 'Done-for-you professional websites for trades, salons, and offices in Dunfermline and West Fife. SEO configured natively. Request absolute free website briefs today.',
      slug: 'website-design-dunfermline',
      heroHeadline: 'Grow Your Dunfermline Local Brand Natively',
      problem: 'Losing organic search customers in Dunfermline because your competitors are listed on Google Maps while your business website lacks structural content.',
      solution: 'Direct local SEO injection, instant mobile-responsive click layouts, and high-conversion QR campaigns mapped to vehicle stickers.',
      features: ['GBP optimization mapping', 'Super high-contrast readability parameters', 'No hidden ongoing maintenance and hosting fees'],
      faq: [
        { q: 'Do you design salon or trades websites?', a: 'Yes. We have specialized ready-made packages targeting driving schools, salons, restaurants, and trades.' }
      ],
      cta: 'Book Consultation',
      internalLinks: ['/website-design-fife'],
      localKeywords: ['web design dunfermline company', 'trades website dunfermline', 'fife digital marketing'],
      approved: false
    }
  ]);

  // --- SECTION 6: GOOGLE AND BING VISIBILITY CHECKLIST STATE ---
  const [checklist, setChecklist] = useState<Record<string, boolean>>({
    gbpProfileCreated: true,
    gbpNameAdded: true,
    gbpWebsiteLinked: true,
    gbpServiceAreaSet: false,
    gbpServicesListed: false,
    gbpPhotosUploaded: true,
    gbpDescriptionConfigured: false,
    gbpLaunchPostPublished: false,
    gbpProductsSaturated: false,
    gbpVerificationPending: true,

    gscDomainVerified: true,
    gscSitemapSubmitted: false,
    gscIndexingActive: false,
    gscMobileUsabilityOk: true,
    gscSpeedMetricsChecked: false,
    gscIndexingRequestSent: false,

    bingSiteAdded: false,
    bingSitemapSubmitted: false,
    bingSeoScanned: false,
    bingImportantUrlsSent: false,
    bingSearchTracked: false
  });

  const [paidAdsStatus, setPaidAdsStatus] = useState<'Paused' | 'Active'>('Paused');

  // --- SECTION 7 & 8: FUNNEL & OFFER LADDER LEAD STORAGE ---
  const [leads, setLeads] = useState<GrowthLead[]>([
    {
      id: 'lead-1',
      name: 'Claire Jenkins',
      email: 'claire@fifebeauty.co.uk',
      company: 'Fife Beauty Salon',
      toolUsed: 'Free Business Website Checker',
      consent: true,
      status: 'Setup Requested',
      timestamp: '2026-06-18 10:15'
    },
    {
      id: 'lead-2',
      name: 'Peter Ross',
      email: 'p.ross@rossplumbing.co.uk',
      company: 'Ross Trades & Plumbing',
      toolUsed: 'Free Smart Gadget Finder',
      consent: true,
      status: 'Quote Requested',
      timestamp: '2026-06-18 12:44'
    }
  ]);

  const [selectedLeadStatus, setSelectedLeadStatus] = useState<string>('lead-1');

  // --- SECTION 9: COMMUNITY GOALS TRACKER ---
  const [communityGoals, setCommunityGoals] = useState<CommunityGoal[]>([
    {
      id: 'goal-1',
      goalName: 'Local High Street Digitization Grant',
      causeName: 'Dunfermline Local Enterprise Charity',
      status: 'Planned',
      notes: 'Eligible receipts basis setup: allocations calculated upon certified sales within KY11/KY12 districts.',
      allocationBasis: '3% of regional net billing',
      recordedAllocation: 750,
      publicDisclosure: true,
      reviewStatus: 'Pending Audit'
    },
    {
      id: 'goal-2',
      goalName: 'Green Cloud Web Server Contribution',
      causeName: 'Global Cloud Carbon Neutral Initiative',
      status: 'Recorded',
      notes: 'Recorded allocation matched 100% via structural maintenance grants.',
      allocationBasis: '10% maintenance offset framework',
      recordedAllocation: 450,
      publicDisclosure: false,
      reviewStatus: 'Approved'
    }
  ]);
  const [newGoalName, setNewGoalName] = useState('');
  const [newCauseName, setNewCauseName] = useState('');

  // --- SECTION 10: MONDAY LAUNCH PLAN CHECKLIST ---
  const [beforeLaunchChecklist, setBeforeLaunchChecklist] = useState<Record<string, boolean>>({
    homepageChecked: true,
    digitalProductsChecked: true,
    freeToolsChecked: false,
    qrGeneratorChecked: true,
    contactFormChecked: true,
    requestSetupChecked: false,
    privacyTermsChecked: true,
    sitemapSubmitted: false,
    gbpProfileDrafted: false,
    socialProfilesUpdated: true,
    tenPostsCreated: false,
    threeScriptsWritten: false,
    fiveCampaignsSet: false
  });

  const [mondayMorningChecklist, setMondayMorningChecklist] = useState<Record<string, boolean>>({
    pubFb: false,
    pubIg: false,
    pubLi: false,
    pubTiktok: false,
    pubYt: false,
    postGbp: false,
    shareQr: false,
    msgWarmContacts: false,
    addEmailSig: true,
    trackLeads: false
  });

  // --- SECTION 11: DEVELOPER API SIMULATOR ---
  const [selectedApiRoute, setSelectedApiRoute] = useState<string>('GET /api/admin/growth-engine');
  const [apiResponsePayload, setApiResponsePayload] = useState<any>(null);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);

  // Dynamic lists of lead tools
  const freeToolsData = [
    { id: 1, name: 'Free QR Code Generator', desc: 'Create instantly printable QR matrix blocks for shop windows or business cards.', inputs: ['websiteUrl'] },
    { id: 2, name: 'Free Business Website Checker', desc: 'Analyzes design density, loading speeds (0.8s targets), and DKIM headers.', inputs: ['websiteUrl'] },
    { id: 3, name: 'Free AI Website Brief Generator', desc: 'Transform rough specifications into structured, B2B-ready architectural plans.', inputs: ['businessName', 'industry', 'painPoint'] },
    { id: 4, name: 'Free Social Caption Generator', desc: 'Generate high-conversion, non-paid social teasers with customized callouts.', inputs: ['industry', 'painPoint'] },
    { id: 5, name: 'Free Slide Deck Outline Generator', desc: 'Outline structural slide proposals designed to pitch local business websites.', inputs: ['presentationTopic'] },
    { id: 6, name: 'Free Business Card QR Generator', desc: 'Build professional, scan-to-contact digital vCards instantly.', inputs: ['contactName', 'contactEmail', 'businessName'] },
    { id: 7, name: 'Free Digital Product Finder', desc: 'Match local client operations with profitable digital assets to package.', inputs: ['skillNiche'] },
    { id: 8, name: 'Free Smart Gadget Finder', desc: 'Identify smart automated hardware setups that reduce physical shop overheads.', inputs: ['industry'] },
    { id: 9, name: 'Free AI Automation Idea Generator', desc: 'Construct targeted n8n/Make.com workflows to auto-respond to inquiries.', inputs: ['businessName', 'painPoint'] },
    { id: 10, name: 'Free Website Quote Request', desc: 'Get transparent cost specifications instantly. No payment requested.', inputs: ['businessName', 'featuresCount'] }
  ];

  // QR templates
  const qrTemplates = [
    { type: 'Business card QR', path: '/vcard-export' },
    { type: 'Website QR', path: '/home' },
    { type: 'Digital Products QR', path: '/digital-products' },
    { type: 'Marketplace QR', path: '/marketplace' },
    { type: 'Restaurant menu QR', path: '/menu' },
    { type: 'WhatsApp enquiry QR', path: '/whatsapp' },
    { type: 'Booking request QR', path: '/book' },
    { type: 'Google review QR', path: '/reviews' },
    { type: 'Slide deck QR', path: '/deck' },
    { type: 'Product offer QR', path: '/discount' },
    { type: 'Social profile QR', path: '/socials' },
    { type: 'Event flyer QR', path: '/flyer' },
    { type: 'Shop window QR', path: '/window' },
    { type: 'Vehicle sticker QR', path: '/van-decal' }
  ];

  // Social content templates generator lookup
  const socialTemplates = [
    { type: 'Launch post', platform: 'Facebook' },
    { type: 'Founder story', platform: 'LinkedIn' },
    { type: 'Free tool post', platform: 'X/Twitter' },
    { type: 'QR code post', platform: 'Instagram' },
    { type: 'Ready-made website post', platform: 'LinkedIn' },
    { type: 'AI assistant post', platform: 'TikTok' },
    { type: 'Slide deck post', platform: 'Pinterest' },
    { type: 'Digital product post', platform: 'LinkedIn' },
    { type: 'Smart gadget post', platform: 'TikTok' },
    { type: 'Tech update post', platform: 'YouTube Shorts' },
    { type: 'Before/after post', platform: 'Instagram' },
    { type: 'Client problem/solution post', platform: 'Facebook' }
  ];

  // Video script templates
  const shortVideoTemplates = [
    { type: '15-second launch video', hook: 'Your business website does not need to take months. NexoraX is built to help you launch faster.' },
    { type: '30-second service explainer', hook: 'Are slow development agencies eating away your early launch cashflow?' },
    { type: 'Website template preview', hook: 'Scan a QR, preview B2B React storefronts, and get online in Dunfermline under 48 hours.' },
    { type: 'AI assistant demo', hook: 'Watch this automated agent respond to cold customer booking requests on port 3000.' },
    { type: 'QR code demo', hook: 'A shop window sticker that brings 40% more warm call leads without a single paid ad campaign.' },
    { type: 'Slide deck offer', hook: 'Tired of building pitch decks from zero? Grab this pre-written B2B outline immediately.' },
    { type: 'Digital product shelf demo', hook: 'Unveiling the NexoraX Digital Product Marketplace: ready-made templates active today.' },
    { type: 'Smart gadget marketplace demo', hook: 'Connect physical business operations to secure cloud nodes cleanly.' },
    { type: 'Tech update radar demo', hook: 'Breaking down DKIM, SPF, and robots index safety keys for secure businesses.' },
    { type: 'Behind-the-scenes build', hook: 'How our lead architect structures 18 grid sectors in under 0.8s.' }
  ];

  // --- TRIGGER TOOL GENERATION (DYNAMIC MOCK RESULTS) ---
  const handleGenerateToolResult = () => {
    setIsToolGenerating(true);
    setTimeout(() => {
      let resultPayload: any = {};
      const t = freeToolsData.find(tool => tool.id === selectedToolId);
      if (!t) return;

      switch(selectedToolId) {
        case 1: // QR Code Gen
          resultPayload = {
            title: 'Dynamic QR Allocation Rendered',
            data: toolInputs.websiteUrl || 'https://nexorax.co.uk',
            codePreview: '🚀 [Simulated QR Matrix Pattern Generated Successfully] 🧱',
            payload: `URL: ${toolInputs.websiteUrl}\nUTM: utm_source=organic_qr&utm_medium=free_generator`,
            fileSize: '4.2 KB SVG Output'
          };
          break;
        case 2: // Checker
          resultPayload = {
            title: 'Audit Report: ' + (toolInputs.websiteUrl || 'Generic Host'),
            score: '84/100 (NEEDS SPEED RE-ALIGNMENT)',
            details: [
              'Page Speed: 2.1s (Unoptimized large assets. Target is under 0.8s on NexoraX)',
              'Mobile Target Size: 32px targets detected (Minimum for clean responsive touch is 44px)',
              'Security: DKIM/SPF protection headers are generic. No custom DMARC keys found.',
              'Robots configuration: Sitemap file not indexable. Search engine crawlers have stalled.'
            ]
          };
          break;
        case 3: // AI Spec Brief
          resultPayload = {
            title: `B2B Specification Brief: ${toolInputs.businessName || 'Default Enterprise'}`,
            hierarchy: ['Home Display Hero Grid', 'Modular Pricing Blocks', 'Digital Lead Sourcing Module with soft checkboxes', 'Strict FAQ Schema Footer'],
            specs: 'Framework: React 18 / Tailwind CSS. Server support: Node Express secure endpoints. Target latency: under 0.8s.'
          };
          break;
        case 4: // Caption
          resultPayload = {
            title: 'Optimized Social Media Hook Set',
            captions: [
              `Stop paying £40 per lead on Facebook ads. Our local B2B software automates customer booking confirmation files natively. Scan to map yours today. #LocalSEO #Dunfermline #GrowthEngine`,
              `We built a custom brief for ${toolInputs.businessName || 'our brand'} using zero-budget SEO pages. Scan here to grab your spec outlines free.`
            ]
          };
          break;
        case 5: // Slide Outline
          resultPayload = {
            title: `Presentation Deck Outline: ${toolInputs.presentationTopic || 'Tech Transformation'}`,
            slides: [
              'Slide 1: Title & Zero-Cost Branding Blueprint',
              'Slide 2: Current Bottlenecks (Manual spreadsheets vs Cloud Sync)',
              'Slide 3: Solution (Pre-baked automated Node dashboard)',
              'Slide 4: Financial Projections (3% levy, 10% maintenance fee offsets)',
              'Slide 5: Book Technical Interview Setup CTA'
            ]
          };
          break;
        case 6: // Contact QR
          resultPayload = {
            title: `vCard Business Card QR Matrix for ${toolInputs.contactName || 'Siddique'}`,
            vCardString: `BEGIN:VCARD\nVERSION:3.0\nN:${toolInputs.contactName || 'Mahmood'}\nORG:${toolInputs.businessName || 'NexoraX'}\nEMAIL:${toolInputs.contactEmail || 'siddique@nexorax.co.uk'}\nEND:VCARD`,
            codePreview: '🚀 [Simulated Contact vCard QR Block - Standard Ingress Ready] 🧱'
          };
          break;
        case 7: // Product Finder
          resultPayload = {
            title: `Niche Assets Match for: ${toolInputs.skillNiche || 'Development'}`,
            recommendedProducts: [
              'Digital Slide Proposal Pack with customizable Figma keys',
              'Pre-packaged local Make.com workflow json files',
              'Done-for-you localized driving/trades website templates'
            ]
          };
          break;
        case 8: // Gadget Finder
          resultPayload = {
            title: 'IoT & Smart Gadget Automation Mapping',
            suggestedHardware: [
              'NFC Brand Cards paired with scanned Google Review redirects',
              'Local QR Menu plates for paperless checkout registration',
              'Smart automated print hub for physical billing receipts'
            ]
          };
          break;
        case 9: // AI idea
          resultPayload = {
            title: 'Suggested Server-Authoritative Workflow Chain',
            flow: 'Inbound Customer Web Form ➔ Local Database Lead Capture ➔ Soft Marketing Checkbox Audit ➔ Auto-Draft Custom Specification Reply (Avoids manual typing)',
            servicesMap: 'Trigger: Webhook. Router: Make.com JSON mapper. Destination: NexoraX Resource Store.'
          };
          break;
        case 10: // Quote
          resultPayload = {
            title: `Transparent B2B Project Quote for ${toolInputs.businessName || 'Fife Retailers'}`,
            breakdown: [
              'Interactive Layouts: £0 launch cost under grant options',
              'Standard secure local database state: Included',
              'DKIM/SPF and GBP Verification: Free under zero-budget guides',
              'Optional maintenance fee: 10% (only when cashflow clears)'
            ],
            terms: 'Choose the level that fits your business. Final quote depends on requirements.'
          };
          break;
      }

      setToolGeneratedResult(resultPayload);
      setIsToolGenerating(false);
      onAddLog('Public Tools', `Executed tool action: ${t.name}`, 'SUCCESS', `Outputs parsed safely`);
    }, 1200);
  };

  // --- SAVE TOOL RESULT / LEAD PROPAGATION ---
  const handleSaveResultAsLead = (ctaType: string) => {
    const t = freeToolsData.find(tool => tool.id === selectedToolId);
    if (!t) return;

    // Consent restriction standard check
    if (!toolMarketingConsent) {
      alert('Marketing consent must be optionally checked before saving lead files to the WorkSuite database.');
      return;
    }

    const newLead: GrowthLead = {
      id: `lead-${Date.now()}`,
      name: toolInputs.contactName || 'Anonymous Visitor',
      email: toolInputs.contactEmail || 'visitor@nexora-inbound.co.uk',
      company: toolInputs.businessName || 'Local Prospect',
      toolUsed: t.name,
      consent: true,
      status: ctaType === 'Request Setup' ? 'Setup Requested' : 'Quote Requested',
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };

    setLeads([newLead, ...leads]);
    onAddLog('Lead Ingestion', `Stored lead via CTA: "${ctaType}"`, 'SUCCESS', `Ingested lead metadata for ${newLead.company}`);
    onAddTaskToWorkSuite(`[LEAD STAGE: ${newLead.status.toUpperCase()}] Follow up with ${newLead.name} (${newLead.company}) regards ${t.name}`, 'HIGH');

    alert(`LEAD FILE LOGGED SECURELY!\n\nLead Name: ${newLead.name}\nCompany: ${newLead.company}\nStatus: ${newLead.status}\n\nOur Chief of Staff Node has routed this request directly to the WorkSuite and Client Dashboard queue for manual review in 0.8s!`);
  };

  // --- TRIGGER DIGITAL CAMPAIGN QR GENERATION ---
  const handleCreateCampaign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCampaignName.trim()) return;

    const newCamp: QRCampaign = {
      id: `qr-camp-${Date.now()}`,
      name: newCampaignName,
      type: newCampaignType,
      url: `${newCampaignUrl}${newCampaignUrl.includes('?') ? '&' : '?'}utm_source=${encodeURIComponent(newCampaignUtmSource)}&utm_medium=${encodeURIComponent(newCampaignUtmMedium)}&utm_campaign=${encodeURIComponent(newCampaignUtmCampaign)}&lead_source=${encodeURIComponent(newCampaignLeadSourceTag)}`,
      utmSource: newCampaignUtmSource,
      utmMedium: newCampaignUtmMedium,
      utmCampaign: newCampaignUtmCampaign,
      caption: newCampaignCaption,
      socialCaption: newCampaignSocialCaption,
      flyerText: newCampaignFlyerText,
      status: 'Active',
      leadSourceTag: newCampaignLeadSourceTag || 'Organic QR'
    };

    setQrCampaigns([...qrCampaigns, newCamp]);
    onAddLog('QR Campaigns', `Created campaign: ${newCampaignName}`, 'SUCCESS', `Configured UTM links targeting ${newCampaignUrl}`);
    
    // Set custom visual feedback
    setActionFeedback({
      message: `Success! QR Campaign "${newCampaignName}" has been successfully active-listed with UTM tracking elements and Source tagging: "${newCamp.leadSourceTag}".`,
      type: 'success'
    });
  };

  // --- TRIGGER SHORT VIDEO SCRIPT SYNTHESIS ---
  const handleSynthesizeVideoScript = () => {
    setIsToolGenerating(true);
    setTimeout(() => {
      const match = shortVideoTemplates.find(v => v.type === selectedVideoType);
      const hookText = match ? match.hook : 'NexoraX can help you launch faster.';

      const script: ShortVideoScript = {
        id: `scr-${Date.now()}`,
        type: selectedVideoType,
        hook: hookText,
        scene1: 'Scene 1 (0-5s): Point camera directly to a sluggish, cluttered website and shake head. Text overlay: "Wait months for web development?"',
        scene2: 'Scene 2 (5-10s): Cut to clean premium React dashboard running instantly on port 3000. Contrast purple glassmorphism card layouts against obsidian backdrop.',
        scene3: 'Scene 3 (10-15s): Scan our Business Card QR code. Show rapid lead creation in WorkSuite live. Overlay text: "Under 48 hours. Ready-made websites."',
        screenText: 'No-fund grant setup available. Scan or book consultation online.',
        voiceover: `Why waste early budget on heavy paid ad agencies? NexoraX builds fully indexed search pages and done-for-you portals instantly. Tap the link to view our digital products.`,
        cta: 'Start Managed Launch',
        caption: 'Stop waiting on slow web design. Build your business automation today.',
        hashtags: ['ShortVideoReels', 'OrganicMarketing', 'FifeBusiness', 'NoFundSetup']
      };

      setVideoScriptDraft(script);
      setIsToolGenerating(false);
      onAddLog('Social Video', `Generated short script for type: ${selectedVideoType}`, 'SUCCESS', 'Script compiled with clear scene metrics');
    }, 1000);
  };

  // --- TRIGGER OUT-OF-THIS-UNIVERSE NEURAL VIDEO SYNTHESIS ---
  const handleLaunchNeuralSynthesizer = () => {
    if (isSynthRunning) return;
    
    setIsSynthRunning(true);
    setSynthProgress(0);
    setSynthLogs([]);
    setSynthStreamPlaying(false);
    
    const steps = [
      { prg: 5, msg: "Establishing secure GPU core connection to NexoraX Quantum Node-09.2...", event: "GRID_CONNECT" },
      { prg: 18, msg: `Parsing multi-modal Prompt directive: "${videoPrompt.slice(0, 48)}..."`, event: "PROMPT_PARSE" },
      { prg: 35, msg: `Aligning weights for layer matrix model [${synthModel}] with Diffusion Rate at ${synthDiffusionRate}%`, event: "WEIGHTS_LOAD" },
      { prg: 50, msg: `Synchronizing frame consistency indexes at ${synthTemporalConsistency}% parameters at ${synthFps} FPS...`, event: "TEMPORAL_ALIGN" },
      { prg: 68, msg: `Tracing 3D vector camera panning standard: [${synthCameraPath}]`, event: "CAMERA_VECTOR" },
      { prg: 82, msg: `Superimposing active branding overlay [Logo Preset: ${selectedLogoPreset.replace('_', ' ')}${uploadedLogo ? ' / + Custom Uploaded' : ''}] at scale ${logoScale}%`, event: "WATERMARK_COMP" },
      { prg: 94, msg: `Synthesizing neural frame buffers at high sitemap resolution: ${synthResolution}...`, event: "FRAME_RENDER" },
      { prg: 100, msg: "Out-of-this-universe Neural Video has successfully compiled into the local storage!", event: "RENDER_COMPLETE" }
    ];
    
    let currentStepIdx = 0;
    
    const interval = setInterval(() => {
      if (currentStepIdx < steps.length) {
        const step = steps[currentStepIdx];
        setSynthProgress(step.prg);
        setSynthLogs(prev => [...prev, `[${step.prg}%] ${step.msg}`]);
        
        onAddLog('Neural Videocasting', `Stage: ${step.event} - Progress ${step.prg}%`, 'SUCCESS');
        currentStepIdx++;
      } else {
        clearInterval(interval);
        setIsSynthRunning(false);
        setSynthStreamPlaying(true);
        
        // Formulate a beautiful output script matching their custom prompt & knobs
        const customScript: ShortVideoScript = {
          id: `scr-custom-${Date.now()}`,
          type: `Neural Synthesizer Output [${synthResolution}]`,
          hook: videoPrompt.length > 90 ? videoPrompt.substring(0, 90) + '...' : videoPrompt,
          scene1: `Scene 1 (0-5s): A magnificent particle storm in a ${synthThemeColor.replace('_', ' ')} layout. Logo watermark super-imposed in ${logoPosition.replace('-', ' ')} coordinates.`,
          scene2: `Scene 2 (5-10s): Camera begins a ${synthCameraPath} sweeps over active responsive elements displaying zero paid ad budget metrics.`,
          scene3: `Scene 3 (10-15s): Leads database logs populate in real-time, matching the client request flow. Displaying instant booking controls.`,
          screenText: `Generated by NexoraX ${synthModel}. Ultra FPS: ${synthFps}.`,
          voiceover: `This premium, out-of-this-universe video was synthesized directly from multi-modal text prompt matrices. Fully configured without unrequested developers latency.`,
          cta: 'Secure Active Setup',
          caption: `Reels generated using prompt: "${videoPrompt}"`,
          hashtags: ['NeuralSynthesis', 'OutofThisUniverse', 'ZeroManualSetup', 'NexoraxDigital', 'FifeSaaS']
        };
        
        setVideoScriptDraft(customScript);
        
        setActionFeedback({
          message: `Success! Out-of-this-universe Neural Video compiled dynamically. View live simulated rendering with interactive overlay watermark below!`,
          type: 'success'
        });
        
        onAddLog('Social Video', 'Successfully compiled neural video synthesis via Antigravity Engine', 'SUCCESS', `Active prompt: "${videoPrompt.substring(0, 40)}..."`);
      }
    }, 700);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setActionFeedback({
          message: 'Error: Logo file size exceeds the 2MB node allocation limit!',
          type: 'info'
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedLogo(reader.result as string);
        setUploadedLogoName(file.name);
        setActionFeedback({
          message: `Success! "${file.name}" uploaded. Active logo watermark maps directly to simulated renderer.`,
          type: 'success'
        });
        onAddLog('Systems Admin', `Custom branding logo uploaded: ${file.name}`, 'SUCCESS');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setActionFeedback({
          message: 'Error: Logo size exceeds safety bounds (Max 2MB)!',
          type: 'info'
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedLogo(reader.result as string);
        setUploadedLogoName(file.name);
        setActionFeedback({
          message: `Success! "${file.name}" uploaded via Drag-And-Drop. Active logo watermark updated.`,
          type: 'success'
        });
        onAddLog('Systems Admin', `Custom logo dropped: ${file.name}`, 'SUCCESS');
      };
      reader.readAsDataURL(file);
    }
  };

  // --- TRIGGER SEO PAGE CREATION ---
  const handleCreateSeoDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSeoKeyword.trim()) return;

    const newDraft: SEODraft = {
      id: `seo-draft-${Date.now()}`,
      keyword: newSeoKeyword,
      title: newSeoTitle,
      metaDesc: newSeoMetaDesc,
      slug: newSeoSlug || newSeoKeyword.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      heroHeadline: newSeoHeroHeadline,
      problem: newSeoProblem,
      solution: newSeoSolution,
      features: newSeoFeatures.split(',').map(f => f.trim()).filter(Boolean),
      localKeywords: newSeoLocalKeywords.split(',').map(k => k.trim()).filter(Boolean),
      faq: [
        { q: newSeoFaqQ, a: newSeoFaqA }
      ],
      cta: 'Request Setup',
      internalLinks: ['/digital-products'],
      approved: false
    };

    setSeoDrafts([newDraft, ...seoDrafts]);
    onAddLog('SEO Generator', `Drafted Google index content for core keyword: "${newSeoKeyword}"`, 'SUCCESS', `Created URL slug node: /${newDraft.slug}`);
    
    setActionFeedback({
      message: `Success! Local SEO page drafted for keyword "${newSeoKeyword}". Review details and grant Admin Approval to activate sitemap index schemas.`,
      type: 'success'
    });
  };

  // --- TRIGGER SEO PAGE APPROVAL ---
  const handleToggleSeoApproval = (id: string) => {
    setSeoDrafts(prev => prev.map(draft => {
      if (draft.id === id) {
        const nextState = !draft.approved;
        onAddLog('SEO Generator', `Update SEO draft approval: ${draft.keyword}`, 'SUCCESS', nextState ? 'Set Approved and indexable' : 'Returned to draft pool');
        return { ...draft, approved: nextState };
      }
      return draft;
    }));
  };

  // --- TRIGGER BACKEND API HANDSHAKE SIMULATION ---
  const handleTriggerApiHandshake = () => {
    setIsApiLoading(true);
    setTimeout(() => {
      let mockRes: any = {};
      switch(selectedApiRoute) {
        case 'GET /api/admin/growth-engine':
          mockRes = {
            status: 'online',
            service: 'NexoraX Quantum Growth Engine',
            allocatedCampaignsCount: qrCampaigns.length,
            seoDraftsStatus: { approved: seoDrafts.filter(d => d.approved).length, total: seoDrafts.length },
            activePaidAdsMode: 'Paused (Zero budget strategy enforced)'
          };
          break;
        case 'POST /api/admin/growth-engine/generate-qr':
          mockRes = {
            success: true,
            uuid: 'qr-gen-883921-b',
            utm_params: { source: 'organic_qr_node', medium: 'invoice_register_acq' },
            rendered_bytes_svg: '...[ENCODED MATRIX STREAM]...'
          };
          break;
        case 'POST /api/admin/growth-engine/generate-social-posts':
          mockRes = {
            success: true,
            generated_posts: [
              { platform: 'LinkedIn', content: 'Fife local shop saved £2,000 using free QR tools.', date_queued: 'Manual queue' }
            ]
          };
          break;
        case 'POST /api/admin/growth-engine/generate-video-scripts':
          mockRes = {
            success: true,
            duration: '15 seconds',
            hook: 'Stop throwing away B2B cashflow.',
            scene_framework: '3-sector grid sync'
          };
          break;
        case 'POST /api/admin/growth-engine/generate-seo-page':
          mockRes = {
            success: true,
            page_slug: 'website-design-fife',
            status: 'Draft (Requires manual Admin approval rule state)'
          };
          break;
        case 'GET /api/admin/growth-engine/leads':
          mockRes = {
            success: true,
            total_logged: leads.length,
            data: leads
          };
          break;
        default:
          mockRes = { error: 'Route placeholder not configured inside core node.' };
      }
      setApiResponsePayload(mockRes);
      setIsApiLoading(false);
      onAddLog('API Simulation', `Simulate endpoint execution: ${selectedApiRoute}`, 'SUCCESS', 'Response payload parsed successfully');
    }, 800);
  };

  // --- DYNAMIC CALCULATOR COMPUTES ---
  const beforeLaunchProgress = Math.round(
    (Object.values(beforeLaunchChecklist).filter(Boolean).length / Object.keys(beforeLaunchChecklist).length) * 100
  );
  
  const mondayMorningProgress = Math.round(
    (Object.values(mondayMorningChecklist).filter(Boolean).length / Object.keys(mondayMorningChecklist).length) * 100
  );

  return (
    <div className="w-full space-y-6 text-left relative selection:bg-[#00ffff]/30 selection:text-white">
      {/* 1. Header Hero Panel */}
      <div className="glass-panel border-2 border-purple-500/30 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#0a0512] via-[#04010a] to-black relative overflow-hidden shadow-[0_0_35px_rgba(232,64,243,0.15)]">
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-650/5 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-650/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-purple-950/40 border border-purple-500/40 rounded-xl">
                <RocketIcon className="w-6 h-6 text-[#e840f3] animate-pulse" />
              </span>
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.25em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/25 flex items-center gap-1">
                <Sparkles size={10} /> ZERO-BUDGET DEMAND ENGINE
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-sans font-black text-white uppercase tracking-tight">
              NexoraX Zero-Budget Growth Engine
            </h1>
            <p className="text-xs sm:text-sm font-sans font-medium text-[#00ffff] normal-case">
              Generate Leads, QR Campaigns, Organic Post Drafts, Short Video Scripts, & Local SEO Pages Natively
            </p>
            <p className="text-xs text-slate-450 max-w-2xl font-sans normal-case leading-relaxed">
              Launch your corporate digital products pipeline with zero paid ad dependency first. Capture inbound clients using free utility magnets, check off Google visibility milestones, and track lead pipelines cleanly in the WorkSuite dashboard.
            </p>
          </div>

          <div className="bg-black/90 border border-purple-500/30 p-4 rounded-xl font-mono text-xs min-w-[240px] space-y-2 shrink-0">
            <div className="flex justify-between border-b border-zinc-900 pb-1 text-[10px] uppercase font-bold text-slate-500">
              <span>Paid Advertising State</span>
              <AlertCircle size={12} className="text-amber-500" />
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-slate-400">Paid Ads Status:</span>
              <span className="px-2 py-0.5 bg-rose-950/50 border border-rose-500/20 text-rose-400 rounded text-[9px] font-bold uppercase">
                {paidAdsStatus}
              </span>
            </div>
            <p className="text-[9px] text-amber-500 leading-normal normal-case font-medium">
              * Temporary hold active until local client subscription cashflow improves. Focus entirely on organic visibility.
            </p>
          </div>
        </div>

        {/* Outer Tabs selector */}
        <div className="mt-8 border-t border-zinc-900 pt-4 flex flex-wrap gap-1.5 text-[10.5px] font-mono font-black uppercase">
          {[
            { id: 'Zero-Budget HUD', icon: Sliders },
            { id: 'Trend Scout & Queue', icon: Sparkles },
            { id: 'Free Lead Tools', icon: Mail },
            { id: 'QR Campaigns', icon: QrCode },
            { id: 'Social Content', icon: Share2 },
            { id: 'SEO Pages', icon: FileText },
            { id: 'Visibility Checklist', icon: Globe },
            { id: 'Offer Ladder', icon: TrendingUp },
            { id: 'Community Goals', icon: Heart },
            { id: 'Monday Plan', icon: Calendar },
            { id: 'Developer Console', icon: Cpu }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  onAddLog('Growth HUD', `Switched tab target: ${tab.id}`, 'SUCCESS', 'Render module update');
                }}
                className={`py-2 px-3 rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer ${
                  isActive
                    ? 'bg-purple-950/45 text-[#e840f3] border-purple-500/50 shadow-[0_0_12px_rgba(232,64,243,0.12)]'
                    : 'text-slate-450 border-transparent hover:text-slate-200 hover:bg-zinc-900/40'
                }`}
              >
                <Icon size={12} />
                {tab.id}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Disclaimers Compliance Block */}
      <div className="bg-[#0b0c1b]/85 border border-yellow-500/20 rounded-xl p-4 text-[11px] text-slate-400 leading-normal flex gap-3 items-start font-sans">
        <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <span className="text-white font-mono font-bold uppercase tracking-wider block">Zero Paid Ads Growth Standard</span>
          <p className="normal-case font-medium">To protect cashflow integrity, NexoraX prohibits launching auto-billed marketing schedules. Users must prioritize free local SEO listings, vehicle QR campaigns, website audit magnets, and manual social posts first. Approved budgets must align with the Tiered Extraction framework.</p>
        </div>
      </div>

      {/* --- RENDER CHOSEN TAB VIEW --- */}
      <AnimatePresence>
        {activeTab === 'Zero-Budget HUD' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 font-mono text-xs">
              <div className="glass-panel border border-purple-500/20 p-4 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9.5px]">Organic Lead Funnel status</span>
                <p className="text-xl font-bold text-white tracking-tight">{leads.length} Active Leads</p>
                <span className="text-emerald-400 font-semibold">100% Non-Ad Inbound</span>
              </div>
              <div className="glass-panel border border-cyan-500/20 p-4 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9.5px]">SEO Draft Pages</span>
                <p className="text-xl font-bold text-white tracking-tight">{seoDrafts.length} Pages Available</p>
                <span className="text-[#00ffff]">
                  {seoDrafts.filter(d => d.approved).length} Approved & Published
                </span>
              </div>
              <div className="glass-panel border border-purple-500/20 p-4 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9.5px]">Active QR Nodes</span>
                <p className="text-xl font-bold text-white tracking-tight">{qrCampaigns.length} Campaigns Ready</p>
                <span className="text-purple-400">Printed Stickers Checked</span>
              </div>
              <div className="glass-panel border border-yellow-500/20 p-4 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9.5px]">Paid Marketing Invoices</span>
                <p className="text-xl font-bold text-white tracking-tight">£0.00 Outstanding</p>
                <span className="text-yellow-400">100% Zero-Budget Guard</span>
              </div>
            </div>

            {/* Stage Mapping Visualizer */}
            <div className="glass-panel border border-purple-500/30 p-6 rounded-2xl bg-black/40 space-y-4">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 7 — ZERO-BUDGET LAUNCH FUNNEL MATRIX
              </span>
              <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Interactive Customer Pipeline Flow Stages</h3>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5 font-mono text-[10px] pt-2">
                {[
                  { step: '1. Discovery', d: 'Visitor views Free Tool / QR Code/ SEO post' },
                  { step: '2. Utility Use', d: 'Visitor inputs details in free utility card' },
                  { step: '3. Spec Out', d: 'Dynamic compiler generates custom result' },
                  { step: '4. Soft Opt-In', d: 'Visitor sees non-pre-checked marketing prompt' },
                  { step: '5. Setup Pitch', d: 'Visitor selects setup or quote request CTA' },
                  { step: '6. CRM Create', d: 'Automated lead created in WorkSuite logs' },
                  { step: '7. Request Item', d: 'Simulated request added to Client Dashboard' },
                  { step: '8. Node Outreach', d: 'Manual follow up completed in 0.8s standard' },
                  { step: '9. Payment Inflow', d: 'Payment invoice constructed for verification' },
                  { step: '10. Active Launch', d: 'System transitions client into Managed Launch' }
                ].map((ph, idx) => (
                  <div key={idx} className="p-3 bg-zinc-950/80 border border-zinc-900 rounded-xl space-y-1 hover:border-purple-500/30 transition-all">
                    <span className="text-[#00ffff] font-extrabold block text-[10.5px] mb-0.5">{ph.step}</span>
                    <p className="text-slate-400 leading-normal lowercase font-sans">{ph.d}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* --- RECHARTS CORE INTEGRATION GRID --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
              {/* 1. Lead Funnel Stage Distribution */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                  <span className="text-[#00ffff] text-[10.5px] uppercase font-bold tracking-wider">
                    Lead pipeline funnel stages
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">Bar Chart</span>
                </div>
                <div className="h-64 font-sans">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'New', count: leads.filter(l => l.status === 'New').length },
                        { name: 'Tool Use', count: leads.filter(l => l.status === 'Free Tool Used').length },
                        { name: 'Setup', count: leads.filter(l => l.status === 'Setup Requested').length },
                        { name: 'Quote', count: leads.filter(l => l.status === 'Quote Requested').length },
                        { name: 'Contacted', count: leads.filter(l => l.status === 'Contacted').length },
                        { name: 'Consult', count: leads.filter(l => l.status === 'Consultation Booked').length },
                        { name: 'Won', count: leads.filter(l => l.status === 'Won').length },
                      ]}
                      margin={{ top: 10, right: 10, left: -25, bottom: 5 }}
                    >
                      <XAxis dataKey="name" stroke="#52525b" fontSize={10} tickLine={false} />
                      <YAxis stroke="#52525b" fontSize={10} tickLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#09090b', borderColor: '#27272a', borderRadius: '8px' }}
                        labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                        itemStyle={{ color: '#00ffff' }}
                      />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                        {leads.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#8b5cf6' : '#06b6d4'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 2. Lead Magnet Lead Distribution */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                  <span className="text-[#e840f3] text-[10.5px] uppercase font-bold tracking-wider">
                    Leads Influx by Source Utility
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">Pie Chart</span>
                </div>
                <div className="h-64 font-sans flex flex-col justify-between">
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={(() => {
                            const distributionMap = leads.reduce((acc, lead) => {
                              const cleanTool = lead.toolUsed ? lead.toolUsed.replace('Free ', '') : 'Unknown Utility';
                              acc[cleanTool] = (acc[cleanTool] || 0) + 1;
                              return acc;
                            }, {} as Record<string, number>);
                            return Object.entries(distributionMap).map(([name, count]) => ({
                              name,
                              value: count
                            }));
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={65}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {leads.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#06b6d4', '#e840f3', '#10b981', '#fbbf24', '#a78bfa'][index % 5]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#09090b', borderColor: '#27272a', borderRadius: '8px' }}
                          itemStyle={{ color: '#00ffff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Custom Mini Legend */}
                  <div className="grid grid-cols-2 gap-2 text-[9.5px] font-mono border-t border-zinc-900 pt-2 text-slate-450 uppercase">
                    {Array.from(new Set(leads.map(l => l.toolUsed))).map((tool, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 truncate">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: ['#06b6d4', '#e840f3', '#10b981', '#fbbf24', '#a78bfa'][idx % 5] }}></span>
                        <span className="truncate">{String(tool).replace('Free ', '')} ({leads.filter(l => l.toolUsed === tool).length})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 3. QR Campaign Category & Status activity */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                  <span className="text-[#ffd700] text-[10.5px] uppercase font-bold tracking-wider">
                    QR Campaign Distribution Index
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">Statuses</span>
                </div>
                <div className="h-56 font-sans">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={[
                        { name: 'Active', count: qrCampaigns.filter(c => c.status === 'Active').length },
                        { name: 'Draft', count: qrCampaigns.filter(c => c.status === 'Draft').length },
                        { name: 'Paused', count: qrCampaigns.filter(c => c.status === 'Paused').length },
                      ]}
                      margin={{ top: 10, right: 10, left: -20, bottom: 5 }}
                    >
                      <XAxis type="number" stroke="#52525b" fontSize={10} tickLine={false} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" stroke="#52525b" fontSize={10} tickLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#09090b', borderColor: '#27272a', borderRadius: '8px' }}
                        itemStyle={{ color: '#ffd700' }}
                      />
                      <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]}>
                        <Cell fill="#10b981" />
                        <Cell fill="#a78bfa" />
                        <Cell fill="#ef4444" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 4. SEO Pages approved vs pending status */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                  <span className="text-[#10b981] text-[10.5px] uppercase font-bold tracking-wider">
                    SEO Publication Approval Ratio
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">Pages Index</span>
                </div>
                <div className="h-56 font-sans flex flex-col justify-between">
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Approved & Listed', value: seoDrafts.filter(d => d.approved).length },
                            { name: 'Pending Review', value: seoDrafts.filter(d => !d.approved).length },
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={0}
                          outerRadius={55}
                          dataKey="value"
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#09090b', borderColor: '#27272a', borderRadius: '8px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-around items-center text-[10px] font-mono border-t border-zinc-900 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded bg-[#10b981]"></span>
                      <span className="text-slate-300">Approved: {seoDrafts.filter(d => d.approved).length}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded bg-[#ef4444]"></span>
                      <span className="text-slate-300">Draft: {seoDrafts.filter(d => !d.approved).length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stage Mapping Visualizer */}
            <div className="glass-panel border border-purple-500/30 p-6 rounded-2xl bg-black/40 space-y-4">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 7 — ZERO-BUDGET LAUNCH FUNNEL MATRIX
              </span>
              <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Interactive Customer Pipeline Flow Stages</h3>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5 font-mono text-[10px] pt-2">
                {[
                  { step: '1. Discovery', d: 'Visitor views Free Tool / QR Code/ SEO post' },
                  { step: '2. Utility Use', d: 'Visitor inputs details in free utility card' },
                  { step: '3. Spec Out', d: 'Dynamic compiler generates custom result' },
                  { step: '4. Soft Opt-In', d: 'Visitor sees non-pre-checked marketing prompt' },
                  { step: '5. Setup Pitch', d: 'Visitor selects setup or quote request CTA' },
                  { step: '6. CRM Create', d: 'Automated lead created in WorkSuite logs' },
                  { step: '7. Request Item', d: 'Simulated request added to Client Dashboard' },
                  { step: '8. Node Outreach', d: 'Manual follow up completed in 0.8s standard' },
                  { step: '9. Payment Inflow', d: 'Payment invoice constructed for verification' },
                  { step: '10. Active Launch', d: 'System transitions client into Managed Launch' }
                ].map((ph, idx) => (
                  <div key={idx} className="p-3 bg-zinc-950/80 border border-zinc-900 rounded-xl space-y-1 hover:border-purple-500/30 transition-all">
                    <span className="text-[#00ffff] font-extrabold block text-[10.5px] mb-0.5">{ph.step}</span>
                    <p className="text-slate-400 leading-normal lowercase font-sans">{ph.d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Funnel Flow Status Map */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-xl bg-black/30 font-mono text-xs space-y-3">
                <h4 className="text-white font-bold uppercase text-xs border-b border-zinc-900 pb-2 flex justify-between items-center">
                  <span>SaaS Conversion Metrics</span>
                  <Sliders size={13} className="text-cyan-400" />
                </h4>
                <div className="space-y-2 text-[11px]">
                  <div>
                    <div className="flex justify-between text-slate-400 mb-0.5">
                      <span>Free Tool Utilization Rate</span>
                      <span className="text-white">82%</span>
                    </div>
                    <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-purple-500 h-full w-[82%]"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-slate-400 mb-0.5">
                      <span>CTA Setup Clicks Ratio</span>
                      <span className="text-white">41%</span>
                    </div>
                    <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-cyan-400 h-full w-[41%]"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-slate-400 mb-0.5">
                      <span>Soft Marketing Opt-In Compliance</span>
                      <span className="text-white">100%</span>
                    </div>
                    <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-emerald-500 h-full w-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Plan quick progress widget */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-xl bg-black/30 font-mono text-xs space-y-3">
                <h4 className="text-white font-bold uppercase text-xs border-b border-zinc-900 pb-2">
                  Launch Execution Standby
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-slate-400 mb-0.5">
                      <span>Before Launch Milestones</span>
                      <span className="text-[#ffd700] font-bold">{beforeLaunchProgress}% Completed</span>
                    </div>
                    <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-amber-400 h-full" style={{ width: `${beforeLaunchProgress}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-slate-400 mb-0.5">
                      <span>Monday Morning Checklist</span>
                      <span className="text-[#00ffff] font-bold">{mondayMorningProgress}% Completed</span>
                    </div>
                    <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-cyan-400 h-full" style={{ width: `${mondayMorningProgress}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION: TREND SCOUT & ORGANIC LAUNCH QUEUE (SECTION 12) --- */}
        {activeTab === 'Trend Scout & Queue' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            
            {/* Header / Preamble card */}
            <div className="glass-panel border border-[#00ffff]/20 p-5 rounded-2xl bg-black/60 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
                <div className="space-y-1">
                  <span className="text-[#00ffff] font-mono text-[9px] font-bold uppercase tracking-[0.22em] bg-cyan-950/40 px-2.5 py-0.5 rounded border border-cyan-500/20 inline-block mb-1">
                    SECTION 12 — TREND SCOUTING & LAUNCH PIPELINE
                  </span>
                  <h3 className="text-white font-sans font-black text-lg uppercase tracking-tight flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
                    Trend Scout & Organic Launch Queue
                  </h3>
                  <p className="text-slate-400 text-xs max-w-2xl font-sans">
                    In the name of Allah, the Greatest and Most Merciful. Active intelligence gathering on competitor high-ticket product designs, recurring YouTube topic triggers, and localized Fife/Dunfermline traffic bottlenecks.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={fetchTrendScoutData} 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-800 bg-zinc-950 text-[10.5px] font-mono font-bold text-slate-300 hover:text-white hover:border-zinc-700 transition"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    RE-SYNC HUD
                  </button>
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-950/40 border border-emerald-500/20 text-emerald-400 font-mono text-[10px] font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                    MONITOR ACTIVE
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5 font-mono text-xs">
              <div className="glass-panel border border-zinc-850 p-3.5 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">Monitored Channels</span>
                <p className="text-lg font-bold text-white tracking-tight">{scoutStats.totalSources || trendSources.length || 0} Sources</p>
                <span className="text-cyan-400">Watchlists Active</span>
              </div>
              <div className="glass-panel border border-zinc-850 p-3.5 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">Calculated Trends</span>
                <p className="text-lg font-bold text-white tracking-tight">{scoutStats.totalTrends || trendCards.length || 0} Identified</p>
                <span className="text-purple-400">Trend Watch Logged</span>
              </div>
              <div className="glass-panel border border-zinc-850 p-3.5 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">New Status Nodes</span>
                <p className="text-lg font-bold text-[#eab308] tracking-tight">{scoutStats.newTrends || trendCards.filter((t: any) => t.status === 'New').length || 0} Unread</p>
                <span className="text-yellow-600">Pending Review</span>
              </div>
              <div className="glass-panel border border-zinc-850 p-3.5 rounded-xl bg-black/40 space-y-1">
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">Active Launch Leases</span>
                <p className="text-lg font-bold text-white tracking-tight">{launchLeads.length || 0} Monday Leads</p>
                <span className="text-emerald-400">WorkSuite Queue</span>
              </div>
              <div className="glass-panel border border-cyan-950/50 p-3.5 rounded-xl bg-cyan-950/10 border-cyan-500/20 col-span-2 lg:col-span-1 space-y-1">
                <span className="text-cyan-400 uppercase tracking-widest text-[9px] font-black">1% Levy Ledger Status</span>
                <p className="text-lg font-bold text-[#00ffff] tracking-tight">Active (CloudSync)</p>
                <span className="text-slate-400">SPP: 04-06-05 | 31396515</span>
              </div>
            </div>

            {/* Split layout: Trends and tools */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Column: Watchlist & Multi-Source Scanner Input (1 part Wide) */}
              <div className="space-y-6 lg:col-span-1">
                
                {/* Watchlist Admin Panel */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                    <span className="text-white font-mono text-[10.5px] font-black uppercase tracking-wider flex items-center gap-1.5">
                      <Database className="w-3.5 h-3.5 text-purple-400" />
                      Watchlist Sources
                    </span>
                    <button 
                      onClick={() => setShowAddSourceForm(!showAddSourceForm)}
                      className="text-xs text-cyan-400 hover:text-cyan-300 font-mono font-bold flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {showAddSourceForm ? "CLOSE DRAW" : "NEW SOURCE"}
                    </button>
                  </div>

                  {showAddSourceForm && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="p-3.5 rounded-xl bg-zinc-950 border border-zinc-900 space-y-3 font-mono text-[11px]">
                      <div>
                        <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Source Name</label>
                        <input 
                          type="text" 
                          value={newSourceName} 
                          onChange={e => setNewSourceName(e.target.value)}
                          placeholder="e.g. FutureTech Hub" 
                          className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Source Type</label>
                          <select 
                            value={newSourceType} 
                            onChange={e => setNewSourceType(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white focus:outline-none focus:border-cyan-500"
                          >
                            <option value="YouTube channel">YouTube channel</option>
                            <option value="Subreddit blog">Subreddit blog</option>
                            <option value="Competitor store">Competitor store</option>
                            <option value="Tech blog RSS">Tech blog RSS</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Price Index</label>
                          <input 
                            type="text" 
                            placeholder="£0.00 / Free" 
                            disabled 
                            className="w-full bg-zinc-900/40 border border-zinc-800 rounded px-2.5 py-1.5 text-slate-500 cursor-not-allowed"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Channel/Target URL</label>
                        <input 
                          type="text" 
                          value={newSourceUrl} 
                          onChange={e => setNewSourceUrl(e.target.value)}
                          placeholder="https://www.youtube.com/c/example" 
                          className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Topic Filters (comma separated)</label>
                        <input 
                          type="text" 
                          value={newSourceTags} 
                          onChange={e => setNewSourceTags(e.target.value)}
                          placeholder="design, web builders, local-marketing" 
                          className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500"
                        />
                      </div>
                      <button 
                        onClick={handleAddSource}
                        className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-bold py-2 rounded transition font-mono uppercase text-xs"
                      >
                        Register Source Watchlist
                      </button>
                    </motion.div>
                  )}

                  <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                    {trendSources.map((source: any) => (
                      <div key={source.id} className="p-3.5 rounded-xl border border-zinc-900 bg-zinc-950/40 space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="space-y-0.5">
                            <span className="text-white font-sans font-bold text-xs">{source.name}</span>
                            <span className="text-[10px] font-mono text-zinc-500 block truncate max-w-[180px]">{source.url}</span>
                          </div>
                          <span className="px-2 py-0.5 rounded bg-cyan-950/80 border border-cyan-500/20 text-cyan-400 font-mono text-[9px] font-bold uppercase">
                            YouTube reference
                          </span>
                        </div>

                        {/* Source Safety Policy Details (Task 2) */}
                        <div className="bg-black/40 border border-zinc-900 p-2 rounded-lg font-mono text-[9.5px] text-zinc-400 space-y-1">
                          <div className="flex justify-between border-b border-zinc-950 pb-0.5">
                            <span>Status:</span>
                            <span className="text-emerald-400 font-bold">Manual source review</span>
                          </div>
                          <div className="flex justify-between border-b border-zinc-950 pb-0.5">
                            <span>Usage:</span>
                            <span className="text-purple-400 font-semibold">Trend inspiration only</span>
                          </div>
                          <div className="normal-case leading-snug pt-0.5 text-zinc-500">
                            <strong>Output:</strong> Original NexoraX summaries, posts, scripts, and product ideas.
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1 pt-1 border-t border-zinc-900/60">
                          {source.tags && source.tags.map((tag: string, i: number) => (
                            <span key={i} className="text-[9px] text-cyan-450 bg-cyan-950/10 px-1.5 py-0.5 rounded border border-cyan-950">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Operational Rule Card */}
                  <div className="p-3.5 rounded-xl bg-yellow-950/10 border border-yellow-500/20 text-yellow-500/90 font-sans text-xs space-y-1">
                    <span className="font-bold flex items-center gap-1 uppercase font-mono text-[9px] text-[#eab308]">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Anti-Copyright Breach directive
                    </span>
                    <p className="leading-snug text-[11px] font-medium">To protect corporate integrity, do not scrape, republish or reproduce full creator videos or competitor texts verbatim. Translate raw data into original styled templates, localized Fife SEO briefs, and printable offline QR campaigns.</p>
                  </div>
                </div>

                {/* Instant Surveillance Trigger */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4 font-mono text-xs">
                  <span className="text-white font-mono text-[10.5px] font-black uppercase tracking-wider block border-b border-zinc-900 pb-2">
                    Scout active YouTube / Web Trends
                  </span>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Select Active Source Endpoint</label>
                      <select 
                        value={selectedScoutSource}
                        onChange={e => setSelectedScoutSource(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-850 rounded p-2 text-white focus:outline-none focus:border-cyan-500 font-mono text-[11px]"
                      >
                        {trendSources.map((s: any) => (
                          <option key={s.id} value={s.id}>{s.name} ({s.type})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Filter Keyword Trigger</label>
                      <input 
                        type="text" 
                        value={scoutKeywordTerm} 
                        onChange={e => setScoutKeywordTerm(e.target.value)}
                        placeholder="e.g. ai, minimalist, SaaS" 
                        className="w-full bg-zinc-950 border border-zinc-850 rounded p-2 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500 text-[11px]"
                      />
                    </div>

                    <button 
                      onClick={handleScoutTrends}
                      disabled={loadingScout}
                      className="w-full bg-cyan-600 hover:bg-cyan-500 text-black font-black py-2.5 rounded-lg transition uppercase flex items-center justify-center gap-2"
                    >
                      {loadingScout ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          PROBING RAW CHANNELS...
                        </>
                      ) : (
                        <>
                          <Play className="w-3.5 h-3.5" />
                          LAUNCH TREND WATCH
                        </>
                      )}
                    </button>
                  </div>

                  {/* Terminal Log Console */}
                  <div className="space-y-1.5">
                    <span className="text-slate-500 text-[9px] uppercase font-bold tracking-wider">Crawl Log telemetry</span>
                    <div className="h-[100px] rounded-lg border border-zinc-900 bg-zinc-950 p-2 font-mono text-[9px] text-[#00ffff] overflow-y-auto space-y-1">
                      {scoutingLog.length === 0 ? (
                        <span className="text-zinc-600 italic block">Ready. Trigger active scan above...</span>
                      ) : (
                        scoutingLog.map((logStr, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="text-zinc-700">[{new Date().toLocaleTimeString()}]</span>
                            <span className="truncate">{logStr}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Template Market Scan Section */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                  <span className="text-white font-mono text-[10.5px] font-black uppercase tracking-wider block border-b border-zinc-900 pb-2">
                    Template market Scan & Inspiration
                  </span>
                  
                  <div className="space-y-3 font-mono text-xs">
                    <div>
                      <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Competitor Template Title / Target</label>
                      <input 
                        type="text" 
                        value={competitorTemplateName} 
                        onChange={e => setCompetitorTemplateName(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-850 rounded p-2 text-white text-[11px]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">List price Index</label>
                        <input 
                          type="text"
                          value={competitorTemplatePrice}
                          onChange={e => setCompetitorTemplatePrice(e.target.value)}
                          className="w-full bg-zinc-950 border border-zinc-850 rounded p-2 text-white text-[11px]"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Pricing Action</label>
                        <span className="w-full bg-zinc-900 border border-zinc-850/40 rounded p-2 text-center block text-amber-400 text-[11px] font-bold">
                          Zero-Budget Repackage
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="text-slate-400 block mb-1 uppercase text-[9px] font-bold">Competitor Marketplace URL</label>
                      <input 
                        type="text" 
                        value={competitorTemplateUrl} 
                        onChange={e => setCompetitorTemplateUrl(e.target.value)}
                        placeholder="e.g. http://competitordemo.com"
                        className="w-full bg-zinc-950 border border-zinc-850 rounded p-2 text-white placeholder-zinc-700 text-[11px]"
                      />
                    </div>

                    <button 
                      onClick={handleSimulateMarketScan}
                      disabled={loadingMarketScan}
                      className="w-full bg-cyan-950 hover:bg-cyan-900 text-cyan-400 border border-cyan-500/20 font-bold py-2 rounded transition uppercase text-xs flex items-center justify-center gap-1.5"
                    >
                      {loadingMarketScan ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          PARSING COMPETITOR METADATA...
                        </>
                      ) : "Trigger Inspiration Analysis"}
                    </button>
                  </div>

                  {marketScanLog.length > 0 && (
                    <div className="space-y-3 font-mono">
                      <div className="h-[90px] rounded bg-black border border-zinc-900 p-2 text-[9.5px] text-[#00ffff] overflow-y-auto space-y-1">
                        {marketScanLog.map((l, i) => <div key={i} className="truncate">&gt; {l}</div>)}
                      </div>

                      {suggestedProductsList.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-zinc-900">
                          <span className="text-amber-400 text-[9px] uppercase font-bold tracking-wider">Suggested Zero-overhead listings</span>
                          {suggestedProductsList.map((prod, idx) => (
                            <div key={idx} className="p-3 rounded-lg bg-zinc-950 border border-zinc-900 space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-white font-sans font-bold text-[11.5px]">{prod.name}</span>
                                <span className="text-[#eab308] font-bold font-mono text-xs">{prod.price}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 font-sans leading-relaxed">{prod.description}</p>
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {prod.features.map((f: string, fi: number) => (
                                  <span key={fi} className="text-[8.5px] text-cyan-400 bg-cyan-950/20 rounded px-1">{f}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-3.5 rounded-xl bg-purple-950/10 border border-purple-500/20 font-sans text-[11px] text-slate-400 leading-snug space-y-1.5">
                    <span className="font-bold text-purple-400 font-mono text-[9px] uppercase block">Market Inspiration Reference</span>
                    <p className="font-medium text-slate-300">
                      When analyzing marketplace products, we enforce strict compliance: **Do not copy seller designs, product names, images, or exact layouts**. We never resell third-party files. External materials are utilized purely as design orientation and structural inspiration to produce our original NexoraX assets.
                    </p>
                  </div>
                </div>

              </div>

              {/* Right Column: Trend Cards Grid & Drawers (2 parts Wide) */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Active Trend Grid Header */}
                <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                  <span className="text-white font-mono text-[10.5px] font-black uppercase tracking-wider flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                    Trend Card Registry
                  </span>
                  <span className="text-xs text-slate-500 font-mono">Showing {trendCards.length} Dynamic Items</span>
                </div>

                {trendCards.map((trend: any) => (
                  <div key={trend.id} className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                    
                    {/* Header line */}
                    <div className="flex flex-wrap justify-between items-start gap-2">
                      <div className="space-y-0.5">
                        <span className="text-[10px] text-cyan-400 font-mono tracking-wider font-bold">#{trend.sourceName}</span>
                        <h4 className="text-white font-sans font-black text-sm uppercase leading-tight">{trend.title}</h4>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2.5 py-0.5 rounded text-[9.5px] font-mono font-black uppercase border ${
                          trend.status === 'Approved' ? 'bg-emerald-950/40 border-emerald-500/20 text-emerald-400' :
                          trend.status === 'Reviewed' ? 'bg-blue-950/40 border-blue-500/20 text-blue-400' :
                          trend.status === 'New' ? 'bg-yellow-950/40 border-yellow-500/20 text-yellow-400' :
                          'bg-zinc-900 border-zinc-800 text-slate-400'
                        }`}>
                          {trend.status}
                        </span>
                        
                        {trend.status === 'New' && (
                          <div className="flex gap-1">
                            <button 
                              onClick={() => handleApproveTrend(trend.id, 'Reviewed')}
                              className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-xs text-slate-400 font-mono"
                            >
                              Flag Reviewed
                            </button>
                            <button 
                              onClick={() => handleApproveTrend(trend.id, 'Approved')}
                              className="px-2 py-0.5 rounded bg-cyan-950/80 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-900 text-xs font-mono font-bold"
                            >
                              Approve
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Summary and Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-2 border-t border-zinc-900/60 font-sans text-xs">
                      <div className="space-y-1">
                        <span className="text-zinc-500 uppercase font-mono text-[9px] tracking-wider block">Why This Matters</span>
                        <p className="text-slate-300 leading-relaxed text-[11.5px]">{trend.whyMatters}</p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-zinc-500 uppercase font-mono text-[9px] tracking-wider block">Local B2B Prospecting Idea</span>
                        <p className="text-slate-300 leading-relaxed text-[11.5px]">{trend.businessOpportunity}</p>
                      </div>
                    </div>

                    {/* Nexorax customized item */}
                    <div className="p-3.5 rounded-xl bg-purple-950/20 border border-purple-500/10 font-mono text-[11px] text-slate-300 space-y-1">
                      <span className="text-purple-400 font-bold uppercase text-[9px] block">NexoraX Unified Conversion Connector</span>
                      <p className="leading-snug text-slate-400">{trend.nexoxConnection}</p>
                    </div>

                    {/* Grid of generators */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-zinc-900/60 text-[10px] font-mono">
                      <button 
                        onClick={() => handleGenerateSocial(trend.id)}
                        className="py-1.5 px-2 rounded bg-zinc-950 border border-zinc-900 text-slate-400 hover:text-white hover:border-zinc-800 flex justify-center items-center gap-1"
                      >
                        <Share2 className="w-3 h-3 text-cyan-400" />
                        SOCIAL POSTS
                      </button>
                      <button 
                        onClick={() => handleGenerateVideoScript(trend.id)}
                        className="py-1.5 px-2 rounded bg-zinc-950 border border-zinc-900 text-slate-400 hover:text-white hover:border-zinc-800 flex justify-center items-center gap-1"
                      >
                        <PlaySquare className="w-3 h-3 text-purple-400" />
                        VIDEO SCRIPT
                      </button>
                      <button 
                        onClick={() => handleGenerateSeoDraft(trend.id)}
                        className="py-1.5 px-2 rounded bg-zinc-950 border border-zinc-900 text-slate-400 hover:text-white hover:border-zinc-800 flex justify-center items-center gap-1"
                      >
                        <Globe className="w-3 h-3 text-amber-500" />
                        LOCAL SEO
                      </button>
                      <button 
                        onClick={() => handleConvertToNews(trend.id)}
                        className="py-1.5 px-2 rounded bg-cyan-950/30 border border-cyan-500/10 text-cyan-400 hover:bg-cyan-950/50 flex justify-center items-center gap-1 font-bold"
                      >
                        <Film className="w-3 h-3" />
                        STAGE TECH UPDATE
                      </button>
                    </div>

                  </div>
                ))}

                {/* SOCIAL DRAWER PREVIEW */}
                {scoutSocialDraft && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel border-2 border-cyan-500/40 p-5 rounded-2xl bg-zinc-950 space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                      <div className="space-y-0.5">
                        <span className="text-cyan-400 font-mono text-[9px] uppercase tracking-widest block font-black">Organic Content Factory</span>
                        <h4 className="text-white font-sans font-black text-sm uppercase">Generated Multi-Network Promotion Core</h4>
                      </div>
                      <button onClick={() => setScoutSocialDraft(null)} className="text-zinc-500 hover:text-white font-mono text-xs">CLOSE DOCK</button>
                    </div>

                    <div className="flex gap-1 bg-zinc-900/60 p-1 rounded-lg">
                      {["instagram", "tiktok", "facebook", "linkedin", "shorts"].map((p) => (
                        <button
                          key={p}
                          onClick={() => setActiveSocialPlatform(p)}
                          className={`flex-1 py-1 px-2.5 rounded font-mono text-[10px] uppercase font-bold text-center transition ${
                            activeSocialPlatform === p ? 'bg-cyan-600 text-black' : 'text-slate-400 hover:bg-zinc-850'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>

                    <div className="p-4 rounded-xl bgcolor bg-zinc-900 border border-zinc-850 space-y-3 font-mono text-xs">
                      <div>
                        {activeSocialPlatform === "instagram" && (
                          <div className="space-y-2">
                            <span className="text-cyan-400 text-[10px] uppercase block">Instagram Minimalist Luxury Post draft</span>
                            <p className="text-slate-200 normal-case leading-relaxed font-sans text-xs bg-zinc-950 p-2.5 rounded border border-zinc-900 whitespace-pre-line">{scoutSocialDraft.instagram || "No template generated"}</p>
                          </div>
                        )}
                        {activeSocialPlatform === "tiktok" && (
                          <div className="space-y-2">
                            <span className="text-purple-400 text-[10px] uppercase block">TikTok Trend Voiceover and Overlay Script</span>
                            <p className="text-slate-200 normal-case leading-relaxed font-sans text-xs bg-zinc-950 p-2.5 rounded border border-zinc-900 whitespace-pre-line">{scoutSocialDraft.tiktok || "No template generated"}</p>
                          </div>
                        )}
                        {activeSocialPlatform === "facebook" && (
                          <div className="space-y-2">
                            <span className="text-cyan-400 text-[10px] uppercase block">Facebook Local Group Discussion Post</span>
                            <p className="text-slate-200 normal-case leading-relaxed font-sans text-xs bg-zinc-950 p-2.5 rounded border border-zinc-900 whitespace-pre-line">{scoutSocialDraft.facebook || "No template generated"}</p>
                          </div>
                        )}
                        {activeSocialPlatform === "linkedin" && (
                          <div className="space-y-2">
                            <span className="text-amber-500 text-[10px] uppercase block">LinkedIn Authority Builder & Strategy Insights</span>
                            <p className="text-slate-200 normal-case leading-relaxed font-sans text-xs bg-zinc-950 p-2.5 rounded border border-zinc-900 whitespace-pre-line">{scoutSocialDraft.linkedin || "No template generated"}</p>
                          </div>
                        )}
                        {activeSocialPlatform === "shorts" && (
                          <div className="space-y-2">
                            <span className="text-red-400 text-[10px] uppercase block">YouTube Shorts / X micro-post</span>
                            <p className="text-slate-200 normal-case leading-relaxed font-sans text-xs bg-zinc-950 p-2.5 rounded border border-zinc-900 whitespace-pre-line">{scoutSocialDraft.youtube || "No template generated"}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-800/80 text-[10px]">
                        <div>
                          <span className="text-zinc-500 uppercase block text-[9px]">Destination QR Tag</span>
                          <span className="text-[#00ffff] font-bold block truncate">https://nexorax.co.uk?utm_medium=organic&utm_source=social</span>
                        </div>
                        <div>
                          <span className="text-zinc-500 uppercase block text-[9px]">Mandatory Branding Tag</span>
                          <span className="text-purple-400 font-bold block">#NexoraX #SC887095</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* VIDEO SCRIPT PREVIEW */}
                {scoutVideoScript && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel border-2 border-purple-500/40 p-5 rounded-2xl bg-zinc-950 space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                      <div className="space-y-0.5">
                        <span className="text-purple-400 font-mono text-[9px] uppercase tracking-widest block font-black">Video Automation Starter</span>
                        <h4 className="text-white font-sans font-black text-sm uppercase">Direct Response Video Script draft</h4>
                      </div>
                      <button onClick={() => setScoutVideoScript(null)} className="text-zinc-500 hover:text-white font-mono text-xs">CLOSE DOCK</button>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono">
                      <div className="p-2 rounded bg-zinc-900">
                        <span className="text-slate-500 uppercase block text-[8px]">Visual Layout</span>
                        <span className="text-white font-bold block">Hyper-minimal</span>
                      </div>
                      <div className="p-2 rounded bg-zinc-900">
                        <span className="text-slate-500 uppercase block text-[8px]">Hook Duration</span>
                        <span className="text-cyan-400 font-bold block">3-5 Seconds</span>
                      </div>
                      <div className="p-2 rounded bg-zinc-900">
                        <span className="text-slate-500 uppercase block text-[8px]">Staged Placement</span>
                        <span className="text-purple-400 font-bold block">Guides Queue</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-850 space-y-4 font-sans text-xs">
                      <div>
                        <span className="text-[#eab308] font-bold font-mono text-[9px] uppercase tracking-wider block mb-1">Engaging Video Hook</span>
                        <p className="text-white text-xs italic bg-zinc-950 p-2.5 rounded border border-zinc-800 font-medium">"{scoutVideoScript.hook}"</p>
                      </div>

                      <div className="space-y-2">
                        <span className="text-cyan-400 font-bold font-mono text-[9px] uppercase tracking-wider block">Full Scene Board & Voiceover Timeline</span>
                        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                          {scoutVideoScript.scenes && scoutVideoScript.scenes.map((scene: any, index: number) => (
                            <div key={index} className="p-3 bg-zinc-950/60 rounded border border-zinc-950 space-y-1.5 font-mono text-[10.5px]">
                              <div className="flex justify-between text-[9px] font-bold pb-1 border-b border-zinc-900/60">
                                <span className="text-purple-400">SCENE #{index + 1}: {scene.visual}</span>
                                <span className="text-zinc-500">EST: {scene.timing}</span>
                              </div>
                              <p className="text-slate-300 font-sans text-xs leading-relaxed">{scene.voiceover}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="p-3 rounded bg-purple-950/30 border border-purple-500/10 font-mono text-[10px] text-purple-300">
                        <span className="font-bold text-purple-400 uppercase text-[9px] block mb-0.5">Automated CTA Overlay Rule</span>
                        At 12 seconds remaining, automatically fade-in a static mock phone rendering a non-pre-checked QR campaign code redirecting back to nexorax.co.uk.
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* SEO DRAFT PREVIEW */}
                {scoutSeoDraft && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel border-2 border-amber-500/40 p-5 rounded-2xl bg-zinc-950 space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-zinc-905">
                      <div className="space-y-0.5">
                        <span className="text-amber-500 font-mono text-[9px] uppercase tracking-widest block font-black">SEO Starter Engine</span>
                        <h4 className="text-white font-sans font-black text-sm uppercase">Staged Local Authority Blog draft</h4>
                      </div>
                      <button onClick={() => setScoutSeoDraft(null)} className="text-zinc-500 hover:text-white font-mono text-xs">CLOSE DOCK</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 font-mono text-xs">
                      <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-850 space-y-1">
                        <span className="text-slate-500 text-[9px] uppercase block">Assigned SEO Slug</span>
                        <span className="text-cyan-400 font-bold block">{scoutSeoDraft.slug || `/fife-${scoutSeoDraft.keyword}`}</span>
                      </div>
                      <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-850 space-y-1">
                        <span className="text-slate-500 text-[9px] uppercase block">Page Meta Title</span>
                        <span className="text-purple-400 font-bold block">NexoraX: {scoutSeoDraft.title}</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-850 space-y-4 font-sans text-xs">
                      <div>
                        <span className="text-amber-400 font-bold font-mono text-[9px] uppercase tracking-wider block mb-1">Target Keyword Focus</span>
                        <span className="px-2 py-0.5 rounded bg-zinc-950 border border-zinc-800 text-white font-mono text-xs font-bold">{scoutSeoDraft.keyword || "fife web templates"}</span>
                      </div>

                      <div className="space-y-1 bg-zinc-950 p-3 rounded-xl border border-zinc-900">
                        <span className="text-zinc-500 font-mono text-[9.5px] uppercase block">Meta Description</span>
                        <p className="text-slate-300 leading-relaxed font-sans text-xs">{scoutSeoDraft.metaDescription}</p>
                      </div>

                      <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                        <span className="text-cyan-400 font-bold font-mono text-[9px] uppercase tracking-wider block">Local Fife Business FAQ schema tags</span>
                        {scoutSeoDraft.faqs && scoutSeoDraft.faqs.map((faq: any, i: number) => (
                          <div key={i} className="p-2.5 rounded bg-zinc-950/40 border border-zinc-950 font-sans text-[11.5px] space-y-1">
                            <span className="font-bold text-white block">Q: {faq.q}</span>
                            <p className="text-slate-400 block">A: {faq.a}</p>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => {
                          onAddLog('SEO Pages', `Staged page ${scoutSeoDraft.slug} to master sitemap list`, 'SUCCESS', 'XML updated');
                          setScoutSeoDraft(null);
                        }}
                        className="w-full bg-amber-600 hover:bg-amber-500 text-black font-black py-2 rounded uppercase font-mono text-xs"
                      >
                        Approve and stage inside Sitemap compiler
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Monday Waiting List Queue & Public Submission Hook */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5">
                  <div className="border-b border-zinc-900 pb-3">
                    <span className="text-[#00ffff] font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-cyan-950/40 px-2.5 py-0.5 rounded border border-cyan-500/20 inline-block mb-1">
                      SECTION 9 — THE MONDAY WAITING LIST FUNNEL
                    </span>
                    <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight flex items-center gap-1.5 mt-1">
                      <Users className="w-4 h-4 text-purple-400" />
                      Active Waitlist queue
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-2">
                    
                    {/* Left: Queue items list */}
                    <div className="space-y-3">
                      <span className="text-slate-500 uppercase font-mono text-[9.5px] tracking-wider block">Registered launch participants ({launchLeads.length})</span>
                      <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
                        {launchLeads.length === 0 ? (
                          <div className="p-4 text-center rounded-xl bg-zinc-950 border border-zinc-900 text-zinc-500 italic font-mono text-[11px]">
                            No registered launch candidates inside the database. Submit the public form to populate instantly.
                          </div>
                        ) : (
                          launchLeads.map((lead: any) => (
                            <div key={lead.id} className="p-3 rounded-lg bg-zinc-950/80 border border-zinc-900 font-mono text-[10.5px] space-y-1.5">
                              <div className="flex justify-between items-start">
                                <span className="text-white font-sans font-bold">{lead.name} {lead.businessName ? `(${lead.businessName})` : ''}</span>
                                <span className="px-1.5 py-0.5 rounded bg-cyan-950 text-cyan-400 border border-cyan-500/10 uppercase text-[8.5px] font-black">
                                  {lead.budgetRange}
                                </span>
                              </div>
                              <div className="space-y-0.5 font-mono text-[9.5px] text-zinc-500">
                                <p>Email: <span className="text-slate-300 font-semibold">{lead.email}</span></p>
                                <p>Website: <span className="text-slate-300 font-semibold truncate block max-w-[170px]">{lead.website || 'None'}</span></p>
                                <p>Service: <span className="text-[#eab308] font-bold">{lead.interestedService}</span></p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Right: Beautiful Public Front-end Submission Form */}
                    <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-900 space-y-3">
                      <div className="space-y-0.5">
                        <span className="text-[#00ffff] font-mono text-[9px] font-bold uppercase tracking-widest block">Client Portal Mockup</span>
                        <h4 className="text-white font-sans font-black text-xs uppercase">Join the Monday Launch List</h4>
                        <p className="text-slate-500 text-[10.5px] font-sans">Secure a zero-cost consulting and digital placement assessment slot.</p>
                      </div>

                      {publicLeadSuccessMsg ? (
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-3.5 rounded-lg bg-emerald-950/40 border border-emerald-500/20 text-emerald-400 font-sans space-y-3 text-xs text-center">
                          <span className="text-lg block">✓</span>
                          <p className="font-bold leading-normal">{publicLeadSuccessMsg}</p>
                          <div className="flex justify-center gap-1.5 pt-1.5 border-t border-emerald-900/40 text-[9px] font-mono">
                            <button onClick={() => alert("Free brief structure drafted & dispatched to WorkSuite desk internally.")} className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded hover:border-zinc-700 text-slate-300 font-semibold">
                              Brief structures
                            </button>
                            <button onClick={() => alert("Free Campaign QR code URL spawned: https://nexorax.co.uk?utm_campaign=monday-launch")} className="px-2 py-1 bg-cyan-900 text-black rounded font-black">
                              Printable QR
                            </button>
                          </div>
                          <button 
                            onClick={() => setPublicLeadSuccessMsg("")} 
                            className="text-slate-400 hover:text-white uppercase font-mono text-[8px] underline block mx-auto pt-1"
                          >
                            Submit another lead
                          </button>
                        </motion.div>
                      ) : (
                        <form onSubmit={handleLaunchListSubmit} className="space-y-2 font-mono text-[10.5px]">
                          <div>
                            <input 
                              type="text" 
                              required
                              value={publicLeadName}
                              onChange={e => setPublicLeadName(e.target.value)}
                              placeholder="Your full name" 
                              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input 
                                type="text"
                                value={publicLeadBusinessName}
                                onChange={e => setPublicLeadBusinessName(e.target.value)}
                                placeholder="Business name" 
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none"
                              />
                            </div>
                            <div>
                              <input 
                                type="email" 
                                required
                                value={publicLeadEmail}
                                onChange={e => setPublicLeadEmail(e.target.value)}
                                placeholder="Business email" 
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input 
                                type="text" 
                                value={publicLeadPhone}
                                onChange={e => setPublicLeadPhone(e.target.value)}
                                placeholder="Contact number" 
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none"
                              />
                            </div>
                            <div>
                              <input 
                                type="text" 
                                value={publicLeadWebsite}
                                onChange={e => setPublicLeadWebsite(e.target.value)}
                                placeholder="Current website URL" 
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-white placeholder-zinc-700 focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <select 
                                value={publicLeadService} 
                                onChange={e => setPublicLeadService(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-slate-300 focus:outline-none"
                              >
                                <option value="Ready-made website">Ready-made website</option>
                                <option value="Premium Reels Bundle">Premium Reels Bundle</option>
                                <option value="Local SEO Audit Pack">Local SEO Audit Pack</option>
                                <option value="HMRC & Ledger Spec">HMRC & Ledger Spec</option>
                              </select>
                            </div>
                            <div>
                              <select 
                                value={publicLeadBudget} 
                                onChange={e => setPublicLeadBudget(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-slate-300 focus:outline-none"
                              >
                                <option value="£1,000 - £3,000">£1,000 - £3,000</option>
                                <option value="£3,000 - £5,000">£3,000 - £5,000</option>
                                <option value="£5,000 - £10,000">£5,000 - £10,000</option>
                                <option value="£10,000+">£10,000+</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <textarea 
                              value={publicLeadMessage}
                              onChange={e => setPublicLeadMessage(e.target.value)}
                              rows={2}
                              placeholder="Any specific instructions for design/copy..." 
                              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.2 text-white placeholder-zinc-700 focus:outline-none focus:border-cyan-500 font-sans"
                            ></textarea>
                          </div>
                          
                          {/* Optional marketing consent, default non-pre-checked to respect the strict mandate */}
                          <div className="space-y-1 bg-zinc-900/40 p-2 rounded border border-zinc-900 font-sans text-[10px] text-slate-500">
                            <label className="flex items-start gap-1.5 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={publicConsentResponse} 
                                disabled
                                className="mt-0.5 accent-cyan-500" 
                              />
                              <span className="leading-tight">Consent: Authorize NexoraX to list the enquiry in the Monday pipeline.</span>
                            </label>
                            <label className="flex items-start gap-1.5 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={publicConsentMarketing} 
                                onChange={e => setPublicConsentMarketing(e.target.checked)}
                                className="mt-0.5 accent-cyan-500" 
                              />
                              <span className="leading-tight text-slate-400">Optional: I opt-in to receive periodic B2B tech updates from info@nexoraxdigital.co.uk.</span>
                            </label>
                          </div>

                          <button 
                            type="submit"
                            disabled={submittingPublicLead}
                            className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-bold py-2 rounded-lg transition uppercase tracking-wider text-xs"
                          >
                            {submittingPublicLead ? "SUBMITTING TO QUEUE..." : "DEPLOY CAMPAIGN BRIEF"}
                          </button>
                        </form>
                      )}

                    </div>

                  </div>

                </div>

                {/* QR CAMPAIGN STARTER CO-CONNECTOR */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4 font-mono text-xs">
                  <div className="border-b border-zinc-900 pb-2">
                    <span className="text-white font-mono text-[10.5px] font-black uppercase tracking-wider flex items-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5 text-cyan-400" />
                      QR Campaigner Co-Connector
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                    <div>
                      <label className="text-slate-500 block mb-0.5 uppercase text-[8.5px]">UTM Source</label>
                      <input 
                        type="text" 
                        value={qrUtmSource} 
                        onChange={e => setQrUtmSource(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-900 rounded p-1.5 text-white" 
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-0.5 uppercase text-[8.5px]">UTM Medium</label>
                      <input 
                        type="text" 
                        value={qrUtmMedium} 
                        onChange={e => setQrUtmMedium(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-900 rounded p-1.5 text-white" 
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-0.5 uppercase text-[8.5px]">UTM Campaign</label>
                      <input 
                        type="text" 
                        value={qrUtmCampaign} 
                        onChange={e => setQrUtmCampaign(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-900 rounded p-1.5 text-white" 
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={handleAddLocalQrCampaign}
                      className="flex-1 bg-cyan-950/80 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-900 py-2 rounded font-bold uppercase transition"
                    >
                      Spawn Campaign Node URL
                    </button>
                    <button 
                      onClick={() => alert("Static printable capture cards dispatched to active print queue buffer.")}
                      className="px-4 py-2 border border-zinc-850 hover:border-zinc-700 bg-zinc-950 text-slate-300 rounded font-bold uppercase"
                    >
                      PRINT CARD
                    </button>
                  </div>

                  <div className="space-y-2 mt-3 pt-2 border-t border-zinc-900/60 max-h-[140px] overflow-y-auto pr-1">
                    {qrCampaignsLocal.map((qc: any) => (
                      <div key={qc.id} className="p-2.5 rounded bg-zinc-950 border border-zinc-900 font-mono text-[10px] space-y-1">
                        <div className="flex justify-between items-center font-bold">
                          <span className="text-white truncate max-w-[200px]">{qc.name}</span>
                          <span className="text-cyan-400">{qc.leadCount} Scans</span>
                        </div>
                        <p className="text-slate-500 truncate">{qc.url}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
              
            </div>

          </motion.div>
        )}

        {/* --- TABS SECTION 1: PUBLIC FREE LEAD MAGNET TOOLS --- */}
        {activeTab === 'Free Lead Tools' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-mono text-xs">
              {/* Left sidebar: Tools list selective buttons */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3 shrink-0">
                <span className="text-[#00ffff] text-[10px] uppercase font-bold tracking-wider block border-b border-zinc-900 pb-2">
                  Select Free Utility Magnet
                </span>
                <div className="space-y-1.5 max-h-[380px] overflow-y-auto pr-1">
                  {freeToolsData.map((t) => {
                    const isSelected = selectedToolId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          setSelectedToolId(t.id);
                          setToolGeneratedResult(null);
                        }}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all cursor-pointer block ${
                          isSelected
                            ? 'bg-purple-950/40 text-[#e840f3] border-purple-500/40 font-black'
                            : 'text-slate-400 bg-transparent border-transparent hover:bg-zinc-950/60 hover:text-white'
                        }`}
                      >
                        <span className="block font-bold truncate text-[11px]">{t.id}. {t.name}</span>
                        <span className="text-[9px] text-slate-500 line-clamp-1 lowercase normal-case">{t.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right area: Tool input and simulated compiler */}
              <div className="lg:col-span-2 glass-panel border border-purple-500/25 p-5 rounded-2xl bg-black/40 space-y-4">
                {(() => {
                  const currentTool = freeToolsData.find(tool => tool.id === selectedToolId);
                  if (!currentTool) return null;
                  return (
                    <>
                      <div className="space-y-1">
                        <span className="text-[#ffd700] text-[9.5px] uppercase font-bold tracking-wider bg-yellow-950/30 px-2 py-0.5 rounded border border-amber-500/15">
                          ACTIVE MAGNET: NO-FUND ENFORCED
                        </span>
                        <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">{currentTool.name}</h3>
                        <p className="text-slate-400 text-[10px] leading-normal normal-case">{currentTool.desc}</p>
                      </div>

                      {/* Tool inputs wrapper */}
                      <div className="p-4 bg-zinc-950/60 border border-zinc-900 rounded-xl space-y-3">
                        <span className="text-slate-500 uppercase text-[9px] block border-b border-zinc-900 pb-1">Input Parameters (Simulation Core)</span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                          {currentTool.inputs.includes('websiteUrl') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Target Website Address:</label>
                              <input
                                type="text"
                                value={toolInputs.websiteUrl}
                                onChange={(e) => setToolInputs({ ...toolInputs, websiteUrl: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                                placeholder="https://mybusiness.com"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('businessName') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Trading Business Name:</label>
                              <input
                                type="text"
                                value={toolInputs.businessName}
                                onChange={(e) => setToolInputs({ ...toolInputs, businessName: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('industry') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Trade Niche / Industry Category:</label>
                              <input
                                type="text"
                                value={toolInputs.industry}
                                onChange={(e) => setToolInputs({ ...toolInputs, industry: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('presentationTopic') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Pitch Presentation Topic:</label>
                              <input
                                type="text"
                                value={toolInputs.presentationTopic}
                                onChange={(e) => setToolInputs({ ...toolInputs, presentationTopic: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('skillNiche') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Skill Niche or Target Category:</label>
                              <input
                                type="text"
                                value={toolInputs.skillNiche}
                                onChange={(e) => setToolInputs({ ...toolInputs, skillNiche: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('painPoint') && (
                            <div className="space-y-1 sm:col-span-2">
                              <label className="text-slate-400 text-[10px] block font-bold">Primary Administrative Pain Point:</label>
                              <input
                                type="text"
                                value={toolInputs.painPoint}
                                onChange={(e) => setToolInputs({ ...toolInputs, painPoint: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}

                          {currentTool.inputs.includes('featuresCount') && (
                            <div className="space-y-1">
                              <label className="text-slate-400 text-[10px] block font-bold">Expected Layout Deliverables:</label>
                              <input
                                type="text"
                                value={toolInputs.featuresCount}
                                onChange={(e) => setToolInputs({ ...toolInputs, featuresCount: e.target.value })}
                                className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-cyan-400"
                              />
                            </div>
                          )}
                        </div>

                        {/* Contact details capture required for real conversion simulation */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2 border-t border-zinc-900/60 mt-2">
                          <div className="space-y-1">
                            <label className="text-slate-400 text-[10.5px] font-bold block">Contact Person Name:</label>
                            <input
                              type="text"
                              value={toolInputs.contactName}
                              onChange={(e) => setToolInputs({ ...toolInputs, contactName: e.target.value })}
                              className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-[11px] text-zinc-300 outline-none"
                              placeholder="Required for lead capture"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-slate-400 text-[10.5px] font-bold block">Business Verification Email:</label>
                            <input
                              type="email"
                              value={toolInputs.contactEmail}
                              onChange={(e) => setToolInputs({ ...toolInputs, contactEmail: e.target.value })}
                              className="w-full bg-black border border-zinc-800 rounded px-2.5 py-1 text-[11px] text-zinc-300 outline-none"
                              placeholder="siddique@fiferetailers.co.uk"
                            />
                          </div>
                        </div>

                        {/* Optional Marketing Consent checkbox - MUST NEVER BE PRE-TICKED */}
                        <div className="flex items-start gap-2.5 pt-3 select-none">
                          <input
                            type="checkbox"
                            id="marketing-consent-chk"
                            checked={toolMarketingConsent}
                            onChange={(e) => setToolMarketingConsent(e.target.checked)}
                            className="w-4 h-4 cursor-pointer align-middle mt-0.5 text-purple-650 rounded bg-black outline-none border-zinc-800"
                          />
                          <label htmlFor="marketing-consent-chk" className="text-[10px] text-slate-400 leading-normal font-sans cursor-pointer">
                            <strong>Marketing Opt-In Checkbox (Optional):</strong> I consent to receive professional digital products specification briefings, zero-budget organic checklists, and hosting packages info from NexoraX.
                          </label>
                        </div>

                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={handleGenerateToolResult}
                            disabled={isToolGenerating}
                            className="w-full py-2 bg-gradient-to-r from-purple-900 to-cyan-950 hover:from-purple-850 hover:to-cyan-900 text-white font-bold border border-purple-500/30 uppercase text-xs rounded-xl cursor-pointer"
                          >
                            {isToolGenerating ? 'Processing compiler vectors...' : 'Run Specification Engine'}
                          </button>
                        </div>
                      </div>

                      {/* Generated output visualization panel */}
                      <AnimatePresence>
                        {toolGeneratedResult && (
                          <motion.div
                            initial={{ scale: 0.98, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="p-5 bg-[#03010b] border border-cyan-500/35 rounded-xl space-y-4"
                          >
                            <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                              <span className="text-cyan-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                                <Sparkles size={11} /> Simulated Engine Result Output
                              </span>
                              <span className="text-slate-500 text-[9px]">Verified No-Fund Basis</span>
                            </div>

                            <div className="space-y-3 font-mono text-xs text-slate-300">
                              <h4 className="text-white font-extrabold text-[12px] uppercase">{toolGeneratedResult.title}</h4>
                              
                              {toolGeneratedResult.score && (
                                <div className="p-4 border border-red-500/20 bg-red-950/10 rounded-xl space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-red-400 uppercase font-black tracking-wider text-[10px]">Site Health Index:</span>
                                    <span className="text-white font-extrabold text-xs">{toolGeneratedResult.score}</span>
                                  </div>
                                  <div className="w-full bg-zinc-900 h-2 rounded-full overflow-hidden relative">
                                    <div className="bg-gradient-to-r from-red-500 to-amber-500 h-full" style={{ width: '84%' }}></div>
                                  </div>
                                  <p className="text-[9px] text-slate-550 leading-relaxed normal-case font-sans">
                                    A poor page speed layout index (target is under 0.8s on NexoraX) combined with mobile touch elements sized under 44px limits your organic reach. Server compression and bare-metal caching are critical.
                                  </p>
                                </div>
                              )}

                              {toolGeneratedResult.codePreview && (
                                <div className="space-y-3 flex flex-col items-center justify-center p-4 bg-zinc-950/80 border border-zinc-900 rounded-lg">
                                  <img 
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&color=a78bfa&bgcolor=000000&data=${encodeURIComponent(toolGeneratedResult.data || toolGeneratedResult.vCardString || 'https://nexorax.co.uk')}`} 
                                    alt="QR Code" 
                                    className="w-40 h-40 border border-purple-500/30 p-2.5 bg-black rounded"
                                    referrerPolicy="no-referrer"
                                  />
                                  <span className="text-[10px] text-purple-400 uppercase font-bold tracking-wider">Scannable B2B Integration Node</span>
                                  <pre className="p-2 bg-black border border-zinc-900 rounded text-center text-emerald-400 text-[9.5px] leading-relaxed w-full overflow-x-auto select-all">
                                    {toolGeneratedResult.codePreview}
                                  </pre>
                                </div>
                              )}

                              {toolGeneratedResult.details && (
                                <ul className="space-y-1 list-disc list-inside">
                                  {toolGeneratedResult.details.map((det: string, idx: number) => (
                                    <li key={idx} className="text-slate-400 text-[10.5px] leading-normal">{det}</li>
                                  ))}
                                </ul>
                              )}

                              {toolGeneratedResult.hierarchy && (
                                <div className="space-y-1">
                                  <span className="text-slate-500 text-[9.5px] uppercase font-bold block">Page Slices Map:</span>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {toolGeneratedResult.hierarchy.map((hi: string, idx: number) => (
                                      <span key={idx} className="bg-zinc-900 text-purple-300 px-2.5 py-0.5 rounded text-[10px]">
                                        {hi}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {toolGeneratedResult.captions && (
                                <div className="space-y-1.5">
                                  {toolGeneratedResult.captions.map((cap: string, idx: number) => (
                                    <p key={idx} className="p-2 bg-zinc-950 border border-zinc-900 rounded text-[10px] text-slate-300 leading-normal italic">
                                      "{cap}"
                                    </p>
                                  ))}
                                </div>
                              )}

                              {toolGeneratedResult.slides && (
                                <ol className="space-y-1">
                                  {toolGeneratedResult.slides.map((s: string, idx: number) => (
                                    <li key={idx} className="text-slate-400 text-[11px] font-mono">
                                      {s}
                                    </li>
                                  ))}
                                </ol>
                              )}

                              {toolGeneratedResult.recommendedProducts && (
                                <div className="space-y-1">
                                  {toolGeneratedResult.recommendedProducts.map((p: string, idx: number) => (
                                    <span key={idx} className="block p-1.5 bg-black border border-zinc-900 rounded-lg text-amber-400">
                                      {p}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {toolGeneratedResult.flow && (
                                <div className="p-2.5 bg-zinc-950 border border-zinc-900 rounded">
                                  <span className="text-slate-500 uppercase font-black block text-[9.5px]">Synthesized Sequence:</span>
                                  <p className="normal-case leading-normal">{toolGeneratedResult.flow}</p>
                                </div>
                              )}

                              {toolGeneratedResult.breakdown && (
                                <div className="space-y-1">
                                  {toolGeneratedResult.breakdown.map((b: string, idx: number) => (
                                    <div key={idx} className="flex justify-between border-b border-zinc-900 pb-0.5">
                                      <span>{b.split(':')[0]}</span>
                                      <span className="text-cyan-400">{b.split(':')[1]}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* EXPORT OPTIONS */}
                            <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-900/85">
                              <button
                                onClick={() => handleCopyText(JSON.stringify(toolGeneratedResult, null, 2), 'tool-json')}
                                className="px-3 py-1.5 bg-zinc-900 text-white rounded hover:bg-zinc-850 flex items-center gap-1"
                              >
                                {copiedId === 'tool-json' ? <Check size={11} /> : <Copy size={11} />}
                                Copy JSON Result
                              </button>
                              <a
                                href={`data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(toolGeneratedResult, null, 2))}`}
                                download="nexorax-growth-tool-result.txt"
                                className="px-3 py-1.5 bg-zinc-900 text-zinc-300 rounded hover:bg-zinc-850 flex items-center gap-1"
                              >
                                <Download size={11} /> Export Text
                              </a>
                            </div>

                            {/* CTAs SECTION: soft prompt with multiple requested variants */}
                            <div className="mt-4 p-4.5 bg-purple-950/20 border border-purple-500/30 rounded-xl space-y-3 font-sans">
                              <div className="flex items-center gap-2 text-white font-mono font-bold uppercase text-[11px] tracking-wider">
                                <Sparkles className="text-amber-400" size={13} />
                                <span>Want NexoraX to build this for you?</span>
                              </div>
                              <p className="text-[11px] text-slate-400 normal-case leading-normal font-sans">
                                We specialize in high-velocity deployments with zero front-loaded cash flow limits. Select your execution preference below to log your lead directly.
                              </p>

                              <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase font-black pt-1">
                                <button
                                  onClick={() => handleSaveResultAsLead('Request Setup')}
                                  className="px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 cursor-pointer"
                                >
                                  Request Setup
                                </button>
                                <button
                                  onClick={() => handleSaveResultAsLead('Request Invoice')}
                                  className="px-3 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 cursor-pointer"
                                >
                                  Request Invoice
                                </button>
                                <button
                                  onClick={() => handleSaveResultAsLead('Book Consultation')}
                                  className="px-3 py-1.5 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 cursor-pointer"
                                >
                                  Book Consultation
                                </button>
                                <button
                                  onClick={() => {
                                    onAddLog('Public Tools CTA', 'Trigger Storefront View', 'SUCCESS', 'View Digital Products catalog initiated internally');
                                    onAddLog('System Core', 'Redirect Handshake complete', 'SUCCESS', 'Simulated navigation to /digital-products catalog done');
                                  }}
                                  className="px-3 py-1.5 rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30 cursor-pointer"
                                >
                                  View Digital Products
                                </button>
                                <button
                                  onClick={() => handleSaveResultAsLead('Start Managed Launch')}
                                  className="px-3 py-1.5 rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 cursor-pointer"
                                >
                                  Start Managed Launch
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 2: QR GROWTH CAMPAIGNS --- */}
        {activeTab === 'QR Campaigns' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-mono text-xs">
              {/* Creator Form panel */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                <span className="text-[#00ffff] text-[10px] uppercase font-bold tracking-wider block border-b border-zinc-900 pb-2">
                  Create QR Campaign Node
                </span>

                <form onSubmit={handleCreateCampaign} className="space-y-3.5">
                  <div className="space-y-1">
                    <label className="text-slate-450 block font-bold">Campaign Name:</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white outline-none focus:border-cyan-400"
                      value={newCampaignName}
                      onChange={(e) => setNewCampaignName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-450 block font-bold">Campaign QR Category:</label>
                    <select
                      className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white outline-none"
                      value={newCampaignType}
                      onChange={(e) => setNewCampaignType(e.target.value)}
                    >
                      {qrTemplates.map((t) => (
                        <option key={t.type} value={t.type}>{t.type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-450 block font-bold">Destination Core URL:</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white outline-none focus:border-cyan-400"
                      value={newCampaignUrl}
                      onChange={(e) => setNewCampaignUrl(e.target.value)}
                    />
                  </div>

                  <div className="p-3 bg-zinc-950 border border-zinc-900 rounded-lg space-y-2.5">
                    <span className="text-slate-500 uppercase text-[9px] font-bold block border-b border-zinc-900 pb-1">Advanced UTM parameters & Source tag</span>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-0.5">
                        <label className="text-[9.5px] text-slate-400 font-bold block">Source:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-zinc-350 outline-none"
                          value={newCampaignUtmSource}
                          onChange={(e) => setNewCampaignUtmSource(e.target.value)}
                        />
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-[9.5px] text-slate-400 font-bold block">Medium:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-zinc-350 outline-none"
                          value={newCampaignUtmMedium}
                          onChange={(e) => setNewCampaignUtmMedium(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-0.5">
                        <label className="text-[9.5px] text-slate-400 font-bold block">Campaign Code:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-zinc-350 outline-none"
                          value={newCampaignUtmCampaign}
                          onChange={(e) => setNewCampaignUtmCampaign(e.target.value)}
                        />
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-[9.5px] text-slate-400 font-bold block">Lead Source Tag:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-zinc-350 outline-none"
                          value={newCampaignLeadSourceTag}
                          onChange={(e) => setNewCampaignLeadSourceTag(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2.5 border-t border-zinc-900 pt-2.5">
                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold text-[10px]">Printable Label Captions:</label>
                      <textarea
                        className="w-full h-11 bg-black border border-zinc-800 rounded p-1.5 text-xs text-white outline-none resize-none"
                        value={newCampaignCaption}
                        onChange={(e) => setNewCampaignCaption(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold text-[10px]">Social Share Text:</label>
                      <textarea
                        className="w-full h-11 bg-black border border-zinc-800 rounded p-1.5 text-xs text-white outline-none resize-none"
                        value={newCampaignSocialCaption}
                        onChange={(e) => setNewCampaignSocialCaption(e.target.value)}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-xs rounded-xl cursor-pointer"
                  >
                    Generate Zero-Budget QR Campaign
                  </button>
                </form>
              </div>

              {/* Active list display */}
              <div className="lg:col-span-2 glass-panel border border-purple-500/25 p-5 rounded-2xl bg-black/40 space-y-4">
                <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight border-b border-zinc-900 pb-2">Active QR Campaigns Registry</h3>

                <div className="space-y-4">
                  {qrCampaigns.map((camp) => (
                    <div key={camp.id} className="p-4 border border-zinc-950 bg-[#04010b] rounded-xl flex flex-col md:flex-row gap-5 hover:border-cyan-500/25 transition-all">
                      {/* Left: simulated QR matrix icon canvas output */}
                      <div className="w-24 h-24 bg-black border border-zinc-850 p-1 flex flex-col items-center justify-center shrink-0 rounded-lg relative group overflow-hidden">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&color=00ffff&bgcolor=000000&data=${encodeURIComponent(camp.url)}`}
                          alt="Campaign QR"
                          className="w-20 h-20 bg-black rounded"
                          referrerPolicy="no-referrer"
                        />
                        <span className="text-[6.5px] text-zinc-500 mt-1 uppercase">Scan to Open</span>
                      </div>

                      {/* Right: details stream */}
                      <div className="space-y-2 flex-grow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-white font-bold text-xs uppercase">{camp.name}</h4>
                            <span className="text-[#00ffff] text-[9.5px] uppercase font-bold">{camp.type} • Source Tag: {camp.leadSourceTag}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                setQrCampaigns(qrCampaigns.map(q => q.id === camp.id ? { ...q, status: q.status === 'Active' ? 'Draft' : q.status === 'Draft' ? 'Paused' : 'Active' } : q));
                                onAddLog('QR Campaigns', `Toggled campaign status of "${camp.name}"`, 'SUCCESS');
                              }}
                              className={`px-2 py-0.5 rounded text-[8px] font-black uppercase transition-all cursor-pointer ${
                                camp.status === 'Active' ? 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-400' : camp.status === 'Draft' ? 'bg-zinc-850 text-slate-400' : 'bg-red-950/20 text-red-400 border border-red-900/30'
                              }`}
                            >
                              {camp.status}
                            </button>
                            <button
                              onClick={() => {
                                setQrCampaigns(qrCampaigns.filter(q => q.id !== camp.id));
                                onAddLog('QR Campaigns', `Deleted campaign: ${camp.name}`, 'SUCCESS');
                              }}
                              className="text-slate-500 hover:text-red-400 p-0.5 transition-colors cursor-pointer"
                              title="Delete Campaign"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>

                        <div className="p-2 bg-black/80 rounded font-mono text-[9.5px] text-slate-400 leading-normal normal-case">
                          <p><strong>UTM Mapped Target URL:</strong> <span className="text-cyan-400 select-all font-sans italic break-all">{camp.url}</span></p>
                          <p className="mt-1"><strong>Campaign Printable Caption:</strong> "{camp.caption}"</p>
                          <p className="mt-1 text-[#ffd700]"><strong>Social Copy:</strong> "{camp.socialCaption}"</p>
                        </div>

                        <div className="flex gap-2 text-[9px] pt-1">
                          <button
                            onClick={() => handleCopyText(camp.url, 'utm-url')}
                            className="p-1 px-2.5 bg-zinc-900 text-zinc-300 rounded hover:text-white transition-all cursor-pointer"
                          >
                            Copy target link
                          </button>
                          <button
                            onClick={() => handleCopyText(camp.socialCaption, 'utm-soc')}
                            className="p-1 px-2.5 bg-zinc-900 text-zinc-300 rounded hover:text-white transition-all cursor-pointer"
                          >
                            Copy Copywriting Text
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 3 & 4: ORGANIC SOCIAL CONTENT & SHORTS --- */}
        {activeTab === 'Social Content' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-mono text-xs">
              {/* Creator machine panel */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                  SECTION 3 — ORGANIC SOCIAL CONTENT MACHINE
                </span>
                <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Generate High-Conversion Platform Drafts</h3>

                <div className="space-y-3.5 pt-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold">Select Platform Node:</label>
                      <select 
                        className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white"
                        value={newSocialPlatform}
                        onChange={(e) => setNewSocialPlatform(e.target.value as any)}
                      >
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="TikTok">TikTok</option>
                        <option value="YouTube Shorts">YouTube Shorts</option>
                        <option value="X/Twitter">X/Twitter</option>
                        <option value="Pinterest">Pinterest</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold">Content Narrative Type:</label>
                      <select 
                        className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white"
                        value={newSocialType}
                        onChange={(e) => setNewSocialType(e.target.value)}
                      >
                        {socialTemplates.map((t, idx) => (
                          <option key={idx} value={t.type}>{t.type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-455 font-bold block">Post Caption Content Draft:</label>
                    <textarea
                      className="w-full h-16 bg-black border border-zinc-800 rounded p-1.5 text-xs text-slate-200 outline-none resize-none focus:border-amber-400"
                      value={newSocialCaption}
                      onChange={(e) => setNewSocialCaption(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-slate-455 font-bold block">Hashtags (comma separated):</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-800 rounded p-1.5 text-[11px] text-slate-200 outline-none"
                        value={newSocialHashtags}
                        onChange={(e) => setNewSocialHashtags(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-slate-455 font-bold block">Initial Status Queue:</label>
                      <select 
                        className="w-full bg-black border border-zinc-800 rounded p-1.5 text-xs text-white outline-none"
                        value={newSocialStatus}
                        onChange={(e) => setNewSocialStatus(e.target.value as any)}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Ready">Ready</option>
                        <option value="Posted">Posted</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-3 bg-zinc-950 border border-zinc-900 rounded-xl space-y-2">
                    <span className="text-slate-500 uppercase text-[9px] font-bold block border-b border-zinc-900 pb-0.5">Asset Creative Specifications</span>
                    
                    <div className="space-y-1">
                      <label className="text-[10px] text-slate-400 block">Proposed Graphical / Video Asset Idea:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSocialImageIdea}
                        onChange={(e) => setNewSocialImageIdea(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] text-slate-400 block font-bold">15-Second Reels Script Hook:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSocialVideoScript}
                        onChange={(e) => setNewSocialVideoScript(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-[9.5px] text-slate-400 block">Target CTA:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                          value={newSocialCta}
                          onChange={(e) => setNewSocialCta(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9.5px] text-slate-400 block">Redirection Link:</label>
                        <input
                          type="text"
                          className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                          value={newSocialLink}
                          onChange={(e) => setNewSocialLink(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (!newSocialCaption.trim()) return;
                      const hashtagsArr = newSocialHashtags.split(',').map(h => h.trim()).filter(Boolean);

                      const newP: SocialPost = {
                        id: `post-${Date.now()}`,
                        platform: newSocialPlatform,
                        contentType: newSocialType,
                        caption: newSocialCaption,
                        hashtags: hashtagsArr,
                        imageIdea: newSocialImageIdea,
                        videoScript: newSocialVideoScript,
                        cta: newSocialCta,
                        destinationLink: newSocialLink,
                        qrCampaignId: 'qr-camp-1',
                        status: newSocialStatus
                      };

                      setSocialPosts([newP, ...socialPosts]);
                      setSelectedSocialPostId(newP.id);
                      onAddLog('Social Content', `Drafted ${newSocialType} post for ${newSocialPlatform}`, 'SUCCESS', 'Appended social queue manually');
                      setActionFeedback({
                        message: `Success! Post draft created for ${newSocialPlatform} with a tracking status of "${newSocialStatus}". Mapped CTA: "${newSocialCta}"`,
                        type: 'success'
                      });
                    }}
                    className="w-full py-2 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-xs rounded-xl cursor-pointer shadow-[0_0_15px_rgba(232,64,243,0.1)]"
                  >
                    Draft Social Machine Content
                  </button>
                </div>
              </div>

              {/* Active Draft details layout */}
              <div className="glass-panel border border-cyan-500/25 p-5 rounded-2xl bg-black/40 space-y-4 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                    <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Posts Generator Queue</h3>
                    
                    {/* Platform quick filtering tabs */}
                    <div className="flex gap-1 bg-black p-0.5 rounded border border-zinc-950">
                      <span className="text-[8px] text-slate-500 font-bold px-1.5 flex items-center">Channel filtering</span>
                    </div>
                  </div>

                  <div className="space-y-3.5 max-h-[440px] overflow-y-auto pr-1 pt-2.5">
                    {socialPosts.map((post) => (
                      <div
                        key={post.id}
                        onClick={() => setSelectedSocialPostId(post.id)}
                        className={`p-3.5 rounded-xl border transition-all cursor-pointer space-y-2.5 relative ${
                          selectedSocialPostId === post.id
                            ? 'bg-[#03010b] border-cyan-400 shadow-[0_0_10px_rgba(0,255,255,0.05)]'
                            : 'bg-zinc-950/60 border-zinc-900 hover:border-zinc-850'
                        }`}
                      >
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="text-purple-400 font-extrabold uppercase">{post.platform} • {post.contentType}</span>
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <select
                              className={`p-0.5 px-2 bg-black border border-zinc-800 rounded text-[8.5px] font-bold outline-none cursor-pointer text-amber-400`}
                              value={post.status}
                              onChange={(e) => {
                                setSocialPosts(socialPosts.map(p => p.id === post.id ? { ...p, status: e.target.value as any } : p));
                                onAddLog('Social Engine', `Updated post queue status to ${e.target.value}`, 'SUCCESS');
                              }}
                            >
                              <option value="Draft">Draft</option>
                              <option value="Ready">Ready</option>
                              <option value="Posted">Posted</option>
                            </select>
                            
                            <button
                              onClick={() => {
                                setSocialPosts(socialPosts.filter(p => p.id !== post.id));
                                onAddLog('Social Engine', `Deleted draft post ID ${post.id}`, 'SUCCESS');
                              }}
                              className="text-slate-500 hover:text-red-400 p-0.5 cursor-pointer"
                              title="Delete Draft"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>

                        {/* Editable caption in-place! */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <textarea
                            className="w-full bg-black/40 border border-zinc-950 focus:border-zinc-850 text-white text-[11px] leading-relaxed rounded p-1 outline-none resize-none normal-case font-sans h-12"
                            value={post.caption}
                            onChange={(e) => {
                              setSocialPosts(socialPosts.map(p => p.id === post.id ? { ...p, caption: e.target.value } : p));
                            }}
                          />
                        </div>

                        <p className="text-cyan-400 font-mono text-[9.5px] lowercase">
                          {post.hashtags.map(h => `#${h}`).join(' ')}
                        </p>

                        <div className="p-2.5 bg-black/80 rounded border border-zinc-900 text-slate-405 text-[9.5px] leading-normal space-y-1">
                          <p><strong>Proposed Media Alt Idea:</strong> {post.imageIdea}</p>
                          <p className="text-amber-400 font-mono text-[9px]"><strong>Short Script:</strong> "{post.videoScript}"</p>
                          <p className="text-cyan-500"><strong>Target Link:</strong> <span className="underline italic break-all">{post.destinationLink}</span> [CTA: {post.cta}]</p>
                        </div>

                        <div className="flex gap-2 pt-1 font-mono text-[9.5px]">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyText(`${post.caption}\n\n${post.hashtags.map(h => `#${h}`).join(' ')}`, `sc-${post.id}`);
                            }}
                            className="p-1 px-2.5 bg-zinc-900 text-zinc-300 rounded hover:text-white transition-colors cursor-pointer"
                          >
                            {copiedId === `sc-${post.id}` ? '✓ Copied Post' : 'Copy All Text'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSocialPosts(socialPosts.map(p => p.id === post.id ? { ...p, status: 'Posted' } : p));
                              onAddLog('Social Engine', 'Marked post status as Posted', 'SUCCESS');
                            }}
                            className="p-1 px-2.5 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-950/50 cursor-pointer"
                          >
                            Mark as Posted
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                 {/* SECTION 4 - NEXT-GENERATION NEURAL VIDEO SYNTHESIZER AND SCRIPT GENERATOR */}
            <div className="glass-panel border border-purple-500/30 p-5 rounded-2xl bg-black/40 font-mono text-xs space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 border-b border-zinc-900 pb-3">
                <div className="space-y-1">
                  <span className="text-[#a855f7] font-mono text-[9px] font-black uppercase tracking-[0.25em] bg-purple-950/40 px-3 py-1 rounded border border-purple-500/25 inline-block animate-pulse">
                    SECTION 4 — NEURAL VIDEO SYNTHESIS PROTOCOL (NODE-09.2)
                  </span>
                  <h3 className="text-white font-sans font-black text-base uppercase tracking-tight">Out-Of-This-Universe Video Engine Workspace</h3>
                </div>
                <div className="flex gap-2 text-[10px] items-center">
                  <span className="text-zinc-500 font-bold uppercase">System Link:</span>
                  <span className="text-[#00ffff] bg-cyan-950/20 px-2 py-0.5 rounded border border-cyan-500/20 font-mono select-none">Active Matrix Grid Node-09.2</span>
                </div>
              </div>

              {/* Grid content */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                
                {/* COLUMN 1: CONTROLS & MODEL MODULATION */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="space-y-3.5 p-4 bg-zinc-950/60 rounded-xl border border-zinc-900">
                    <span className="text-purple-400 font-extrabold uppercase text-[10px] tracking-wider block border-b border-zinc-900 pb-1">1. Multi-Modal Vision Parameters</span>
                    
                    <div className="space-y-1.5 pt-1">
                      <div className="flex justify-between items-center text-[10px] text-slate-400">
                        <label className="font-bold text-slate-300">Neural Prompt Directive:</label>
                        <button 
                          type="button"
                          onClick={() => {
                            navigator.clipboard.readText().then(text => {
                              if (text) {
                                setVideoPrompt(text);
                                setActionFeedback({ message: "Successfully pasted prompt from clipboard!", type: "success" });
                              }
                            }).catch(() => {
                              // Fallback prompt paste if clipboard permissions are disabled
                              setVideoPrompt("High-speed flyover of a glowing cyan organic SEO sitemap wireframe on a stellar rotating planetary canvas.");
                              setActionFeedback({ message: "Loaded celestial demo prompt!", type: "success" });
                            });
                          }}
                          className="text-[#00ffff] hover:underline text-[9.5px] cursor-pointer"
                        >
                          [Paste From Clipboard]
                        </button>
                      </div>
                      <textarea
                        className="w-full h-20 bg-black border border-zinc-800 rounded p-2 text-[11px] text-slate-200 focus:border-purple-400 transition-colors placeholder-zinc-700 outline-none resize-none"
                        value={videoPrompt}
                        onChange={(e) => setVideoPrompt(e.target.value)}
                        placeholder="Write or paste your custom prompt matrix..."
                      />
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        <button
                          type="button"
                          onClick={() => setVideoPrompt("Cinematic 3D slow-motion rendering of an holographic ledger mapping £2,820,000 revenue. High-contrast ambient obsidian background with particle trails.")}
                          className="bg-zinc-900 text-[9px] text-slate-400 px-2 py-0.5 rounded hover:text-white border border-zinc-850 hover:bg-zinc-800 cursor-pointer"
                        >
                          Preset: Revenue Ledger
                        </button>
                        <button
                          type="button"
                          onClick={() => setVideoPrompt("Cyberpunk retro neon vector grid showcasing instant 0.8s load times. High density telemetry codes streaming dynamically under physical smartphone overlay.")}
                          className="bg-zinc-900 text-[9px] text-slate-400 px-2 py-0.5 rounded hover:text-white border border-zinc-850 hover:bg-zinc-800 cursor-pointer"
                        >
                          Preset: Speed Engine
                        </button>
                        <button
                          type="button"
                          onClick={() => setVideoPrompt("Interactive 3D local mapping sitemap displaying secure direct-billing invoice registries. Floating glass columns emitting purple laser grids.")}
                          className="bg-zinc-900 text-[9px] text-slate-400 px-2 py-0.5 rounded hover:text-white border border-zinc-850 hover:bg-zinc-805 cursor-pointer"
                        >
                          Preset: Secure Slices
                        </button>
                      </div>
                    </div>

                    {/* Pre-made Type templates dropdown */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-900">
                      <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 block font-bold">Populate Prompt Template:</label>
                        <select
                          className="w-full bg-black border border-zinc-800 rounded p-1.5 text-[11px] text-white outline-none cursor-pointer"
                          value={selectedVideoType}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSelectedVideoType(val);
                            const match = shortVideoTemplates.find(v => v.type === val);
                            if (match) {
                              setVideoPrompt(`Generate highly cinematic ${val} sequence. Core visual hook instructions: "${match.hook}". Compile with responsive HUD overlay.`);
                            }
                          }}
                        >
                          {shortVideoTemplates.map((t) => (
                            <option key={t.type} value={t.type}>{t.type}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 block font-bold">Visual Rendering Theme:</label>
                        <select
                          className="w-full bg-black border border-zinc-800 rounded p-1.5 text-[11px] text-white outline-none cursor-pointer"
                          value={synthThemeColor}
                          onChange={(e) => setSynthThemeColor(e.target.value as any)}
                        >
                          <option value="cyberpunk_purple">Cyberpunk Purple (Obsidian Glow)</option>
                          <option value="matrix_emerald">Matrix Emerald (Terminus Grid)</option>
                          <option value="gold_vanguard">Gold Vanguard (Financial Crown)</option>
                          <option value="cosmic_cyan">Cosmic Cyan (Zero-Latency Space)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* LOGO BRANDING UPLOADER TERMINAL */}
                  <div className="space-y-3 p-4 bg-zinc-950/60 rounded-xl border border-zinc-900">
                    <span className="text-cyan-400 font-extrabold uppercase text-[10px] tracking-wider block border-b border-zinc-900 pb-1">2. Custom Brand Logo Integration</span>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Drag & Drop uploader area */}
                      <div 
                        onDragOver={handleLogoDragOver}
                        onDrop={handleLogoDrop}
                        onClick={() => document.getElementById('logo-file-picker')?.click()}
                        className="p-3 bg-black border border-dashed border-zinc-800 hover:border-[#00ffff]/40 rounded-xl flex flex-col justify-center items-center text-center cursor-pointer transition-all space-y-1 group min-h-[75px]"
                      >
                        <input
                          id="logo-file-picker"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                        />
                        <Film className="w-4 h-4 text-zinc-500 group-hover:text-[#00ffff] transition-colors" />
                        <span className="text-[10px] text-slate-350 font-bold block pt-1">
                          {uploadedLogoName ? `✓ ${uploadedLogoName.slice(0, 14)}` : 'Drag / Drop Logo'}
                        </span>
                        <span className="text-[8px] text-zinc-600 block">Or click to select image (Max 2MB)</span>
                      </div>

                      {/* Brand presets if they don't upload */}
                      <div className="space-y-1.5 flex flex-col justify-center">
                        <label className="text-[10px] text-slate-400 block font-bold">Or Select Brand Preset Node:</label>
                        <select
                          className="w-full bg-black border border-zinc-805 rounded p-1.5 text-[11px] text-white outline-none cursor-pointer focus:border-cyan-400"
                          value={selectedLogoPreset}
                          onChange={(e) => {
                            setSelectedLogoPreset(e.target.value);
                            setUploadedLogo(''); // Clear uploaded to prioritize selected preset
                            setUploadedLogoName('');
                          }}
                        >
                          <option value="nexorax_glowing">NexoraX Digital Neon (Core logo)</option>
                          <option value="vanguard_closer">Vanguard-Closer (Gold Crown)</option>
                          <option value="quantum_ledger">Chronos Ledger (Silver Grid)</option>
                          <option value="oracle_mind">The Oracle AI (Cyber Green Sphere)</option>
                        </select>
                        <div className="text-[8.5px] text-zinc-500 leading-normal">
                          Active brand maps immediately as watermark nodes.
                        </div>
                      </div>
                    </div>

                    {/* Logo alignment knobs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-zinc-900 text-[10px]">
                      <div className="space-y-1 col-span-2">
                        <label className="text-zinc-500 block">Overlay Placement:</label>
                        <select
                          className="w-full bg-zinc-950 border border-zinc-850 rounded p-1 text-[10px] text-white outline-none cursor-pointer"
                          value={logoPosition}
                          onChange={(e) => setLogoPosition(e.target.value as any)}
                        >
                          <option value="top-left">Top Left Corner</option>
                          <option value="top-right">Top Right Corner</option>
                          <option value="bottom-left">Bottom Left Corner</option>
                          <option value="bottom-right">Bottom Right Corner</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-zinc-500 block">Opacity ({logoOpacity}%):</label>
                        <input
                          type="range"
                          min="20"
                          max="100"
                          value={logoOpacity}
                          onChange={(e) => setLogoOpacity(Number(e.target.value))}
                          className="w-full h-1 bg-zinc-850 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-zinc-500 block">Scale ({logoScale}%):</label>
                        <input
                          type="range"
                          min="50"
                          max="150"
                          value={logoScale}
                          onChange={(e) => setLogoScale(Number(e.target.value))}
                          className="w-full h-1 bg-zinc-850 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* MODEL MATRIX KNOBS */}
                  <div className="p-4 bg-zinc-950/60 rounded-xl border border-zinc-900 space-y-3.5">
                    <span className="text-amber-400 font-extrabold uppercase text-[10px] tracking-wider block border-b border-zinc-900 pb-1">3. Synthesizer Parameter Modulators</span>
                    
                    <div className="grid grid-cols-3 gap-3 text-[10px]">
                      <div className="space-y-1">
                        <label className="text-slate-400 block font-bold">Engine Synthesis Model:</label>
                        <select
                          className="w-full bg-black border border-zinc-800 rounded p-1 text-white outline-none cursor-pointer"
                          value={synthModel}
                          onChange={(e) => setSynthModel(e.target.value)}
                        >
                          <option value="NexoraX-Neural-Synthesizer-v5">NexoraX Neural v5</option>
                          <option value="Sora-Quantum-Turbo-Mesh">Sora Quantum Mesh</option>
                          <option value="Runway-Gen3-Hyperion">Runway Gen-3</option>
                          <option value="OmniDiffusion-Star-v9">OmniDiffusion v9</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-slate-400 block font-bold">Target Resolution:</label>
                        <select
                          className="w-full bg-black border border-zinc-800 rounded p-1 text-white outline-none cursor-pointer"
                          value={synthResolution}
                          onChange={(e) => setSynthResolution(e.target.value)}
                        >
                          <option value="8K Ultra Cosmic">8K Cosmic Ultra</option>
                          <option value="4K High-Contrast">4K High-Contrast</option>
                          <option value="Quantum IMAX Core">Quantum IMAX</option>
                          <option value="Infinity Raw Stream">Infinity Raw</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-slate-400 block font-bold">3D Camera Vector:</label>
                        <select
                          className="w-full bg-black border border-zinc-800 rounded p-1 text-white outline-none cursor-pointer"
                          value={synthCameraPath}
                          onChange={(e) => setSynthCameraPath(e.target.value)}
                        >
                          <option value="Simulated 3D Drone Orbit">Simulated 3D Orbit</option>
                          <option value="Super Hyper Zoom-Out">Hyperspeed Zoom-Out</option>
                          <option value="Dynamic Dolly Track">Dolly Track Pan</option>
                          <option value="Infinite Nebula Rise">Infinite Nebula Rise</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-1 text-[10px]">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <label className="text-slate-400 font-bold">Sora Diffusion:</label>
                          <span className="text-purple-400 font-bold">{synthDiffusionRate}%</span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          value={synthDiffusionRate}
                          onChange={(e) => setSynthDiffusionRate(Number(e.target.value))}
                          className="w-full h-1 bg-zinc-850 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <label className="text-slate-400 font-bold">Temporal Flow:</label>
                          <span className="text-cyan-400 font-bold">{synthTemporalConsistency}%</span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          value={synthTemporalConsistency}
                          onChange={(e) => setSynthTemporalConsistency(Number(e.target.value))}
                          className="w-full h-1 bg-zinc-850 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <label className="text-slate-405 font-bold">Target FPS Output:</label>
                          <span className="text-[#e840f3] font-bold">{synthFps} FPS</span>
                        </div>
                        <select
                          className="w-full bg-black border border-zinc-805 rounded p-0.5 text-[10px] text-white outline-none cursor-pointer"
                          value={synthFps}
                          onChange={(e) => setSynthFps(Number(e.target.value))}
                        >
                          <option value={30}>30 FPS Standard</option>
                          <option value={60}>60 FPS Ultra-Sleek</option>
                          <option value={120}>120 FPS High-Glow</option>
                          <option value={240}>240 FPS Matrix Fluid</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Trigger main action */}
                  <button
                    type="button"
                    onClick={handleLaunchNeuralSynthesizer}
                    disabled={isSynthRunning}
                    className={`w-full py-3 bg-gradient-to-r from-[#a855f7] to-[#e840f3] text-black font-black uppercase text-xs rounded-xl transition-all shadow-[0_0_20px_rgba(232,64,243,0.15)] flex items-center justify-center gap-2 cursor-pointer ${
                      isSynthRunning ? 'opacity-40 cursor-wait' : 'hover:opacity-100 font-extrabold'
                    }`}
                  >
                    <Sparkles className={`w-4 h-4 ${isSynthRunning ? 'animate-spin' : ''}`} />
                    {isSynthRunning ? `SYNTHESIZING MATRIX: ${synthProgress}% COMPLETE` : 'Initialize Neural Video Synthesizer (Node-09.2)'}
                  </button>
                </div>

                {/* COLUMN 2: LIVE SIMULATED VIDEO CANVASES & RENDER SCREEN */}
                <div className="lg:col-span-2 space-y-4">
                  {/* MAIN PREVIEW TERMINAL WRAPPER */}
                  <div className="relative rounded-2xl border border-zinc-800 bg-[#020106] overflow-hidden shadow-[0_0_25px_rgba(0,0,0,0.8)] aspect-video flex flex-col justify-between">
                    
                    {/* Simulated space network particle background */}
                    <div className="absolute inset-0 opacity-80 pointer-events-none overflow-hidden mix-blend-screen bg-black">
                      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(15,8,30,0.85)_0%,rgba(0,0,0,1)_100%)]" />
                      
                      {/* Active orbital dynamic circles simulating out of this universe particle synthesis! */}
                      {synthStreamPlaying && !isSynthRunning && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-[240px] h-[240px] rounded-full border border-dashed border-[#e840f3]/25 animate-spin" style={{ animationDuration: '40s' }} />
                          <div className="absolute w-[180px] h-[180px] rounded-full border border-double border-[#00ffff]/15 animate-spin" style={{ animationDuration: '25s', animationDirection: 'reverse' }} />
                          <div className="absolute w-[100px] h-[100px] rounded-full border border-dotted border-purple-500/20 animate-ping" style={{ animationDuration: '5s' }} />
                          
                          {/* Theme visual matrices */}
                          {synthThemeColor === 'cyberpunk_purple' && (
                            <div className="absolute inset-0 bg-gradient-to-tr from-purple-950/15 via-black/25 to-pink-950/20 mix-blend-color-dodge animate-pulse" />
                          )}
                          {synthThemeColor === 'matrix_emerald' && (
                            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/25 via-black/30 to-zinc-950/20 mix-blend-color-dodge animate-pulse" />
                          )}
                          {synthThemeColor === 'gold_vanguard' && (
                            <div className="absolute inset-0 bg-gradient-to-tr from-amber-950/15 via-black/30 to-yellow-950/25 mix-blend-color-dodge animate-pulse" style={{ animationDuration: '6s' }} />
                          )}
                          {synthThemeColor === 'cosmic_cyan' && (
                            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-950/20 via-black/25 to-blue-950/15 mix-blend-color-dodge animate-pulse" style={{ animationDuration: '4s' }} />
                          )}
                        </div>
                      )}

                      {/* Scanning visual state if running */}
                      {isSynthRunning && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#020108]/95 z-20">
                          <div className="loader-pulse w-14 h-14 rounded-full border-4 border-t-purple-500 border-r-cyan-400 border-zinc-900 animate-spin" />
                          <span className="text-[10px] text-purple-400 font-bold uppercase tracking-[0.2em] font-mono animate-pulse pt-2">Computing Voxel Grids...</span>
                          
                          <div className="w-48 bg-zinc-900 h-1.5 rounded-full overflow-hidden border border-zinc-800">
                            <div className="bg-gradient-to-r from-purple-500 via-[#e840f3] to-cyan-400 h-full transition-all duration-300" style={{ width: `${synthProgress}%` }} />
                          </div>
                          <span className="text-[9px] text-zinc-500 font-mono">{synthProgress}% Complete</span>
                        </div>
                      )}
                    </div>

                    {/* TOP STATUS BAR OVERLAY */}
                    <div className="absolute top-0 left-0 right-0 p-3 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent z-10 text-[9px] font-mono">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-zinc-400 uppercase">SYS-RENDER: <strong className="text-[#00ffff]">{synthStreamPlaying ? 'LIVE FEED' : 'IDLE'}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-400 bg-black/50 px-2 py-0.5 rounded border border-zinc-900">
                        <span>RES: <strong>{synthResolution}</strong></span>
                        <span>FPS: <strong>{synthFps}</strong></span>
                        <span className="text-purple-400 font-bold">{synthModel.split('-')[0]}</span>
                      </div>
                    </div>

                    {/* INTERACTIVE LOGO WATERMARK OVERLAY */}
                    {((uploadedLogo || selectedLogoPreset) && !isSynthRunning) && (
                      <div 
                        className="absolute z-20 transition-all p-1.5 pointer-events-none"
                        style={{
                          top: logoPosition.startsWith('top') ? '40px' : 'auto',
                          bottom: logoPosition.startsWith('bottom') ? '12px' : 'auto',
                          left: logoPosition.endsWith('left') ? '12px' : 'auto',
                          right: logoPosition.endsWith('right') ? '12px' : 'auto',
                          opacity: logoOpacity / 100,
                          transform: `scale(${logoScale / 100})`,
                          transformOrigin: `${logoPosition.replace('-', ' ')}`
                        }}
                      >
                        {uploadedLogo ? (
                          <div className="flex items-center gap-1.5 bg-black/80 px-2 py-1 rounded border border-cyan-500/20 shadow-lg">
                            <img 
                              src={uploadedLogo} 
                              alt="Custom Watermark" 
                              className="h-6 w-auto max-w-[80px] object-contain rounded"
                              referrerPolicy="no-referrer"
                            />
                            <span className="text-[7.5px] text-cyan-400 uppercase font-black tracking-wider font-mono">NODE RENDER</span>
                          </div>
                        ) : (
                          /* Render glowing preset vector SVG logos */
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded border shadow-lg bg-black/90 transition-transform">
                            {selectedLogoPreset === 'nexorax_glowing' && (
                              <>
                                <Zap size={11} className="text-[#e840f3] animate-pulse" />
                                <span className="text-[8.5px] font-black tracking-[0.1em] text-white uppercase font-sans">
                                  Nexora<span className="text-[#00ffff]">X</span>
                                </span>
                              </>
                            )}
                            {selectedLogoPreset === 'vanguard_closer' && (
                              <>
                                <Flame size={11} className="text-amber-400" />
                                <span className="text-[8px] font-black text-[#ffb61e] tracking-widest font-mono">
                                  VANGUARD
                                </span>
                              </>
                            )}
                            {selectedLogoPreset === 'quantum_ledger' && (
                              <>
                                <Database size={11} className="text-slate-400" />
                                <span className="text-[8px] font-bold text-slate-300 font-mono">
                                  CHRONOS-LEDR
                                </span>
                              </>
                            )}
                            {selectedLogoPreset === 'oracle_mind' && (
                              <>
                                <Cpu size={11} className="text-emerald-400 animate-pulse" />
                                <span className="text-[8px] font-black text-emerald-400 tracking-wider">
                                  ORACLE_AI
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* LIVE SUBTITLE SCENE TEXT OVERLAY */}
                    {synthStreamPlaying && !isSynthRunning && videoScriptDraft && (
                      <div className="absolute inset-x-4 bottom-14 z-10 text-center bg-black/75 backdrop-blur-sm p-2 rounded border border-zinc-900/50">
                        <span className="text-[8px] text-purple-400 font-black tracking-widest block uppercase mb-0.5">TELECAST CAPTION OVERLAY</span>
                        <p className="text-white text-[11.5px] font-sans antialiased leading-snug drop-shadow-lg font-black italic normal-case">
                          {videoScriptDraft.hook}
                        </p>
                        <p className="text-[#00ffff] font-mono text-[9px] mt-1 italic break-all normal-case">
                          Active Scene: {videoScriptDraft.scene1.slice(0, 70)}...
                        </p>
                      </div>
                    )}

                    {/* RENDER FINISH EMPTY STATE IN CANVAS */}
                    {!synthStreamPlaying && !isSynthRunning && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center bg-zinc-950/85">
                        <div className="w-12 h-12 rounded-full bg-purple-950/50 border border-purple-500/25 flex items-center justify-center text-purple-400 mb-3 animate-pulse">
                          <Play className="w-5 h-5 ml-0.5" />
                        </div>
                        <h4 className="text-white font-sans font-black text-xs uppercase tracking-wider">Preview Stream Ready</h4>
                        <p className="text-zinc-500 text-[10px] max-w-xs mt-1">
                          Configure your multi-modal prompts, adjust parameters, upload corporate branding, and click Compile!
                        </p>
                      </div>
                    )}

                    {/* BOTTOM PLAY BAR HUD */}
                    {synthStreamPlaying && !isSynthRunning && (
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black to-black/70 z-10 flex justify-between items-center border-t border-zinc-900 font-mono text-[9px]">
                        <div className="flex gap-2 items-center">
                          <button 
                            type="button"
                            onClick={() => {
                              setActionFeedback({ message: "Simulating synthesis playthrough toggled!", type: 'success' });
                            }}
                            className="p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
                            title="Play Stream"
                          >
                            <PlayCircle size={14} className="text-[#e840f3]" />
                          </button>
                          <span className="text-slate-450 uppercase">Rendering path: {synthCameraPath.slice(0, 16)}...</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-zinc-500">Vol: [Muted Synth]</span>
                          <span className="bg-emerald-950 text-emerald-400 px-1.5 py-0.2 rounded border border-emerald-500/30 text-[8px] font-bold font-mono">100% SYNCHRONIZED</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TELEMETRY CONSOLE FEEDBACK */}
                  <div className="p-3 bg-black rounded-xl border border-zinc-900 space-y-2 h-32 flex flex-col justify-between">
                    <div className="flex justify-between items-center text-[8.5px] uppercase border-b border-zinc-900 pb-1 font-mono">
                      <span className="text-purple-400 font-extrabold flex items-center gap-1"><Cpu size={10} /> Real-Time compilation Telemetry logs</span>
                      <span className="text-zinc-500 font-mono">Engine: Node-09.2</span>
                    </div>

                    <div className="flex-grow font-mono overflow-y-auto text-[9.5px] leading-relaxed text-[#00ffcc] space-y-1 pr-1 select-text scrollbar-thin">
                      {synthLogs.length > 0 ? (
                        synthLogs.map((log, i) => (
                          <div key={i} className="font-mono text-cyan-400 transition-all duration-300">
                            {log}
                          </div>
                        ))
                      ) : (
                        <div className="text-zinc-700 italic text-center pt-4">
                          Launch neural video synthesis node to stream raw GPU vector logs...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SCRIPT RESULT DISPLAY */}
                  {videoScriptDraft && (
                    <div className="p-4 bg-[#03010b] border border-cyan-500/35 rounded-2xl space-y-3 shadow-inner relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
                      
                      <div className="flex justify-between items-center text-[10px] border-b border-zinc-900 pb-2 font-mono">
                        <span className="text-[#e840f3] font-black uppercase tracking-wider">{videoScriptDraft.type}</span>
                        <span className="text-slate-500">Manual Publish Enabled</span>
                      </div>

                      <div className="space-y-2.5 text-[11px] leading-relaxed">
                        <div className="p-2 bg-black/60 rounded border border-zinc-950">
                          <p className="text-amber-400 font-black text-[10px] uppercase tracking-tight block">ACTIVE VIDEO HOOK</p>
                          <p className="text-slate-200 mt-1 leading-normal italic normal-case">"{videoScriptDraft.hook}"</p>
                        </div>
                        
                        <div className="p-2.5 bg-black/60 rounded border border-zinc-950 space-y-1.5">
                          <span className="text-slate-500 font-bold uppercase text-[9px] tracking-widest block">Scene Sequences</span>
                          <ul className="space-y-1 list-none p-0 leading-normal normal-case text-slate-300">
                            <li className="p-1 bg-zinc-950 px-2 rounded-md border-l border-purple-500 font-mono text-[10px]">{videoScriptDraft.scene1}</li>
                            <li className="p-1 bg-zinc-950 px-2 rounded-md border-l border-cyan-500 font-mono text-[10px]">{videoScriptDraft.scene2}</li>
                            <li className="p-1 bg-zinc-950 px-2 rounded-md border-l border-pink-500 font-mono text-[10px]">{videoScriptDraft.scene3}</li>
                          </ul>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px]">
                          <div className="p-2 bg-black/80 rounded border border-zinc-950 text-emerald-400 font-mono">
                            <span className="text-zinc-500 font-black block uppercase text-[8.5px] mb-0.5">Synthesized Audio Voiceover</span>
                            "{videoScriptDraft.voiceover}"
                          </div>
                          <div className="p-2 bg-black/80 rounded border border-zinc-950 text-slate-400 font-mono space-y-1">
                            <div>
                              <span className="text-zinc-500 font-black block uppercase text-[8.5px]">Direct CTA Button</span>
                              <span className="text-[#00ffff] font-bold">[{videoScriptDraft.cta}]</span>
                            </div>
                            <div className="pt-1.5 border-t border-zinc-900 mt-1">
                              <span className="text-zinc-500 font-bold block text-[8px] uppercase">Mapped Social Hashtags</span>
                              <span className="text-cyan-400 leading-none lowercase text-[9px]">{videoScriptDraft.hashtags.map(h => `#${h}`).join(' ')}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-1 font-mono text-[9px]">
                        <button
                          type="button"
                          onClick={() => handleCopyText(`Hook: ${videoScriptDraft.hook}\nScene 1: ${videoScriptDraft.scene1}\nVoiceover: ${videoScriptDraft.voiceover}\nCTA: ${videoScriptDraft.cta}`, 'reels')}
                          className="p-1.5 px-3 bg-zinc-900 text-zinc-200 rounded-lg hover:text-white transition-colors cursor-pointer"
                        >
                          {copiedId === 'reels' ? '✓ Copied Telecast Script' : 'Copy All Text Elements'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setActionFeedback({
                              message: 'Success! Video render dispatched package setup to browser client.',
                              type: 'success'
                            });
                            onAddLog('Systems Admin', 'Initiated mp4 rendering output to local directory', 'SUCCESS');
                          }}
                          className="p-1.5 px-3 bg-purple-950/30 border border-purple-500/20 text-[#e840f3] rounded-lg hover:bg-purple-950/50 cursor-pointer"
                        >
                          Export Ultra-mp4 (Node-09.2)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>             </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 5: SEO PAGES GENERATOR --- */}
        {activeTab === 'SEO Pages' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 animate-fadeIn">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 font-mono text-xs">
              
              {/* Creator Form panel */}
              <div className="lg:col-span-1 glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                <span className="text-[#00ffff] font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-cyan-950/40 px-2.5 py-0.5 rounded border border-cyan-500/20 inline-block">
                  SEO AGENT NODE CREATOR
                </span>
                <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Draft Organic Search Index Page</h3>

                <form onSubmit={handleCreateSeoDraft} className="space-y-3 pt-1">
                  <div className="space-y-1">
                    <label className="text-slate-455 block font-bold text-[10px]">Target SEO Keyword:</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 rounded p-1.5 text-xs text-white outline-none focus:border-cyan-400"
                      value={newSeoKeyword}
                      onChange={(e) => {
                        setNewSeoKeyword(e.target.value);
                        setNewSeoSlug(e.target.value.toLowerCase().trim().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-455 block font-bold text-[10px]">Page Meta Title Tag:</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 rounded p-1.5 text-[11px] text-white outline-none focus:border-cyan-400"
                      value={newSeoTitle}
                      onChange={(e) => setNewSeoTitle(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-455 block font-bold text-[10px]">URL Slug Path:</label>
                    <div className="flex bg-black border border-zinc-800 rounded overflow-hidden">
                      <span className="bg-zinc-900 border-r border-zinc-800 p-1.5 text-[10px] text-slate-500 select-none">/seo/</span>
                      <input
                        type="text"
                        className="w-full bg-black p-1.5 text-xs text-cyan-400 outline-none"
                        value={newSeoSlug}
                        onChange={(e) => setNewSeoSlug(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-455 block font-bold text-[10px]">Meta Description Code:</label>
                    <textarea
                      className="w-full h-14 bg-black border border-zinc-800 rounded p-1.5 text-xs text-slate-200 outline-none resize-none focus:border-cyan-400"
                      value={newSeoMetaDesc}
                      onChange={(e) => setNewSeoMetaDesc(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-slate-455 block font-bold text-[10px]">Hero Display Headline:</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 rounded p-1.5 text-[11px] text-white outline-none focus:border-cyan-400"
                      value={newSeoHeroHeadline}
                      onChange={(e) => setNewSeoHeroHeadline(e.target.value)}
                    />
                  </div>

                  <div className="p-2.5 bg-zinc-950 border border-zinc-900 rounded-lg space-y-2">
                    <span className="text-slate-500 uppercase text-[8.5px] font-bold block border-b border-zinc-900 pb-0.5">Pain vs Solution Grid mapping</span>
                    
                    <div className="space-y-1">
                      <label className="text-[9.5px] text-slate-400 block">Identified Regional Pain:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11.5px] text-slate-300 outline-none"
                        value={newSeoProblem}
                        onChange={(e) => setNewSeoProblem(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[9.5px] text-slate-400 block font-bold">Engineered Solution Node:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11.5px] text-slate-300 outline-none"
                        value={newSeoSolution}
                        onChange={(e) => setNewSeoSolution(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="p-2.5 bg-zinc-950 border border-zinc-900 rounded-lg space-y-2">
                    <span className="text-slate-500 uppercase text-[8.5px] font-bold block border-b border-zinc-900 pb-0.5">Keywords & Schema Entities</span>

                    <div className="space-y-1">
                      <label className="text-[9.5px] text-slate-400 block font-bold">Slices Features (comma list):</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSeoFeatures}
                        onChange={(e) => setNewSeoFeatures(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[9.5px] text-slate-400 block font-bold">Local Keywords List (comma list):</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSeoLocalKeywords}
                        onChange={(e) => setNewSeoLocalKeywords(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="p-2.5 bg-[#03010b] border border-cyan-900/35 rounded-lg space-y-2">
                    <span className="text-[#00ffff] uppercase text-[8.5px] font-black block border-b border-zinc-900 pb-0.5">Dynamic Structured FAQ Key</span>
                    
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 block">FAQ Question:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSeoFaqQ}
                        onChange={(e) => setNewSeoFaqQ(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] text-slate-400 block">FAQ Answer Panel:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-850 rounded p-1 text-[11px] text-slate-300 outline-none"
                        value={newSeoFaqA}
                        onChange={(e) => setNewSeoFaqA(e.target.value)}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2 bg-[#e840f3] hover:opacity-95 text-black font-black uppercase text-xs rounded-xl cursor-pointer"
                  >
                    Draft SEO Sitemap Page
                  </button>
                </form>
              </div>

              {/* Active list display */}
              <div className="lg:col-span-3 glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-4">
                <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                  SECTION 5 — SEO ORGANIC PAGE GENERATOR
                </span>
                <div className="flex justify-between items-center pb-2 border-b border-zinc-900">
                  <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Active Search Index Content Drafts</h3>
                  <span className="text-[10px] text-slate-500">Admin Approval Mandate Enforced</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 overflow-y-auto max-h-[85vh] pr-1">
                  {seoDrafts.map((page) => (
                    <div key={page.id} className="p-4 bg-[#04010a] border border-zinc-950 hover:border-cyan-500/20 rounded-xl space-y-3.5 transition-all relative">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-white font-bold text-xs uppercase underline break-all">{page.keyword}</h4>
                          <span className="text-[10px] text-slate-550 font-sans italic">URL Slug: <strong className="text-cyan-400 font-mono">/seo/{page.slug}</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleToggleSeoApproval(page.id)}
                            className={`px-2 py-1 rounded text-[8.5px] font-black uppercase transition-all cursor-pointer ${
                              page.approved
                                ? 'bg-emerald-950/50 border border-emerald-500/25 text-emerald-400'
                                : 'bg-zinc-900 text-slate-550 hover:text-white border border-zinc-800'
                            }`}
                          >
                            {page.approved ? '✓ Approved' : 'Approve Draft'}
                          </button>
                          
                          <button
                            onClick={() => {
                              setSeoDrafts(seoDrafts.filter(d => d.id !== page.id));
                              onAddLog('SEO Generator', `Removed draft: ${page.keyword}`, 'SUCCESS');
                            }}
                            className="text-slate-600 hover:text-red-400 p-0.5 transition-colors cursor-pointer"
                            title="Delete Draft"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      {/* In-place content review list */}
                      <div className="p-3 bg-black/80 rounded-xl border border-zinc-900 text-slate-400 text-[10px] leading-normal space-y-2 normal-case font-sans">
                        <p><strong>Page Header Title Tag:</strong> <span className="text-slate-200 block mt-0.5">"{page.title}"</span></p>
                        <p><strong>Meta Description Tag:</strong> <span className="text-slate-200 block mt-0.5 text-[9.5px]">"{page.metaDesc}"</span></p>
                        <p className="text-amber-400 font-mono text-[9px] uppercase tracking-wider block">Hero: "{page.heroHeadline}"</p>
                        <p className="border-t border-zinc-900 pt-1 text-[9.5px]"><strong>Local Pain Match:</strong> <span className="text-[#e840f3] block">"{page.problem}"</span></p>
                        <p className="text-emerald-400 text-[9.5px]"><strong>Engineered Solution Node:</strong> <span className="block italic text-slate-300">"{page.solution}"</span></p>
                        
                        {page.faq && page.faq.question && (
                          <div className="mt-1.5 border-t border-zinc-900 pt-1.5 text-[9.5px] bg-[#02010b] p-1.5 rounded">
                            <span className="text-cyan-400 uppercase font-black tracking-wider text-[8px] block">Structured FAQ Schema Node</span>
                            <p className="font-bold text-slate-300">Q: {page.faq.question}</p>
                            <p className="text-slate-400 italic">A: {page.faq.answer}</p>
                          </div>
                        )}
                      </div>

                      <div className="text-[10px] pt-1 space-y-1 flex flex-col justify-between">
                        <div>
                          <span className="text-slate-500 uppercase font-bold tracking-wider text-[9px] block mb-1">Grid Features & Slices:</span>
                          <div className="flex gap-1 flex-wrap">
                            {page.features.map((f, i) => (
                              <span key={i} className="bg-zinc-950 text-slate-400 px-2 py-0.5 rounded border border-zinc-900 text-[9px]">
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="pt-2 flex justify-between items-center text-[9.5px] border-t border-zinc-900/60 text-slate-500 uppercase font-mono">
                          <span>Keywords: {page.localKeywords.join(', ')}</span>
                          <button
                            onClick={() => handleCopyText(`Title: ${page.title}\nDescription: ${page.metaDesc}`, `seo-copy-${page.id}`)}
                            className="text-cyan-400 hover:underline cursor-pointer"
                          >
                            {copiedId === `seo-copy-${page.id}` ? '✓ Copied Meta' : 'Copy Meta'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 6: GOOGLE AND BING VISIBILITY CHECKLIST --- */}
        {activeTab === 'Visibility Checklist' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5 font-mono text-xs">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 6 — GOOGLE AND BING VISIBILITY CHECKLIST
              </span>
              <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight pb-2 border-b border-zinc-900">Configure Free Engine Index Identifiers</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* GBP columns */}
                <div className="p-4 bg-zinc-950/80 rounded-xl space-y-3.5 border border-purple-500/15">
                  <span className="text-[#e840f3] font-bold block uppercase tracking-wider text-[11px] border-b border-zinc-900 pb-1">
                    Google Business Profile (GBP)
                  </span>
                  <div className="space-y-2 select-none">
                    {[
                      { key: 'gbpProfileCreated', label: 'Create profile' },
                      { key: 'gbpNameAdded', label: 'Add business name' },
                      { key: 'gbpWebsiteLinked', label: 'Add website' },
                      { key: 'gbpServiceAreaSet', label: 'Add service area' },
                      { key: 'gbpServicesListed', label: 'Add services' },
                      { key: 'gbpPhotosUploaded', label: 'Add photos/logo' },
                      { key: 'gbpDescriptionConfigured', label: 'Add business description' },
                      { key: 'gbpLaunchPostPublished', label: 'Add launch post' },
                      { key: 'gbpProductsSaturated', label: 'Add products/services' },
                      { key: 'gbpVerificationPending', label: 'Verification status' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={checklist[item.key]}
                          onChange={(e) => setChecklist({ ...checklist, [item.key]: e.target.checked })}
                          className="w-4 h-4 cursor-pointer align-middle bg-black border-zinc-850 rounded"
                        />
                        <label htmlFor={item.key} className="text-slate-350 cursor-pointer text-[10.5px] leading-none">{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* GSC columns */}
                <div className="p-4 bg-zinc-950/80 rounded-xl space-y-3.5 border border-cyan-500/15">
                  <span className="text-[#00ffff] font-bold block uppercase tracking-wider text-[11px] border-b border-zinc-900 pb-1">
                    Google Search Console (GSC)
                  </span>
                  <div className="space-y-2 select-none">
                    {[
                      { key: 'gscDomainVerified', label: 'Verify domain/property' },
                      { key: 'gscSitemapSubmitted', label: 'Submit sitemap' },
                      { key: 'gscIndexingActive', label: 'Check indexing' },
                      { key: 'gscMobileUsabilityOk', label: 'Check mobile usability' },
                      { key: 'gscSpeedMetricsChecked', label: 'Check page performance' },
                      { key: 'gscIndexingRequestSent', label: 'Request indexing for key pages' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={checklist[item.key]}
                          onChange={(e) => setChecklist({ ...checklist, [item.key]: e.target.checked })}
                          className="w-4 h-4 cursor-pointer align-middle bg-black border-zinc-850 rounded"
                        />
                        <label htmlFor={item.key} className="text-slate-350 cursor-pointer text-[10.5px] leading-none">{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bing columns */}
                <div className="p-4 bg-zinc-950/80 rounded-xl space-y-3.5 border border-purple-500/15">
                  <span className="text-purple-400 font-bold block uppercase tracking-wider text-[11px] border-b border-zinc-900 pb-1">
                    Bing Webmaster Tools
                  </span>
                  <div className="space-y-2 select-none">
                    {[
                      { key: 'bingSiteAdded', label: 'Add site' },
                      { key: 'bingSitemapSubmitted', label: 'Submit sitemap' },
                      { key: 'bingSeoScanned', label: 'Check SEO reports' },
                      { key: 'bingImportantUrlsSent', label: 'Submit important URLs' },
                      { key: 'bingSearchTracked', label: 'Monitor search performance' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={checklist[item.key]}
                          onChange={(e) => setChecklist({ ...checklist, [item.key]: e.target.checked })}
                          className="w-4 h-4 cursor-pointer align-middle bg-black border-zinc-850 rounded"
                        />
                        <label htmlFor={item.key} className="text-slate-350 cursor-pointer text-[10.5px] leading-none">{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 8: OFFER LADDER --- */}
        {activeTab === 'Offer Ladder' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5 font-mono text-xs">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 8 — OFFER LADDER (CHOOSE THE LEVEL THAT FITS YOUR BUSINESS)
              </span>
              <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Structured Corporate Compensation Allocation</h3>
                <span className="text-[10px] text-slate-550">Choose level that fits your business. Final quote depends on requirements.</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-3.5 text-[10.5px] leading-normal pt-2">
                {/* Column 1: Free */}
                <div className="p-3 bg-zinc-950/90 border border-zinc-900 rounded-lg space-y-2">
                  <span className="text-emerald-400 uppercase font-black block tracking-widest text-[9px] border-b border-zinc-900 pb-1">1. Free Tools Pool</span>
                  <ul className="space-y-1 text-slate-300">
                    <li>- Free QR Gen Core</li>
                    <li>- Site health checks</li>
                    <li>- AI specs brief</li>
                    <li>- Slide outlines</li>
                    <li>- Automation plans</li>
                  </ul>
                </div>

                {/* Column 2: Low cost */}
                <div className="p-3 bg-zinc-950/90 border border-zinc-900 rounded-lg space-y-2">
                  <span className="text-cyan-400 uppercase font-black block tracking-widest text-[9px] border-b border-zinc-900 pb-1">2. Low Cost Pack</span>
                  <ul className="space-y-1 text-slate-300">
                    <li>- QR Business Cards</li>
                    <li>- Presentation deck</li>
                    <li>- React design template</li>
                    <li>- Social posts pack</li>
                    <li>- Code Pack assets</li>
                    <li>- Workflow templates</li>
                  </ul>
                </div>

                {/* Column 3: Setup */}
                <div className="p-3 bg-zinc-950/90 border border-zinc-900 rounded-lg space-y-2">
                  <span className="text-[#ffd700] uppercase font-black block tracking-widest text-[9px] border-b border-zinc-900 pb-1">3. Setup Nodes</span>
                  <ul className="space-y-1 text-slate-300">
                    <li>- Managed launch page</li>
                    <li>- AI assistant deploy</li>
                    <li>- Social flows setup</li>
                    <li>- custom n8n setup</li>
                    <li>- Admin CRM setup</li>
                    <li>- Custom deck outline</li>
                  </ul>
                </div>

                {/* Column 4: Monthly */}
                <div className="p-3 bg-zinc-950/90 border border-zinc-900 rounded-lg space-y-2">
                  <span className="text-purple-400 uppercase font-black block tracking-widest text-[9px] border-b border-zinc-900 pb-1">4. Monthly Support</span>
                  <ul className="space-y-1 text-slate-300">
                    <li>- Cloud host / service</li>
                    <li>- Live Chat assistance</li>
                    <li>- Social automation sync</li>
                    <li>- Index credit cycles</li>
                    <li>- Core worker tasks</li>
                    <li>- 10% Maintenance Fee</li>
                  </ul>
                </div>

                {/* Column 5: Tiers */}
                <div className="p-3 bg-purple-950/30 border border-purple-500/25 rounded-lg space-y-2">
                  <span className="text-[#e840f3] uppercase font-black block tracking-widest text-[9px] border-b border-zinc-900 pb-1">5. Tiers hierarchy</span>
                  <ol className="space-y-1 text-slate-350 list-decimal list-inside font-sans italic">
                    <li>Bronze Tier</li>
                    <li>Silver Tier</li>
                    <li>Gold Tier</li>
                    <li>Diamond Tier</li>
                    <li>Platinum Tier</li>
                    <li>Black Diamond</li>
                    <li>VIP Lifetime</li>
                  </ol>
                </div>
              </div>

              <p className="text-[10px] text-amber-500 italic text-center font-sans">
                * Note: Choose the level that fits your business. Final quote depends on requirements. All contracts route settlements directly to Secure Provider.
              </p>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 9: COMMUNITY GOALS TRACKER --- */}
        {activeTab === 'Community Goals' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5 font-mono text-xs">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 9 — SYSTEM COMMUNITY GOALS TRACKER
              </span>
              <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight">Charitable & Regional Business Contributions</h3>
                <span className="text-[10px] text-slate-550">Review Status: Managed Allocation Enforced Natively</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Goal creator */}
                <div className="p-4 bg-zinc-950/60 rounded-xl border border-zinc-900 space-y-3.5">
                  <span className="text-white font-bold block uppercase tracking-wider text-[11px] border-b border-zinc-900 pb-1">Configure Goal</span>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold">Goal Name:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-zinc-300 outline-none"
                        value={newGoalName}
                        onChange={(e) => setNewGoalName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-slate-450 block font-bold">Approved Cause Name:</label>
                      <input
                        type="text"
                        className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-zinc-300 outline-none"
                        value={newCauseName}
                        onChange={(e) => setNewCauseName(e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (!newGoalName.trim()) return;
                        const newG: CommunityGoal = {
                          id: `goal-${Date.now()}`,
                          goalName: newGoalName,
                          causeName: newCauseName,
                          status: 'Planned',
                          notes: 'Verified allocations tracked upon eligible receipts basis structure.',
                          allocationBasis: '3% of certified invoices',
                          recordedAllocation: 0,
                          publicDisclosure: true,
                          reviewStatus: 'Pending Audit'
                        };
                        setCommunityGoals([...communityGoals, newG]);
                        setNewGoalName('');
                        setNewCauseName('');
                        onAddLog('Community Tracker', `Created goal: ${newGoalName}`, 'SUCCESS', 'Appended regional tracking matrix');
                        alert('Community goal successfully logged! All records backed to the Google Drive Resource Library.');
                      }}
                      className="w-full py-2 bg-gradient-to-r from-purple-900 to-cyan-950 text-white font-bold border border-purple-500/30 uppercase text-xs rounded-xl"
                    >
                      Log Approved Goal
                    </button>
                  </div>
                </div>

                {/* Goals registry list */}
                <div className="md:col-span-2 space-y-3">
                  {communityGoals.map((g) => (
                    <div key={g.id} className="p-3 bg-[#03010a] border border-zinc-950 rounded-xl space-y-1.5 hover:border-cyan-500/15 transition-all text-[11px]">
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-[#00ffff] font-extrabold uppercase">{g.goalName}</span>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                          g.reviewStatus === 'Approved' ? 'bg-emerald-950/40 text-emerald-400' : 'bg-amber-950/40 text-amber-500'
                        }`}>
                          {g.reviewStatus}
                        </span>
                      </div>
                      <p className="text-zinc-300">Cause Entity: <strong className="text-white">{g.causeName}</strong></p>
                      <p className="text-slate-400 normal-case leading-relaxed font-sans">{g.notes}</p>
                      <div className="flex justify-between text-[10px] text-zinc-500 font-mono italic pt-1 border-t border-zinc-900/60">
                        <span>Basis: {g.allocationBasis}</span>
                        <span className="text-amber-400 font-bold">Recorded Allocation: £{g.recordedAllocation.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-xl space-y-1.5 text-slate-500 text-[10px] leading-normal normal-case font-sans">
                <span className="text-amber-400 font-bold uppercase tracking-wider block text-[10.5px]">Mandatory Safe Wording Regulation state:</span>
                <p>Where enabled and documented, NexoraX may allocate 3% of eligible net receipts to approved community or charitable initiatives.</p>
                <p className="text-red-400 font-bold">Do not claim donations unless recorded and verifiable.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 10: MONDAY LAUNCH PLAN CHECKLIST --- */}
        {activeTab === 'Monday Plan' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5 font-mono text-xs">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 10 — MONDAY ZERO-BUDGET ACTION PLAN
              </span>
              <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight pb-2 border-b border-zinc-900">Configure Manual Promotion Schedule</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                {/* Before Launch Checklist box */}
                <div className="p-4 bg-zinc-950/80 border border-purple-500/15 rounded-xl space-y-3">
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5 text-[11px] font-bold">
                    <span className="text-[#e840f3] uppercase tracking-wider">Before Launch Verification Pool</span>
                    <span className="text-[#00ffff] font-bold">{beforeLaunchProgress}% Completed</span>
                  </div>
                  <div className="space-y-2 select-none text-[10px]">
                    {[
                      { key: 'homepageChecked', label: 'Homepage checked' },
                      { key: 'digitalProductsChecked', label: 'Digital Products checked' },
                      { key: 'freeToolsChecked', label: 'Free tools checked' },
                      { key: 'qrGeneratorChecked', label: 'QR generator checked' },
                      { key: 'contactFormChecked', label: 'Contact form checked' },
                      { key: 'requestSetupChecked', label: 'Request setup checked' },
                      { key: 'privacyTermsChecked', label: 'Privacy/cookie/terms checked' },
                      { key: 'sitemapSubmitted', label: 'Sitemap submitted' },
                      { key: 'gbpProfileDrafted', label: 'Google Business Profile drafted' },
                      { key: 'socialProfilesUpdated', label: 'Social profiles updated' },
                      { key: 'tenPostsCreated', label: '10 posts created' },
                      { key: 'threeScriptsWritten', label: '3 short video scripts created' },
                      { key: 'fiveCampaignsSet', label: '5 QR campaigns created' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`before-${item.key}`}
                          checked={beforeLaunchChecklist[item.key]}
                          onChange={(e) => setBeforeLaunchChecklist({ ...beforeLaunchChecklist, [item.key]: e.target.checked })}
                          className="w-4 h-4 cursor-pointer align-middle bg-black border-zinc-850 rounded"
                        />
                        <label htmlFor={`before-${item.key}`} className="text-slate-350 cursor-pointer text-[10.5px] leading-none">{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Monday Morning Checklist box */}
                <div className="p-4 bg-zinc-950/80 border border-cyan-500/15 rounded-xl space-y-3">
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5 text-[11px] font-bold">
                    <span className="text-[#00ffff] uppercase tracking-wider">Monday Morning (08:00 AM Dispatch)</span>
                    <span className="text-amber-400 font-bold">{mondayMorningProgress}% Completed</span>
                  </div>
                  <div className="space-y-2 select-none text-[10px]">
                    {[
                      { key: 'pubFb', label: 'Publish launch post on Facebook' },
                      { key: 'pubIg', label: 'Publish launch post on Instagram' },
                      { key: 'pubLi', label: 'Publish founder post on LinkedIn' },
                      { key: 'pubTiktok', label: 'Publish short video on TikTok' },
                      { key: 'pubYt', label: 'Publish short video on YouTube Shorts' },
                      { key: 'postGbp', label: 'Post Google Business Profile update' },
                      { key: 'shareQr', label: 'Share QR code offer' },
                      { key: 'msgWarmContacts', label: 'Message warm contacts manually' },
                      { key: 'addEmailSig', label: 'Add link to email signature' },
                      { key: 'trackLeads', label: 'Track all leads in WorkSuite' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`monday-${item.key}`}
                          checked={mondayMorningChecklist[item.key]}
                          onChange={(e) => setMondayMorningChecklist({ ...mondayMorningChecklist, [item.key]: e.target.checked })}
                          className="w-4 h-4 cursor-pointer align-middle bg-black border-zinc-850 rounded"
                        />
                        <label htmlFor={`monday-${item.key}`} className="text-slate-350 cursor-pointer text-[10.5px] leading-none">{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Monday Launch Batch Pipeline Console (Task 4) */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-zinc-900 pb-3 gap-2">
                  <div className="space-y-0.5">
                    <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      Monday Launch Batch Pipeline (Drafts)
                    </h3>
                    <p className="text-slate-400 text-[10.5px] font-sans">
                      Verify and approve pre-prepared organic traffic drafts before Sunday public preview.
                    </p>
                  </div>
                  <span className="px-2 py-0.5 rounded bg-amber-950/40 border border-amber-500/20 text-amber-500 font-mono text-[9px] font-bold inline-block self-start sm:self-center">
                    ADMIN INTUITION APPROVAL ENFORCED
                  </span>
                </div>

                {/* Sub-navigation tabs for Draft Pipeline */}
                <div className="flex flex-wrap gap-1.5 border-b border-zinc-950 pb-2">
                  {[
                    { id: 'trends', label: '10 Trend Cards' },
                    { id: 'socials', label: '10 Social Posts' },
                    { id: 'videos', label: '3 Video Scripts' },
                    { id: 'seo', label: '5 SEO Pages' },
                    { id: 'qrs', label: '5 QR Campaigns' },
                    { id: 'special', label: 'Launch Deliverables' }
                  ].map((subTab) => (
                    <button
                      key={subTab.id}
                      onClick={() => setDraftPipelineTab(subTab.id as any)}
                      className={`px-3 py-1.5 rounded-lg border text-[10.5px] font-mono font-bold uppercase transition ${
                        draftPipelineTab === subTab.id
                          ? 'bg-purple-950/10 border-purple-500/50 text-[#e840f3]'
                          : 'bg-zinc-950/60 border-zinc-900 text-slate-400 hover:text-white'
                      }`}
                    >
                      {subTab.label}
                    </button>
                  ))}
                </div>

                {/* Content based on selected draft pipeline subtab */}
                <div className="pt-2">
                  {draftPipelineTab === 'trends' && (
                    <div className="space-y-3">
                      {[
                        { id: 'dt-1', title: 'Autonomous AI Swarm Workforces', summary: 'Parallel automated coordinate swarm agents processing standard CRM and bookings.', tag: 'AI Swarms' },
                        { id: 'dt-2', title: 'Neural Video Synthesizers', summary: 'Studio-grade video reels compiled dynamically from layout text specs.', tag: 'AI Video' },
                        { id: 'dt-3', title: 'Hyper-Personalized Edge AI', summary: 'Privacy-first small model weights running securely on office Terminals.', tag: 'Edge AI' },
                        { id: 'dt-4', title: 'Decentralized AI Networks', summary: 'P2P computing loops minimizing standard API vendor subscription fees.', tag: 'Infrastructure' },
                        { id: 'dt-5', title: 'Zero-Budget Web SEO Magnets', summary: 'Dynamic structured micro sitemaps driving regional traffic bottlenecks.', tag: 'SEO Systems' },
                        { id: 'dt-6', title: 'Automated Customer Ingestion Funnels', summary: 'Frictionless customer intake rails mapped to Calendly and standard billing.', tag: 'User Intake' },
                        { id: 'dt-7', title: 'Physical QR Sticker Campaigns', summary: 'Offline walk-in visitor indicators routing directly to digital offers.', tag: 'QR Decals' },
                        { id: 'dt-8', title: 'Secure Invoice Register Audits', summary: 'Automatic check pipelines cross-referencing invoice statements to open-banking.', tag: 'Finance' },
                        { id: 'dt-9', title: 'Dynamic Voice Command Terminals', summary: 'Low-latency simulation representing official Planet Telecom voice anchors.', tag: 'Voice Matrix' },
                        { id: 'dt-10', title: 'Local Google Business Profile Boosters', summary: 'Fife and Dunfermline local index tags optimization maximizing search volume.', tag: 'Google GBP' }
                      ].map((item) => (
                        <div key={item.id} className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px]">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-black uppercase font-sans">{item.title}</span>
                              <span className="text-[9px] font-mono text-cyan-400 bg-cyan-950/20 px-1.5 py-0.5 rounded border border-cyan-950">#{item.tag}</span>
                            </div>
                            <p className="text-slate-400 font-sans normal-case">{item.summary}</p>
                          </div>
                          <div className="sm:shrink-0">
                            {approvedDrafts[item.id] ? (
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 font-mono font-bold text-[9.5px] uppercase">Approved Draft</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setApprovedDrafts({ ...approvedDrafts, [item.id]: true });
                                  onAddLog('Organic Launch Queue', `Approved trend draft: ${item.title}`, 'SUCCESS', 'Monday launch queue synced');
                                }}
                                className="px-3 py-1 rounded bg-[#00ffff] hover:opacity-90 text-black font-mono font-bold text-[10px] uppercase cursor-pointer"
                              >
                                Approve Draft
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftPipelineTab === 'socials' && (
                    <div className="space-y-3">
                      {[
                        { id: 'ds-1', title: 'Post 1 (LinkedIn/X)', caption: 'Traditional web pages are static. NexoraX AI Swarms execute end-to-end booking workflows in sub-seconds. Clear your Monday backlog safely. #EnterpriseAutomation #ZeroBudget' },
                        { id: 'ds-2', title: 'Post 2 (Instagram)', caption: 'Don\'t spend thousands on marketing assets. Our new Neural Video Synthesizer creates studio-grade vertical reels in 0.8s. Run your organic tests today! #DesignInnovation #DarkLuxury' },
                        { id: 'ds-3', title: 'Post 3 (X/Twitter)', caption: 'Hyperscale servers are leaking customer logs. Protect corporate data by running secure local Edge AI weights on physical office Terminals. #DataPrivacy #EdgeAI' },
                        { id: 'ds-4', title: 'Post 4 (LinkedIn)', caption: 'Fife and Dundee business owners: discover how a simple local SEO structured schema audit can boost Google search authority by 3x. Read our UK LocalBusiness guide. #LocalSEO #UKBusiness' },
                        { id: 'ds-5', title: 'Post 5 (Facebook)', caption: 'Say goodbye to auto-billed ad schedules. The Zero-Budget Launch Plan focuses strictly on high-density QR stickers, free GBP updates, and community trust. #FrugalMarketing' },
                        { id: 'ds-6', title: 'Post 6 (Instagram)', caption: 'Visual aesthetics matched to modern Inter and Space Grotesk display headings. Professional branding paired with optimized mobile touch grids. #DesignAesthetics' },
                        { id: 'ds-7', title: 'Post 7 (X/Twitter)', caption: 'Simplify your transactional tracking. Connect physical checkout paths directly to a local, authenticated invoice ledger. #SmallBusinessTools' },
                        { id: 'ds-8', title: 'Post 8 (LinkedIn)', caption: 'Are you running an agency but stuck in manual onboarding chats? Set up a self-coordinating client portal with automatic draft-routing queues. #AgencyGrowth' },
                        { id: 'ds-9', title: 'Post 9 (Facebook)', caption: 'Make your business discoverable. Scan our Fife Networking cards to access free website sitemap audits that pinpoint blockages in under 10 seconds. #FifeBiz' },
                        { id: 'ds-10', title: 'Post 10 (Instagram)', caption: 'Unrivaled design spacing, generous negative space, and premium dark glassmorphism card surfaces. Join the waiting list today to upgrade your system. #ProductLaunch' }
                      ].map((item) => (
                        <div key={item.id} className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 text-[11px]">
                          <div className="space-y-1 w-full sm:max-w-xl">
                            <span className="text-[#00ffff] font-mono text-[10px] font-bold block">{item.title}</span>
                            <p className="text-zinc-300 font-sans leading-relaxed text-xs p-2 bg-black/60 rounded border border-zinc-900 select-all font-medium normal-case">"{item.caption}"</p>
                          </div>
                          <div className="sm:shrink-0 mt-1">
                            {approvedDrafts[item.id] ? (
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 font-mono font-bold text-[9.5px] uppercase">Approved Draft</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setApprovedDrafts({ ...approvedDrafts, [item.id]: true });
                                  onAddLog('Organic Launch Queue', `Approved social draft: ${item.title}`, 'SUCCESS', 'Monday launch queue synced');
                                }}
                                className="px-3 py-1 rounded bg-[#00ffff] hover:opacity-90 text-black font-mono font-bold text-[10px] uppercase cursor-pointer"
                              >
                                Approve Draft
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftPipelineTab === 'videos' && (
                    <div className="space-y-3">
                      {[
                        { id: 'dv-1', title: 'Script 1 (Reels)', hook: 'Traditional video production is officially dead.', script: 'Scene 1: Person editing in complex timelines.\nScene 2: Text prompt typing "Stunning obsidian logo reveal" converting into full render in 0.8s.' },
                        { id: 'dv-2', title: 'Script 2 (TikTok)', hook: 'Is your CRM leaking client data?', script: 'Scene 1: Security alert with red exclamation marks.\nScene 2: Demonstrating secure local Edge model running fully offline on an office Mac.' },
                        { id: 'dv-3', title: 'Script 3 (YouTube Shorts)', hook: 'Bespoke web design doesn\'t have to cost £5,000.', script: 'Scene 1: Standard slow WordPress loading screen.\nScene 2: NexoraX pristine template loading beautifully in under 0.2s.' }
                      ].map((item) => (
                        <div key={item.id} className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl space-y-2 text-[11px]">
                          <div className="flex justify-between items-center pb-1 border-b border-zinc-900">
                            <span className="text-white font-black uppercase font-mono">{item.title}</span>
                            {approvedDrafts[item.id] ? (
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 font-mono font-bold text-[9.5px] uppercase">Approved Draft</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setApprovedDrafts({ ...approvedDrafts, [item.id]: true });
                                  onAddLog('Organic Launch Queue', `Approved video script draft: ${item.title}`, 'SUCCESS', 'Monday launch queue synced');
                                }}
                                className="px-3 py-1 rounded bg-[#00ffff] hover:opacity-90 text-black font-mono font-bold text-[10px] uppercase cursor-pointer"
                              >
                                Approve Draft
                              </button>
                            )}
                          </div>
                          <div>
                            <span className="text-amber-400 font-mono text-[9.5px] block">CONVERSION-FOCUSED VIDEO HOOK:</span>
                            <p className="italic font-sans text-xs text-slate-100 normal-case mb-1">"{item.hook}"</p>
                          </div>
                          <div className="bg-black/60 p-2 text-zinc-300 font-mono text-[10px] rounded border border-zinc-900 whitespace-pre-line leading-relaxed">
                            {item.script}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftPipelineTab === 'seo' && (
                    <div className="space-y-3">
                      {[
                        { id: 'dseo-1', path: '/seo/dunfermline-business-automation', focus: 'Fife/Dunfermline local business automation keywords', density: '3.4%' },
                        { id: 'dseo-2', path: '/seo/zero-budget-digital-marketing-uk', focus: 'Organic launch plans and free sitemap checklists', density: '2.8%' },
                        { id: 'dseo-3', path: '/seo/secure-offline-qr-campaigns', focus: 'Physical sticker marketing guides and UTM procedures', density: '3.1%' },
                        { id: 'dseo-4', path: '/seo/private-local-edge-ai-setup', focus: 'Security-conscious corporate on-premise AI models', density: '2.5%' },
                        { id: 'dseo-5', path: '/seo/fife-web-design-optimization', focus: 'Region-focused responsive mobile interface creation', density: '3.0%' }
                      ].map((item) => (
                        <div key={item.id} className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px]">
                          <div className="space-y-1">
                            <span className="text-cyan-400 font-mono block select-all">{item.path}</span>
                            <p className="text-slate-350 font-sans normal-case">Target Focus: <strong className="text-white">{item.focus}</strong></p>
                            <span className="text-zinc-500 font-mono text-[9px] uppercase font-bold">Density Target: {item.density}</span>
                          </div>
                          <div className="sm:shrink-0">
                            {approvedDrafts[item.id] ? (
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 font-mono font-bold text-[9.5px] uppercase">Approved Draft</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setApprovedDrafts({ ...approvedDrafts, [item.id]: true });
                                  onAddLog('Organic Launch Queue', `Approved SEO Draft Page: ${item.path}`, 'SUCCESS', 'Monday launch queue synced');
                                }}
                                className="px-3 py-1 rounded bg-[#00ffff] hover:opacity-90 text-black font-mono font-bold text-[10px] uppercase cursor-pointer"
                              >
                                Approve Draft
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftPipelineTab === 'qrs' && (
                    <div className="space-y-3">
                      {[
                        { id: 'dqr-1', name: 'QR Campaign 1: High Street Sticker', target: 'utm_source=print-flyer&utm_medium=qr-sticker&utm_campaign=dunfermline-shop-walk' },
                        { id: 'dqr-2', name: 'QR Campaign 2: Fife Business Cards QR', target: 'utm_source=print-card&utm_medium=qr-card&utm_campaign=fife-networking' },
                        { id: 'dqr-3', name: 'QR Campaign 3: Glasgow Tech Hub Poster', target: 'utm_source=poster&utm_medium=qr-poster&utm_campaign=glasgow-launch' },
                        { id: 'dqr-4', name: 'QR Campaign 4: Regional Vehicle Decal Vinyl', target: 'utm_source=vehicle&utm_medium=qr-decal&utm_campaign=fife-van' },
                        { id: 'dqr-5', name: 'QR Campaign 5: Edinburgh Business Dinner Banner', target: 'utm_source=event&utm_medium=qr-banner&utm_campaign=edinburgh-pre' }
                      ].map((item) => (
                        <div key={item.id} className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px]">
                          <div className="space-y-1">
                            <span className="text-white font-sans font-black uppercase">{item.name}</span>
                            <span className="text-zinc-400 font-mono text-[9px] block bg-black/60 p-1.5 rounded border border-zinc-900 select-all truncate max-w-sm sm:max-w-md">nexorax.co.uk?{item.target}</span>
                          </div>
                          <div className="sm:shrink-0">
                            {approvedDrafts[item.id] ? (
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 font-mono font-bold text-[9.5px] uppercase">Approved Draft</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setApprovedDrafts({ ...approvedDrafts, [item.id]: true });
                                  onAddLog('Organic Launch Queue', `Approved QR Campaign: ${item.name}`, 'SUCCESS', 'Monday launch queue synced');
                                }}
                                className="px-3 py-1 rounded bg-[#00ffff] hover:opacity-90 text-black font-mono font-bold text-[10px] uppercase cursor-pointer"
                              >
                                Approve Draft
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftPipelineTab === 'special' && (
                    <div className="space-y-4">
                      {/* Monday Launch List CTA block */}
                      <div className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl space-y-2">
                        <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5">
                          <span className="text-[#e840f3] font-mono text-[10px] font-bold uppercase">1. Monday Launch List CTA block</span>
                          {approvedDrafts['dsb-1'] ? (
                            <span className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-emerald-950/60 text-emerald-400">Approved</span>
                          ) : (
                            <button
                              onClick={() => {
                                setApprovedDrafts({ ...approvedDrafts, 'dsb-1': true });
                                onAddLog('Organic Launch Queue', 'Approved CTA block draft', 'SUCCESS', 'Launch Queue Sync');
                              }}
                              className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-cyan-400 text-black font-semibold hover:opacity-90 cursor-pointer"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                        <div className="bg-gradient-to-r from-purple-950/20 to-cyan-950/20 p-5 rounded-xl border border-purple-500/20 text-center space-y-3 font-sans">
                          <span className="text-purple-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] block">Exclusive Priority Admission</span>
                          <h4 className="text-white font-black text-lg uppercase tracking-tight max-w-md mx-auto">Elevate Your Corporate Operations For Free</h4>
                          <p className="text-slate-350 text-[11px] max-w-md mx-auto normal-case font-medium">Join the Monday Launch List to unlock premium original templates and secure custom system updates under our Zero-Budget Launch Plan.</p>
                          <button className="px-5 py-2 bg-gradient-to-r from-purple-600 to-[#e840f3] text-white font-bold text-xs uppercase rounded-xl tracking-wider shadow-lg">Secure Your Placement</button>
                        </div>
                      </div>

                      {/* Google Business Profile post draft */}
                      <div className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl space-y-2">
                        <div className="flex justify-between items-center border-[#eec5ff]/10 border-b pb-1.5">
                          <span className="text-[#e840f3] font-mono text-[10px] font-bold uppercase">2. Google Business Profile Post Draft</span>
                          {approvedDrafts['dsb-2'] ? (
                            <span className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-emerald-950/60 text-emerald-400">Approved</span>
                          ) : (
                            <button
                              onClick={() => {
                                setApprovedDrafts({ ...approvedDrafts, 'dsb-2': true });
                                onAddLog('Organic Launch Queue', 'Approved GBP draft post', 'SUCCESS', 'Launch Queue Sync');
                              }}
                              className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-cyan-400 text-black font-semibold hover:opacity-90 cursor-pointer"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                        <p className="text-zinc-300 font-sans leading-relaxed text-xs bg-black/60 p-2.5 rounded border border-zinc-900 select-all font-medium normal-case">
                          "We are officially open for Dunfermline, Fife, and UK-wide business systems optimization! Get original, secure website templates and organic growth audits tailored to your local audience. Zero-budget consultation slots are live."
                        </p>
                      </div>

                      {/* LinkedIn Founder post draft */}
                      <div className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl space-y-2">
                        <div className="flex justify-between items-center border-[#eec5ff]/10 border-b pb-1.5">
                          <span className="text-[#e840f3] font-mono text-[10px] font-bold uppercase">3. LinkedIn Founder Post Draft</span>
                          {approvedDrafts['dsb-3'] ? (
                            <span className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-emerald-950/60 text-emerald-400">Approved</span>
                          ) : (
                            <button
                              onClick={() => {
                                setApprovedDrafts({ ...approvedDrafts, 'dsb-3': true });
                                onAddLog('Organic Launch Queue', 'Approved LinkedIn founder draft', 'SUCCESS', 'Launch Queue Sync');
                              }}
                              className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-cyan-400 text-black font-semibold hover:opacity-90 cursor-pointer"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                        <p className="text-zinc-300 font-sans leading-relaxed text-xs bg-black/60 p-2.5 rounded border border-zinc-900 select-all font-medium normal-case">
                          "Excited to preview our new organic launch queue. We are launching without paid ads, showing local merchants how to optimize visibility cleanly. No scraping, no shortcuts — just beautiful, high-contrast systems and robust local sitemaps. Let's build together."
                        </p>
                      </div>

                      {/* Homepage launch announcement block */}
                      <div className="p-3.5 bg-zinc-950/40 border border-zinc-900 rounded-xl space-y-2">
                        <div className="flex justify-between items-center border-[#eec5ff]/10 border-b pb-1.5">
                          <span className="text-[#e840f3] font-mono text-[10px] font-bold uppercase">4. Homepage Launch Announcement Block</span>
                          {approvedDrafts['dsb-4'] ? (
                            <span className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-emerald-950/60 text-emerald-400">Approved</span>
                          ) : (
                            <button
                              onClick={() => {
                                setApprovedDrafts({ ...approvedDrafts, 'dsb-4': true });
                                onAddLog('Organic Launch Queue', 'Approved homepage announcement draft', 'SUCCESS', 'Launch Queue Sync');
                              }}
                              className="px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-cyan-400 text-black font-semibold hover:opacity-90 cursor-pointer"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                        <div className="p-4 bg-zinc-950 border border-red-500/20 text-red-400 font-mono text-[11px] leading-relaxed rounded-lg whitespace-pre-line">
                          <strong>PROS_PORTAL SYSTEM IS LIVE</strong> // Sunday Night Public Preview active ahead of Monday morning launch. All systems verified and sitemaps submitted.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- TABS SECTION 11: DEVELOPER API SIMULATOR --- */}
        {activeTab === 'Developer Console' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-5 font-mono text-xs">
              <span className="text-amber-400 font-mono text-[9px] font-bold uppercase tracking-[0.2em] bg-yellow-950/40 px-2.5 py-0.5 rounded border border-amber-500/20 inline-block">
                SECTION 11 — BACKEND API SERVER PLATFORM PATHS
              </span>
              <h3 className="text-white font-sans font-black text-sm uppercase tracking-tight pb-2 border-b border-zinc-900">Inspect Server-Authoritative Routing Keys</h3>

              <div className="flex flex-col md:flex-row gap-5 items-start">
                <div className="space-y-3.5 w-full md:w-1/3 shrink-0">
                  <div className="space-y-1">
                    <label className="text-slate-450 block font-bold">Select Active Endpoint:</label>
                    <select
                      className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white"
                      value={selectedApiRoute}
                      onChange={(e) => {
                        setSelectedApiRoute(e.target.value);
                        setApiResponsePayload(null);
                      }}
                    >
                      <option value="GET /api/admin/growth-engine">GET /api/admin/growth-engine</option>
                      <option value="POST /api/admin/growth-engine/generate-qr">POST /api/admin/growth-engine/generate-qr</option>
                      <option value="POST /api/admin/growth-engine/generate-social-posts">POST /api/admin/growth-engine/generate-social-posts</option>
                      <option value="POST /api/admin/growth-engine/generate-video-scripts">POST /api/admin/growth-engine/generate-video-scripts</option>
                      <option value="POST /api/admin/growth-engine/generate-seo-page">POST /api/admin/growth-engine/generate-seo-page</option>
                      <option value="GET /api/admin/growth-engine/leads">GET /api/admin/growth-engine/leads</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={handleTriggerApiHandshake}
                    disabled={isApiLoading}
                    className="w-full py-2 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-xs rounded-xl"
                  >
                    {isApiLoading ? 'Processing telemetry payload...' : 'Simulate API Call'}
                  </button>
                </div>

                <div className="flex-grow w-full space-y-3">
                  {apiResponsePayload ? (
                    <pre className="p-4 bg-zinc-950 border border-cyan-400 rounded-lg text-emerald-400 text-[10px] whitespace-pre-wrap font-mono leading-relaxed select-all">
                      {JSON.stringify(apiResponsePayload, null, 2)}
                    </pre>
                  ) : (
                    <div className="p-12 text-center text-slate-550 border border-dashed border-zinc-850 rounded-xl">
                      Select an endpoint parameter to inspect mock server returns.
                    </div>
                  )}

                  <div className="p-4 bg-zinc-950/60 rounded border border-zinc-900 text-slate-500 text-[10px] space-y-1 font-sans normal-case">
                    <span className="text-white font-mono font-bold uppercase block tracking-wider mb-1">Standard Server Routing Policies:</span>
                    <p>- Public tools output MUST NEVER leak internal worksuite or user-auth administrative data values.</p>
                    <p>- Lead ingestion requires optional consent checkbox parameters to align with legal guidelines.</p>
                    <p>- Under no circumstance can background processes launch paid ads without direct administrator oversight.</p>
                    <p>- Secure tokens and payment keys remain fully confined to server-side variables.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEAD TRACKER IN HUD OR FUNNEL TAB */}
      <div className="glass-panel border border-[#e845f3]/15 p-5 rounded-2xl bg-black/30 font-mono text-xs space-y-4">
        <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
          <span className="text-purple-400 text-[10.5px] font-bold uppercase tracking-wider">
            SECTION 7 & WORKSUITE CRM — LEAD TRACKER DIRECTORY
          </span>
          <span className="text-slate-500 font-sans italic text-[11px] lowercase">Active Lead status transitions are processed below</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Active status dropdown and details */}
          <div className="space-y-3.5">
            <div className="space-y-1">
              <label className="text-slate-450 block font-bold">Select Simulated Lead File:</label>
              <select
                className="w-full bg-black border border-zinc-800 rounded p-2 text-xs text-white"
                value={selectedLeadStatus}
                onChange={(e) => setSelectedLeadStatus(e.target.value)}
              >
                {leads.map((ld) => (
                  <option key={ld.id} value={ld.id}>{ld.name} ({ld.company})</option>
                ))}
              </select>
            </div>

            {(() => {
              const file = leads.find(l => l.id === selectedLeadStatus);
              if (!file) return null;
              return (
                <div className="p-3 bg-zinc-950 border border-zinc-900 rounded-lg space-y-1.5 text-[11px]">
                  <p className="text-white font-bold">{file.name}</p>
                  <p className="text-cyan-400">Company: {file.company}</p>
                  <p className="text-slate-350">Source Tool: {file.toolUsed}</p>
                  <p className="text-[#ffd700]">Registered date: {file.timestamp}</p>
                  
                  <div className="space-y-1 pt-1">
                    <label className="text-slate-500 uppercase text-[9px] block">Pipeline Stage Update:</label>
                    <select
                      value={file.status}
                      onChange={(e) => {
                        setLeads(leads.map(l => l.id === file.id ? { ...l, status: e.target.value as any } : l));
                        onAddLog('CRM Tracker', `Lead status shift for ${file.name} to ${e.target.value}`, 'SUCCESS', 'Update state variables');
                      }}
                      className="w-full bg-black border border-zinc-800 px-2 py-1 text-slate-300 font-bold font-mono outline-none text-[11px]"
                    >
                      <option value="New">New</option>
                      <option value="Free Tool Used">Free Tool Used</option>
                      <option value="Setup Requested">Setup Requested</option>
                      <option value="Quote Requested">Quote Requested</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Consultation Booked">Consultation Booked</option>
                      <option value="Invoice Sent">Invoice Sent</option>
                      <option value="Won">Won</option>
                      <option value="Lost">Lost</option>
                      <option value="Follow Up Later">Follow Up Later</option>
                    </select>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Leads matrix visual cards */}
          <div className="md:col-span-2 space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
            {leads.map((l) => (
              <div key={l.id} className="p-3 bg-zinc-950/60 border border-zinc-905 rounded-xl flex justify-between items-center gap-4 text-[11px]">
                <div>
                  <span className="text-white font-bold block">{l.name}</span>
                  <span className="text-slate-450">{l.company} • Joined: {l.timestamp}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#00ffff] font-extrabold max-w-[150px] truncate">{l.toolUsed}</span>
                  <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase bg-purple-950/40 text-[#e840f3] border border-purple-500/20">
                    {l.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple visual fallback svg rocket icon
const RocketIcon = ({ className, size = 16 }: { className?: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4.5 16.5c-1.5 1.25-2.5 3.5-2.5 3.5s2.25-1 3.5-2.5" />
    <path d="M12 2C6.5 2 2 6.5 2 12c0 1.2.3 2.3.8 3.3L12 6l5.7 9.3c.5-1 .8-2.1.8-3.3 0-5.5-4.5-10-10-10z" />
    <path d="M9 12c0 1.66 1.34 3 3 3s3-1.34 3-3V9H9v3z" />
  </svg>
);
